import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PublicLayout } from './PublicLayout';
import { useAuth } from './AuthContext';
import { authAPI, billingAPI, referralAPI, supportApi, trackerApi, type BillingInvoiceApiItem, type BillingSubscriptionSummaryApi, type ReferralOverviewApi, type SurebetHistoryApiItem } from './api';
import { AUTH_MODE, getPlanLabel, type BillingCycle, type PlanType } from './services/authService';
import { openCookiePreferences } from './AnalyticsConsent';

const FREE_TELEGRAM_JOIN_URL = 'https://t.me/+7tVb_SXcYB01MzA0';
const BILLING_MODE = AUTH_MODE === 'api' || process.env.REACT_APP_BILLING_MODE === 'api' ? 'api' : 'mock';
const SUPPORT_EMAIL = 'info@perfectoddsnl.com';
const TELEGRAM_SUPPORT_URL = (process.env.REACT_APP_TELEGRAM_SUPPORT_URL || '').trim();
const AMSTERDAM_TZ = 'Europe/Amsterdam';
const CONTACT_FORM_INITIAL_STATE = {
  name: '',
  email: '',
  topic: 'support',
  plan: 'nog-geen-abonnement',
  message: '',
};

type PaidPlanId = 'pro' | 'pre-match' | 'live';
type PaidBillingCycle = Exclude<BillingCycle, 'none'>;
type HomepageArchivePreset = 'this-month' | 'last-month';
type HomepageArchiveRange = { startDate: string; endDate: string };

interface BillingOption {
  value: PaidBillingCycle;
  label: string;
  price: string;
  note: string;
  oldPrice?: string;
  promoLabel?: string;
}

const MONTHLY_SUBSCRIPTION_TEXT = 'Automatisch maandelijks abonnement. Altijd opzegbaar via je account.';
const WEEKLY_SUBSCRIPTION_TEXT = 'Automatisch wekelijks abonnement. Altijd opzegbaar via je account.';
const DAY_PASS_SUBSCRIPTION_TEXT = 'Eenmalig en niet terugkerend.';
const SUMMER_ACTION_END_AT = Date.parse('2026-09-21T22:00:00.000Z');
const SUMMER_ACTION_PRICE = '€44,95';
const SUMMER_ACTION_OLD_PRICE = '€64,95';
const SUMMER_ACTION_TERMS = `Eerste maand €44,95, daarna €64,95 per maand. De korting wordt bij je eerste Pro-maandabonnement automatisch toegepast. Altijd opzegbaar via je account.`;
const SUMMER_ACTION_DEADLINE = 'Geldig tot 22 september 2026 om 00:00.';

const PLAN_BILLING_OPTIONS: Record<PaidPlanId, BillingOption[]> = {
  pro: [
    { value: 'month', label: 'Maand', price: '€64,95', note: 'Volledige maand toegang' },
    { value: 'week', label: 'Week', price: '€19,95', note: '7 dagen Pro proberen' },
    { value: 'day-pass', label: 'Dagpas', price: '€3,95', note: '24 uur toegang' },
  ],
  'pre-match': [
    { value: 'month', label: 'Maand', price: '€34,95', note: 'Volledige maand toegang' },
    { value: 'week', label: 'Week', price: '€9,95', note: '7 dagen pre-match' },
  ],
  live: [
    { value: 'month', label: 'Maand', price: '€49,95', note: 'Volledige maand toegang' },
    { value: 'week', label: 'Week', price: '€14,95', note: '7 dagen live toegang' },
  ],
};

const isPaidPlanId = (value: unknown): value is PaidPlanId => (
  value === 'pro' || value === 'pre-match' || value === 'live'
);

const isSummerActionActive = (now = Date.now()) => now < SUMMER_ACTION_END_AT;

const getBillingOptions = (plan: PaidPlanId) => {
  const options = PLAN_BILLING_OPTIONS[plan];
  if (plan !== 'pro' || !isSummerActionActive()) return options;
  return options.map((option) => (
    option.value === 'month'
      ? {
          ...option,
          price: SUMMER_ACTION_PRICE,
          oldPrice: SUMMER_ACTION_OLD_PRICE,
          promoLabel: 'Zomeractie',
          note: SUMMER_ACTION_TERMS,
        }
      : option
  ));
};

const normalizeBillingCycleForPlan = (plan: string | null | undefined, value: unknown): BillingCycle => {
  if (!isPaidPlanId(plan)) return 'none';
  const allowed = getBillingOptions(plan).map((option) => option.value);
  return allowed.includes(value as PaidBillingCycle) ? value as PaidBillingCycle : allowed[0];
};

const getCheckoutPath = (plan: PaidPlanId, billingCycle: PaidBillingCycle) => (
  `/checkout?plan=${encodeURIComponent(plan)}&billingCycle=${encodeURIComponent(billingCycle)}`
);

const getBillingTerms = (billingCycle: BillingCycle) => (
  billingCycle === 'day-pass'
    ? DAY_PASS_SUBSCRIPTION_TEXT
    : billingCycle === 'week'
      ? WEEKLY_SUBSCRIPTION_TEXT
      : MONTHLY_SUBSCRIPTION_TEXT
);

const getBillingOptionTerms = (plan: PaidPlanId, billingCycle: BillingCycle) => (
  plan === 'pro' && billingCycle === 'month' && isSummerActionActive()
    ? SUMMER_ACTION_TERMS
    : getBillingTerms(billingCycle)
);

const PromoPrice: React.FC<{ oldPrice: string; price: string; label?: string; suffix?: string; compact?: boolean }> = ({
  oldPrice,
  price,
  label = 'Zomeractie',
  suffix = SUMMER_ACTION_TERMS,
  compact = false,
}) => (
  <span className={`promo-price${compact ? ' promo-price-compact' : ''}`}>
    <span className="promo-old-price">{oldPrice}</span>
    <span className="promo-current-price"><span>{label}</span>{price}</span>
    <small>{suffix}</small>
  </span>
);

type SummerCountdown = {
  active: boolean;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const getSummerCountdown = (now = Date.now()): SummerCountdown => {
  const remaining = Math.max(0, SUMMER_ACTION_END_AT - now);
  return {
    active: remaining > 0,
    days: Math.floor(remaining / 86400000),
    hours: Math.floor((remaining % 86400000) / 3600000),
    minutes: Math.floor((remaining % 3600000) / 60000),
    seconds: Math.floor((remaining % 60000) / 1000),
  };
};

const useSummerCountdown = () => {
  const [countdown, setCountdown] = useState<SummerCountdown>(() => getSummerCountdown());

  useEffect(() => {
    if (!countdown.active) return undefined;
    const intervalId = window.setInterval(() => setCountdown(getSummerCountdown()), 1000);
    return () => window.clearInterval(intervalId);
  }, [countdown.active]);

  return countdown;
};

const SummerCountdownClock: React.FC<{ countdown: SummerCountdown; compact?: boolean }> = ({ countdown, compact = false }) => {
  if (!countdown.active) return null;
  const units = [
    { label: 'dagen', value: countdown.days },
    { label: 'uur', value: countdown.hours },
    { label: 'min', value: countdown.minutes },
    { label: 'sec', value: countdown.seconds },
  ];
  return (
    <div className={`summer-countdown${compact ? ' summer-countdown-compact' : ''}`} aria-label="Aftelklok zomeractie">
      {units.map((unit) => (
        <span key={unit.label} className="summer-countdown-unit">
          <strong>{String(unit.value).padStart(2, '0')}</strong>
          <small>{unit.label}</small>
        </span>
      ))}
    </div>
  );
};

const SummerActionBanner: React.FC<{
  countdown: SummerCountdown;
  compact?: boolean;
  showAction?: boolean;
}> = ({ countdown, compact = false, showAction = true }) => {
  if (!countdown.active) return null;
  return (
    <section className={`summer-action-banner${compact ? ' summer-action-banner-compact' : ''}`} aria-label="Zomeractie">
      <div className="summer-action-sun" aria-hidden="true">☀</div>
      <div className="summer-action-copy">
        <span>Zomeractie</span>
        <strong>€20 korting op je eerste maand Pro</strong>
        <small>De korting wordt automatisch toegepast bij Stripe Checkout. Eerste maand {SUMMER_ACTION_PRICE}, daarna {SUMMER_ACTION_OLD_PRICE} per maand. {SUMMER_ACTION_DEADLINE}</small>
      </div>
      <SummerCountdownClock countdown={countdown} compact={compact} />
      {showAction && (
        <Link to="/checkout?plan=pro&billingCycle=month" className="summer-action-button">
          Kies Pro
        </Link>
      )}
    </section>
  );
};

interface PlanBillingSelectorProps {
  plan: PaidPlanId;
  title?: string;
  initialBillingCycle?: BillingCycle;
  selectedBillingCycle?: BillingCycle;
  onBillingCycleChange?: (billingCycle: PaidBillingCycle) => void;
  showCheckoutLink?: boolean;
}

const PlanBillingSelector: React.FC<PlanBillingSelectorProps> = ({
  plan,
  title = 'Kies je looptijd',
  initialBillingCycle = 'month',
  selectedBillingCycle: controlledBillingCycle,
  onBillingCycleChange,
  showCheckoutLink = true,
}) => {
  const { user } = useAuth();
  const summerCountdown = useSummerCountdown();
  const hasActivePaidAccess = Boolean(
    user
    && user.role !== 'admin'
    && user.subscriptionStatus === 'active'
    && ['pro', 'pre-match', 'live'].includes(user.plan),
  );
  const options = getBillingOptions(plan).filter(
    (option) => !(hasActivePaidAccess && option.value === 'day-pass'),
  );
  const [localBillingCycle, setLocalBillingCycle] = useState<PaidBillingCycle>(
    normalizeBillingCycleForPlan(plan, initialBillingCycle) as PaidBillingCycle,
  );
  const requestedBillingCycle = normalizeBillingCycleForPlan(plan, controlledBillingCycle ?? localBillingCycle) as PaidBillingCycle;
  const selectedOption = options.find((option) => option.value === requestedBillingCycle) ?? options[0];
  const selectedBillingCycle = selectedOption.value;
  const cycleDescription = options.some((option) => option.value === 'day-pass') ? 'maand, week of dagpas' : 'maand of week';
  const updateBillingCycle = (nextBillingCycle: PaidBillingCycle) => {
    if (onBillingCycleChange) {
      onBillingCycleChange(nextBillingCycle);
      return;
    }
    setLocalBillingCycle(nextBillingCycle);
  };

  return (
    <div className="billing-selector-card">
      <div className="billing-selector-heading">
        <h2>{title}</h2>
        <p>Selecteer alvast {cycleDescription}. Deze keuze wordt meegestuurd naar de betaalstap.</p>
      </div>
      {plan === 'pro' && <SummerActionBanner countdown={summerCountdown} compact showAction={false} />}
      <div className="billing-option-grid" role="radiogroup" aria-label={`${title} voor ${plan}`}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`billing-option-button${selectedBillingCycle === option.value ? ' active' : ''}`}
            aria-pressed={selectedBillingCycle === option.value}
            onClick={() => updateBillingCycle(option.value)}
          >
            <span className="billing-option-label">{option.label}</span>
            {option.oldPrice ? (
              <PromoPrice oldPrice={option.oldPrice} price={option.price} label={option.promoLabel} suffix={option.note} compact />
            ) : (
              <strong>{option.price}</strong>
            )}
            {!option.oldPrice && <span>{option.note}</span>}
            <small>{getBillingOptionTerms(plan, option.value)}</small>
          </button>
        ))}
      </div>
      <div className="billing-selector-summary">
        <span>{selectedOption.label} geselecteerd. {getBillingOptionTerms(plan, selectedBillingCycle)}</span>
        {showCheckoutLink && (
          <Link to={getCheckoutPath(plan, selectedBillingCycle)} className="card-btn card-link">
            Doorgaan met {selectedOption.label.toLowerCase()}
          </Link>
        )}
      </div>
    </div>
  );
};

interface PublicPageProps {
  title: string;
  description: string;
}

const DisclaimerText: React.FC = () => (
  <>
    PerfectOddsNL biedt informatieve odds-signalen van volgens de Kansspelautoriteit vergunde bookmakers en plaatst zelf
    geen weddenschappen. PerfectOddsNL treedt niet op als tussenpersoon, agent of affiliate-bemiddelaar.
    PerfectOddsNL garandeert geen winst. Speel bewust - 18+. Meer informatie via{' '}
    <a href="https://loketkansspel.nl/" target="_blank" rel="noreferrer">loketkansspel.nl</a>.
  </>
);

interface SurebetCalcInput {
  oddA: string;
  oddB: string;
  oddC: string;
  totalStake: string;
}

interface SurebetCalcOptions {
  includeOddC: boolean;
}

interface SurebetCalcResult {
  isSurebet: boolean;
  usesOddC: boolean;
  impliedTotal: number;
  impliedPercent: number;
  roiPercent: number;
  payout: number;
  totalStake: number;
  stakeA: number;
  stakeB: number;
  stakeC?: number;
  payoutA: number;
  payoutB: number;
  payoutC?: number;
  profitA: number;
  profitB: number;
  profitC?: number;
  minimumProfit: number;
  expectedDeficit: number;
}

interface SurebetCalcResponse {
  errors: string[];
  result: SurebetCalcResult | null;
}

interface TelegramLinkState {
  linked: boolean;
  chatId?: string | null;
  username?: string | null;
  linkedAt?: string | null;
  linkCode?: string | null;
  linkCodeExpiresAt?: string | null;
  linkCodeExpired?: boolean;
  botStartUrl?: string | null;
  accessAllowed?: boolean;
  accessStatus?: string | null;
  inviteLink?: string | null;
  inviteLinks?: Array<{ channel: string; inviteLink: string; status?: string | null; expiresAt?: string | null; joinedAt?: string | null }>;
  accessSyncedAt?: string | null;
  accessError?: string | null;
}

const parseDecimalInput = (value: string): number | null => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const roundMoney = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const calculateSurebet = (
  { oddA, oddB, oddC, totalStake }: SurebetCalcInput,
  { includeOddC }: SurebetCalcOptions,
): SurebetCalcResponse => {
  const errors: string[] = [];
  const parsedOddA = parseDecimalInput(oddA);
  const parsedOddB = parseDecimalInput(oddB);
  const parsedOddC = parseDecimalInput(oddC);
  const parsedStake = parseDecimalInput(totalStake);
  const hasOddC = includeOddC;

  if (!oddA.trim()) errors.push('Odd A is verplicht.');
  if (!oddB.trim()) errors.push('Odd B is verplicht.');
  if (hasOddC && !oddC.trim()) errors.push('Odd C is verplicht als 3 uitkomsten aan staat.');
  if (!totalStake.trim()) errors.push('Totale inzet is verplicht.');

  if (oddA.trim() && (parsedOddA === null || parsedOddA <= 1)) {
    errors.push('Odd A moet groter zijn dan 1.');
  }
  if (oddB.trim() && (parsedOddB === null || parsedOddB <= 1)) {
    errors.push('Odd B moet groter zijn dan 1.');
  }
  if (hasOddC && oddC.trim() && (parsedOddC === null || parsedOddC <= 1)) {
    errors.push('Odd C moet groter zijn dan 1 als je deze invult.');
  }
  if (totalStake.trim() && (parsedStake === null || parsedStake <= 0)) {
    errors.push('Totale inzet moet groter zijn dan 0.');
  }

  if (errors.length > 0 || parsedOddA === null || parsedOddB === null || parsedStake === null) {
    return { errors, result: null };
  }

  const impliedTotal =
    hasOddC && parsedOddC !== null
      ? (1 / parsedOddA) + (1 / parsedOddB) + (1 / parsedOddC)
      : (1 / parsedOddA) + (1 / parsedOddB);

  const payout = parsedStake / impliedTotal;
  const stakeA = payout / parsedOddA;
  const stakeB = payout / parsedOddB;
  const stakeC = hasOddC && parsedOddC !== null ? payout / parsedOddC : undefined;

  const payoutA = stakeA * parsedOddA;
  const payoutB = stakeB * parsedOddB;
  const payoutC = stakeC !== undefined && parsedOddC !== null ? stakeC * parsedOddC : undefined;

  const profitA = payoutA - parsedStake;
  const profitB = payoutB - parsedStake;
  const profitC = payoutC !== undefined ? payoutC - parsedStake : undefined;

  const minimumProfit = Math.min(...[profitA, profitB, ...(profitC !== undefined ? [profitC] : [])]);
  const expectedDeficit = Math.max(parsedStake - payout, 0);

  return {
    errors: [],
    result: {
      isSurebet: impliedTotal < 1,
      usesOddC: hasOddC && parsedOddC !== null,
      impliedTotal,
      impliedPercent: impliedTotal * 100,
      roiPercent: ((1 / impliedTotal) - 1) * 100,
      payout,
      totalStake: parsedStake,
      stakeA,
      stakeB,
      stakeC,
      payoutA,
      payoutB,
      payoutC,
      profitA,
      profitB,
      profitC,
      minimumProfit,
      expectedDeficit,
    },
  };
};

const formatEuro = (value: number): string =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(roundMoney(value));

const formatBillingAmount = (value: number, currency = 'EUR'): string => new Intl.NumberFormat('nl-NL', {
  style: 'currency',
  currency: currency || 'EUR',
}).format((value || 0) / 100);

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  card: 'Betaalkaart',
  sepa_debit: 'SEPA-incasso',
  ideal: 'iDEAL',
  wero: 'Wero',
  paypal: 'PayPal',
};

const CARD_BRAND_LABELS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  american_express: 'American Express',
  amex: 'American Express',
  cartes_bancaires: 'Cartes Bancaires',
};

const BANK_CODE_LABELS: Record<string, string> = {
  ABNA: 'ABN AMRO',
  ASN: 'ASN Bank',
  BUNQ: 'bunq',
  INGB: 'ING',
  KNAB: 'Knab',
  RABO: 'Rabobank',
  RBRB: 'RegioBank',
  SNSB: 'SNS',
  TRIO: 'Triodos Bank',
};

const getPaymentMethodDisplay = (method: BillingSubscriptionSummaryApi['paymentMethod']) => {
  if (!method) return { title: 'Niet beschikbaar', detail: 'Beheer je betaalmethode via Stripe.' };
  if (method.type === 'card') {
    const brand = CARD_BRAND_LABELS[String(method.brand || '').toLowerCase()] || method.brand || 'Kaart';
    const expiry = method.expiryMonth && method.expiryYear
      ? ` · geldig tot ${String(method.expiryMonth).padStart(2, '0')}/${String(method.expiryYear).slice(-2)}`
      : '';
    return {
      title: `${brand} •••• ${method.last4 || '----'}`,
      detail: `${PAYMENT_METHOD_LABELS.card}${expiry}`,
    };
  }
  if (method.type === 'sepa_debit') {
    const bankName = BANK_CODE_LABELS[String(method.bankCode || '').toUpperCase()] || method.bankCode || 'Bankrekening';
    return {
      title: `${bankName} · IBAN •••• ${method.last4 || '----'}`,
      detail: `Automatische SEPA-incasso${method.country ? ` · ${method.country}` : ''}`,
    };
  }
  const methodLabel = PAYMENT_METHOD_LABELS[method.type] || method.type.replace(/_/g, ' ');
  const suffix = method.last4 ? ` •••• ${method.last4}` : '';
  return {
    title: `${method.bank || methodLabel}${suffix}`,
    detail: methodLabel,
  };
};

const formatPercent = (value: number): string => `${value.toFixed(2).replace('.', ',')}%`;

const padDatePart = (value: number): string => String(value).padStart(2, '0');

const buildIsoDate = (year: number, month: number, day: number): string => `${year}-${padDatePart(month)}-${padDatePart(day)}`;

const createStableDateFromIso = (isoDate: string): Date => {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

const getAmsterdamDateParts = (value = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: AMSTERDAM_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);

  return {
    year: Number(parts.find((part) => part.type === 'year')?.value || '0'),
    month: Number(parts.find((part) => part.type === 'month')?.value || '1'),
    day: Number(parts.find((part) => part.type === 'day')?.value || '1'),
  };
};

const toIsoDateFromUtcDate = (value: Date): string => buildIsoDate(
  value.getUTCFullYear(),
  value.getUTCMonth() + 1,
  value.getUTCDate(),
);

const shiftIsoDateByDays = (isoDate: string, dayDelta: number): string => {
  const value = createStableDateFromIso(isoDate);
  value.setUTCDate(value.getUTCDate() + dayDelta);
  return toIsoDateFromUtcDate(value);
};

const getInclusiveDayCount = (startDate: string, endDate: string): number => {
  const startValue = createStableDateFromIso(startDate);
  const endValue = createStableDateFromIso(endDate);
  if (Number.isNaN(startValue.getTime()) || Number.isNaN(endValue.getTime()) || endValue.getTime() < startValue.getTime()) return 0;
  return Math.floor((endValue.getTime() - startValue.getTime()) / 86400000) + 1;
};

const getHomepageArchiveRange = (preset: HomepageArchivePreset = 'this-month', now = new Date()): HomepageArchiveRange => {
  const current = getAmsterdamDateParts(now);
  const currentAmsterdamDate = buildIsoDate(current.year, current.month, current.day);

  if (preset === 'last-month') {
    const previousMonth = current.month === 1 ? 12 : current.month - 1;
    const previousYear = current.month === 1 ? current.year - 1 : current.year;
    const lastDay = new Date(Date.UTC(previousYear, previousMonth, 0, 12, 0, 0)).getUTCDate();
    return {
      startDate: buildIsoDate(previousYear, previousMonth, 1),
      endDate: buildIsoDate(previousYear, previousMonth, lastDay),
    };
  }

  return {
    startDate: buildIsoDate(current.year, current.month, 1),
    endDate: shiftIsoDateByDays(currentAmsterdamDate, -1),
  };
};

const getMillisecondsUntilNextAmsterdamMidnight = (now = new Date()): number => {
  const current = getAmsterdamDateParts(now);
  const currentIsoDate = buildIsoDate(current.year, current.month, current.day);
  let low = now.getTime() + 1000;
  let high = now.getTime() + 36 * 60 * 60 * 1000;

  while (high - low > 1000) {
    const midpoint = Math.floor((low + high) / 2);
    const midpointParts = getAmsterdamDateParts(new Date(midpoint));
    const midpointIsoDate = buildIsoDate(midpointParts.year, midpointParts.month, midpointParts.day);
    if (midpointIsoDate === currentIsoDate) {
      low = midpoint;
    } else {
      high = midpoint;
    }
  }

  return Math.max(1000, high - now.getTime() + 1500);
};

const getArchiveSummary = (items: SurebetHistoryApiItem[], startDate: string, endDate: string) => {
  const totalProfit = items.reduce((sum, item) => sum + Number(item.minimumProfit || 0), 0);
  const averageRoi = items.length > 0
    ? items.reduce((sum, item) => sum + Number(item.roiPercent || 0), 0) / items.length
    : 0;
  const periodDays = getInclusiveDayCount(startDate, endDate);

  return {
    count: items.length,
    totalProfit,
    averageProfitPerDay: periodDays > 0 ? totalProfit / periodDays : 0,
    averageRoi,
    periodDays,
  };
};

const getApiErrorDetail = (error: unknown, fallbackMessage: string): string => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
  }
  return fallbackMessage;
};

export const PublicPage: React.FC<PublicPageProps> = ({ title, description }) => {
  return (
    <PublicLayout>
      <section className="public-page-card">
        <div className="welcome-section public-hero">
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
        <div className="page-actions">
          <Link to="/" className="card-btn card-link back-button">
            Terug naar Home
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
};

export const PublicHomePage: React.FC = () => {
  const [faqOpen, setFaqOpen] = React.useState<number | null>(null);
  const summerCountdown = useSummerCountdown();
  const [archiveItems, setArchiveItems] = useState<SurebetHistoryApiItem[]>([]);
  const [archiveLoadState, setArchiveLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [archivePreset, setArchivePreset] = useState<HomepageArchivePreset>('this-month');
  const [archiveRange, setArchiveRange] = useState(() => getHomepageArchiveRange('this-month'));
  const archivePeriodLabel = archivePreset === 'this-month' ? 'deze maand' : 'vorige maand';
  const archiveSummary = useMemo(
    () => getArchiveSummary(archiveItems, archiveRange.startDate, archiveRange.endDate),
    [archiveItems, archiveRange.endDate, archiveRange.startDate],
  );

  const loadHomepageArchive = useCallback(async (range: HomepageArchiveRange) => {
    setArchiveLoadState('loading');
    try {
      const response = await trackerApi.getTrackerHistory({
        limit: 5000,
        minRoi: 0,
        startDate: range.startDate,
        endDate: range.endDate,
        includeCurrentDay: false,
      });
      setArchiveItems(Array.isArray(response.data) ? response.data : []);
      setArchiveLoadState('loaded');
    } catch (error) {
      setArchiveLoadState('error');
    }
  }, []);

  const handleArchiveRefresh = () => {
    const nextRange = getHomepageArchiveRange(archivePreset);
    if (nextRange.startDate === archiveRange.startDate && nextRange.endDate === archiveRange.endDate) {
      void loadHomepageArchive(nextRange);
      return;
    }
    setArchiveRange(nextRange);
  };

  useEffect(() => {
    let timeoutId: number | undefined;
    const scheduleArchiveRefresh = () => {
      timeoutId = window.setTimeout(() => {
        const nextRange = getHomepageArchiveRange(archivePreset);
        setArchiveRange((current) => (
          current.startDate === nextRange.startDate && current.endDate === nextRange.endDate ? current : nextRange
        ));
        scheduleArchiveRefresh();
      }, getMillisecondsUntilNextAmsterdamMidnight());
    };

    scheduleArchiveRefresh();

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [archivePreset]);

  useEffect(() => {
    const nextRange = getHomepageArchiveRange(archivePreset);
    setArchiveRange((current) => (
      current.startDate === nextRange.startDate && current.endDate === nextRange.endDate ? current : nextRange
    ));
  }, [archivePreset]);

  useEffect(() => {
    void loadHomepageArchive(archiveRange);
  }, [archiveRange, loadHomepageArchive]);
  
  const faqItems = [
    { q: "Garandeert PerfectOddsNL winst?", a: "Nee. PerfectOddsNL geeft informatieve odds-signalen. Odds kunnen wijzigen, markten kunnen sluiten en resultaten kunnen afwijken." },
    { q: "Is surebetten legaal?", a: "Ja, surebetten is legaal. Je gebruikt oddsverschillen tussen bookmakers en plaatst zelf je weddenschappen. Bookmakers en casino's hebben liever niet dat je dit doet en kunnen daarom accounts limiteren of inzetten weigeren." },
    { q: "Plaatsen jullie bets voor mij?", a: "Nee. Je ontvangt meldingen, maar je controleert en plaatst de bets zelf." },
    { q: "Wat krijg ik in het gratis kanaal?", a: "Tijdens actieve wedstrijdperiodes ontvang je ongeveer 1 tot 3 surebetmeldingen per week. Het gratis kanaal is bedoeld om kennis te maken." },
    { q: "Wat krijg ik bij een betaald abonnement?", a: "Meer meldingen via je betaalde Telegramkanaal en toegang tot de Surebet Tracker." },
    { q: "Wat is het verschil tussen pre-match en live?", a: "Pre-match is vóór de wedstrijd. Live is tijdens de wedstrijd. Live kan sneller bewegen en soms hogere ROI tonen, maar kansen verdwijnen ook sneller." },
    { q: "Waarom Bet365 en Kambi?", a: "PerfectOddsNL focust op oddsverschillen tussen Bet365 en Kambi-bookmakers, omdat die verschillen interessant kunnen zijn voor surebetters." },
  ];

  return (
    <PublicLayout>
      <section className="public-page-card home-page" style={{padding:'0'}}>
        <section className="home-hero-grid">
          <div className="home-hero-copy">
            <div className="home-eyebrow-row">
              <span className="home-eyebrow">Realtime oddsvergelijking</span>
              <span className="home-eyebrow">Live en pre-match alerts</span>
            </div>
            <h1>Gebruik wiskunde. Niet geluk.</h1>
            <p>
              PerfectOddsNL vergelijkt odds tussen Bet365 en Kambi-bookmakers en stuurt live en pre-match
              surebetkansen direct naar jou.
              <br />
              Jij controleert de odds zelf en houdt je resultaten bij in de Surebet Tracker.
            </p>
            <div className="public-cta-row home-hero-actions">
              <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
              <a href={FREE_TELEGRAM_JOIN_URL} className="card-btn card-link public-cta-secondary" target="_blank" rel="noreferrer">Gratis meedoen</a>
              <Link to="/surebets" className="card-btn card-link public-cta-secondary">Wat zijn surebets?</Link>
            </div>
          </div>

          <article className="home-arbitrage-card" aria-label="Arbitrage voorbeeld">
            <div className="home-alert-topline">
              <span>PerfectOddsNL Alert</span>
              <span>Zojuist gedetecteerd</span>
            </div>
            <div className="home-alert-title-row">
              <div>
                <p>Arbitrage kans</p>
                <h2>FC Barcelona vs. Real Madrid</h2>
                <span>Totaal aantal doelpunten 2.5</span>
              </div>
              <strong>21.5%</strong>
            </div>
            <div className="home-bookmaker-grid">
              <div className="home-bookmaker-card">
                <span>Betcity</span>
                <strong>Minder dan 2.5</strong>
                <b>2.70</b>
                <small>Inzet: €90.00</small>
                <small>Uitbetaling A: €243.00</small>
              </div>
              <div className="home-bookmaker-card">
                <span>Bet365</span>
                <strong>Meer dan 2.5</strong>
                <b>2.21</b>
                <small>Inzet: €110.00</small>
                <small>Uitbetaling B: €243.00</small>
              </div>
            </div>
            <div className="home-profit-panel">
              <div><span>Totale inzet</span><strong>€200</strong></div>
              <div><span>Uitbetaling</span><strong>€243</strong></div>
              <div><span>Winst</span><strong>+€43</strong></div>
            </div>
            <p className="home-card-footnote">Voorbeeldmelding. Dit is geen financieel advies en geen winstgarantie.</p>
          </article>
        </section>

        <section className="home-summer-action-shell">
          <SummerActionBanner countdown={summerCountdown} />
        </section>

        {/* Trust strip */}
        <div style={{background:'rgba(0,201,142,0.05)',borderTop:'1px solid rgba(0,201,142,0.1)',borderBottom:'1px solid rgba(0,201,142,0.1)',padding:'32px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))',gap:'32px',maxWidth:'1200px',margin:'0 auto'}}>
            <div style={{textAlign:'center'}}>
              <h3 style={{fontSize:'16px',fontWeight:'900',color:'var(--text)',marginBottom:'6px'}}>Telegram-first</h3>
              <p style={{fontSize:'14px',color:'var(--muted)'}}>Geen ingewikkeld odds-dashboard nodig.</p>
            </div>
            <div style={{textAlign:'center'}}>
              <h3 style={{fontSize:'16px',fontWeight:'900',color:'var(--text)',marginBottom:'6px'}}>Bet365 + Kambi</h3>
              <p style={{fontSize:'14px',color:'var(--muted)'}}>Gefocust op relevante Nederlandse odds-bronnen.</p>
            </div>
            <div style={{textAlign:'center'}}>
              <h3 style={{fontSize:'16px',fontWeight:'900',color:'var(--text)',marginBottom:'6px'}}>Tracker inbegrepen</h3>
              <p style={{fontSize:'14px',color:'var(--muted)'}}>Bij elk betaald abonnement.</p>
            </div>
            <div style={{textAlign:'center'}}>
              <h3 style={{fontSize:'16px',fontWeight:'900',color:'var(--text)',marginBottom:'6px'}}>Besloten kanalen</h3>
              <p style={{fontSize:'14px',color:'var(--muted)'}}>Automatisch gekoppeld aan je abonnement.</p>
            </div>
          </div>
        </div>

        <section className="home-section-shell home-process-section">
          <div className="home-section-heading">
            <h2>Zo werkt live en pre-match <span>surebetten</span></h2>
            <p>Van detectie tot plaatsing: elke stap is ingericht op snelle controle en duidelijke inzetverdeling.</p>
          </div>
          <div className="home-process-flow">
            {[
              {k:'Realtime detectie',t:'Ontvang een alert',d:'Tijdens actieve wedstrijdperiodes vergelijkt PerfectOddsNL relevante live en pre-match markten. Zodra de odds bij verschillende bookmakers samen een arbitragekans vormen, krijg je direct een duidelijke Telegrammelding.',m:'FC Barcelona vs. Real Madrid · Totaal goals 2.5 · 21.5% winst',details:['Wedstrijd, markt en selectie staan direct in de alert.','Je ziet welke bookmaker welke kant van de bet heeft.','Live kansen worden zo compact mogelijk getoond zodat je snel kunt controleren.']},
              {k:'Automatisch berekend',t:'Bekijk je inzetverdeling',d:'De melding laat zien hoeveel je per bookmaker inzet en welke uitbetaling je bij beide uitkomsten verwacht. Zo hoef je niet eerst zelf met odds en stakes te rekenen.',m:'€90 bij Betcity + €110 bij Bet365 · uitbetaling €243',details:['Totale inzet, uitbetaling en winst staan naast elkaar.','De marge wordt gebaseerd op de actuele odds in de alert.','Verandert een odd, dan controleer je de kans opnieuw voordat je inzet.']},
              {k:'Zelf plaatsen',t:'Open de bookmaker en controleer',d:'PerfectOddsNL plaatst nooit automatisch bets. Jij opent de bookmaker, checkt of de selectie en odds nog kloppen en plaatst daarna zelf de inzet.',m:'Inzet €200 → verwachte winst €43',details:['Controleer altijd wedstrijd, markt, line en odd.','Gebruik de inzetverdeling uit de alert als leidraad.','Sla een kans over als de odd is veranderd of de markt gesloten is.']},
              {k:'Resultaat bijhouden',t:'Werk je tracker bij',d:'Na het plaatsen sla je de surebet op in je tracker. Daar vul je je echte odds en inzetten in, zodat je resultaat, ROI, kosten en correcties overzichtelijk blijven.',m:'Al je geplaatste kansen op één plek',details:['Bewaar geplaatste surebets vanuit je geschiedenis.','Pas werkelijke odds en inzetbedragen achteraf aan.','Bekijk open bets, afgeronde bets en je netto resultaat op één plek.']},
            ].map((item, i) => (
              <article className="home-process-step" key={item.t}>
                <div className="home-step-number">{i + 1}</div>
                <div>
                  <span>{item.k}</span>
                  <h3>{item.t}</h3>
                  <p>{item.d}</p>
                  <ul className="home-process-details">
                    {item.details.map((detail) => <li key={detail}>{detail}</li>)}
                  </ul>
                  <strong>{item.m}</strong>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="home-section-shell home-archive-section" aria-label="Surebet archief maandstatistieken">
          <article className="home-archive-card">
            <div className="home-archive-copy">
              <span className="home-archive-eyebrow">Surebet archief</span>
              <h2>Surebet archief</h2>
              <p>
                Het archief wordt dagelijks om 00:00 bijgewerkt met de laatst afgesloten dag.
                Bekijk welke surebets er {archivePeriodLabel} zijn gevonden en wat ze minimaal hadden kunnen opleveren.
              </p>
              <p className="home-archive-pause">
                <strong>Zomerstop:</strong> de surebetscanner staat sinds 22 juli 2026 tijdelijk uit.
              </p>
              <div className="home-archive-toggle" aria-label="Kies archiefperiode">
                <button
                  type="button"
                  className={archivePreset === 'this-month' ? 'is-active' : undefined}
                  onClick={() => setArchivePreset('this-month')}
                >
                  Deze maand
                </button>
                <button
                  type="button"
                  className={archivePreset === 'last-month' ? 'is-active' : undefined}
                  onClick={() => setArchivePreset('last-month')}
                >
                  Vorige maand
                </button>
              </div>
              <Link to="/tracker/surebet-history" className="card-btn card-link home-archive-button">Bekijk surebet archief</Link>
            </div>
            <div className="home-archive-stats" aria-busy={archiveLoadState === 'loading'}>
              <div className="home-archive-stat home-archive-stat-primary">
                <span>Aantal surebets {archivePeriodLabel}</span>
                <strong>{archiveLoadState === 'loading' ? '...' : archiveSummary.count}</strong>
              </div>
              <div className="home-archive-stat home-archive-stat-profit">
                <span>Mogelijke winst {archivePeriodLabel}</span>
                <strong>{archiveLoadState === 'loading' ? '...' : formatEuro(archiveSummary.totalProfit)}</strong>
              </div>
              <div className="home-archive-stat">
                <span>Gemiddelde winst per dag {archivePeriodLabel}</span>
                <strong>{archiveLoadState === 'loading' ? '...' : formatEuro(archiveSummary.averageProfitPerDay)}</strong>
              </div>
              <div className="home-archive-stat">
                <span>Gemiddelde ROI {archivePeriodLabel}</span>
                <strong>{archiveLoadState === 'loading' ? '...' : formatPercent(archiveSummary.averageRoi)}</strong>
              </div>
              {archiveLoadState === 'error' && (
                <div className="home-archive-error">
                  <p>Archiefstatistieken konden niet geladen worden.</p>
                  <button type="button" onClick={handleArchiveRefresh}>
                    Ververs
                  </button>
                </div>
              )}
            </div>
          </article>
        </section>

        {/* Why PerfectOddsNL */}
        <section style={{maxWidth:'1200px',margin:'64px auto',padding:'0 32px'}}>
          <h2 style={{textAlign:'center',fontSize:'clamp(28px, 4vw, 38px)',fontWeight:'900',color:'var(--text)',marginBottom:'16px'}}>Waarom PerfectOddsNL?</h2>
          <p style={{textAlign:'center',fontSize:'18px',color:'var(--muted)',marginBottom:'44px',maxWidth:'600px',margin:'0 auto 44px'}}>PerfectOddsNL is gebouwd voor Nederlandse surebetters die snelheid, overzicht en eenvoud willen.</p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))',gap:'28px'}}>
            {[
              {t:'Direct in Telegram',d:'Ontvang meldingen waar je ze snel ziet: op je telefoon of desktop.'},
              {t:'Geen live dashboard nodig',d:'Je hoeft niet constant een website open te houden om kansen te vinden.'},
              {t:'Bet365 en Kambi focus',d:'PerfectOddsNL richt zich op oddsverschillen tussen Bet365 en Kambi-bookmakers.'},
              {t:'Surebet Tracker',d:'Houd bij welke bets je hebt geplaatst, welke odds je kreeg en wat je totale winst is.'},
              {t:'Persoonlijke kanaaltoegang',d:'De bot koppelt de betaalde Telegramkanalen automatisch aan je actieve abonnement.'},
              {t:'Live en pre-match kansen',d:'PerfectOddsNL biedt live en pre-match surebetmeldingen, zodat je kunt kiezen welke manier van werken het beste bij je past.'},
            ].map((item,i)=>(
              <div key={i} style={{background:'var(--surface)',border:'1px solid var(--line)',borderRadius:'18px',padding:'24px',boxShadow:'0 8px 32px rgba(0,0,0,0.08)'}}>
                <h3 style={{fontSize:'16px',fontWeight:'900',color:'var(--text)',marginBottom:'8px'}}>{item.t}</h3>
                <p style={{fontSize:'14px',color:'var(--muted)',lineHeight:'1.6'}}>{item.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section style={{maxWidth:'900px',margin:'64px auto',padding:'0 32px'}}>
          <h2 style={{textAlign:'center',fontSize:'clamp(28px, 4vw, 38px)',fontWeight:'900',color:'var(--text)',marginBottom:'44px'}}>Veelgestelde vragen</h2>
          <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
            {faqItems.map((item,i)=>(
              <div key={i} style={{background:'var(--surface)',border:'1px solid var(--line)',borderRadius:'12px',overflow:'hidden'}}>
                <button
                  type="button"
                  aria-expanded={faqOpen === i}
                  aria-controls={`home-faq-answer-${i}`}
                  onClick={()=>setFaqOpen(faqOpen===i?null:i)}
                  style={{width:'100%',padding:'18px',textAlign:'left',background:'none',border:'none',cursor:'pointer',fontSize:'16px',fontWeight:'700',color:'var(--text)',display:'flex',justifyContent:'space-between',alignItems:'center'}}
                >
                  {item.q}
                  <span style={{fontSize:'20px'}} aria-hidden="true">{faqOpen===i?'−':'+'}</span>
                </button>
                {faqOpen===i&&<div id={`home-faq-answer-${i}`} style={{padding:'16px 18px 20px',fontSize:'14px',color:'var(--muted)',lineHeight:'1.7',borderTop:'1px solid var(--line)'}}>{item.a}</div>}
              </div>
            ))}
          </div>
          <div style={{textAlign:'center',marginTop:'32px'}}>
            <Link to="/veelgestelde-vragen" style={{color:'#00c98e',textDecoration:'none',fontSize:'14px',fontWeight:'800'}}>Bekijk FAQ →</Link>
          </div>
        </section>

        {/* Final CTA */}
        <section style={{maxWidth:'900px',margin:'64px auto',padding:'0 32px',textAlign:'center'}}>
          <h2 style={{fontSize:'clamp(28px, 4vw, 38px)',fontWeight:'900',color:'var(--text)',marginBottom:'16px'}}>Klaar om surebetkansen sneller te vinden?</h2>
          <p style={{fontSize:'18px',color:'var(--muted)',marginBottom:'32px'}}>Doe gratis mee of kies direct een abonnement met betaalde Telegramalerts en de Surebet Tracker.</p>
          <div className="public-cta-row">
            <a href={FREE_TELEGRAM_JOIN_URL} className="card-btn card-link" target="_blank" rel="noreferrer">Gratis meedoen</a>
            <Link to="/pricing" className="card-btn card-link public-cta-secondary">Bekijk abonnementen</Link>
          </div>
        </section>

        {/* Disclaimer */}
        <section style={{maxWidth:'1200px',margin:'64px auto 48px',padding:'0 32px'}}>
          <div style={{background:'rgba(200, 30, 30, 0.05)',border:'1px solid rgba(200, 30, 30, 0.1)',borderRadius:'14px',padding:'24px'}}>
            <h3 style={{fontSize:'16px',fontWeight:'900',color:'var(--text)',marginBottom:'12px'}}>Belangrijk om te weten</h3>
            <p style={{fontSize:'14px',color:'var(--muted)',lineHeight:'1.7',margin:'0'}}><DisclaimerText /></p>
          </div>
        </section>

      </section>
    </PublicLayout>
  );
};

export const PricingPage: React.FC = () => {
  const summerCountdown = useSummerCountdown();

  const plans = [
    {
      title: 'Gratis kanaal',
      price: 'Gratis',
      description: 'Maak gratis kennis met PerfectOddsNL.',
      features: [
        'Instapgroep',
        'Basisupdates',
      ],
      recommended: false,
      planId: 'gratis',
      buttonText: 'Gratis meedoen',
    },
    {
      title: 'Pro abonnement',
      price: summerCountdown.active ? SUMMER_ACTION_PRICE : '€64,95 / maand',
      oldPrice: summerCountdown.active ? SUMMER_ACTION_OLD_PRICE : undefined,
      priceSuffix: summerCountdown.active ? 'Eerste maand, daarna €64,95 per maand.' : undefined,
      description: 'Pre-match en live meldingen in één abonnement.',
      features: [
        'Pre-match + live meldingen',
        'Hoogste notificatieprioriteit',
        'Surebet Tracker inbegrepen',
      ],
      recommended: true,
      planId: 'pro',
      buttonText: 'Bekijk opties',
    },
    {
      title: 'Pre-match abonnement',
      price: '€34,95 / maand',
      description: 'Kansen ontvangen vóór de wedstrijd begint.',
      features: [
        'Pre-match surebetmeldingen',
        'Snelste Telegram notificaties',
        'Surebet Tracker inbegrepen',
      ],
      recommended: false,
      planId: 'pre-match',
      buttonText: 'Bekijk opties',
    },
    {
      title: 'Live abonnement',
      price: '€49,95 / maand',
      description: 'Kansen ontvangen terwijl de wedstrijd loopt.',
      features: [
        'Live surebetmeldingen',
        'Geld sneller terug op je accounts',
        'Surebet Tracker inbegrepen',
      ],
      recommended: false,
      planId: 'live',
      buttonText: 'Bekijk opties',
    },
  ];

  const comparisonRows = [
    {
      feature: 'Maandprijs',
      gratis: '-',
      pro: summerCountdown.active ? 'Eerste maand €44,95, daarna €64,95' : '€64,95',
      preMatch: '€34,95',
      live: '€49,95',
    },
    { feature: 'Prijs week', gratis: '-', pro: '€19,95', preMatch: '€9,95', live: '€14,95' },
    { feature: 'Prijs dagpas', gratis: '-', pro: '€3,95', preMatch: '-', live: '-' },
    { feature: 'Pre-match meldingen', gratis: 'Nee', pro: 'Ja', preMatch: 'Ja', live: 'Nee' },
    { feature: 'Live meldingen', gratis: 'Nee', pro: 'Ja', preMatch: 'Nee', live: 'Ja' },
    { feature: 'Beschikbare looptijd', gratis: '-', pro: 'Dagpas, week of maand', preMatch: 'Week of maand', live: 'Week of maand' },
    { feature: 'Surebet Tracker', gratis: 'Nee', pro: 'Ja', preMatch: 'Ja', live: 'Ja' },
    { feature: 'Persoonlijke Telegramkoppeling', gratis: 'Niet nodig', pro: 'Ja', preMatch: 'Ja', live: 'Ja' },
  ];

  return (
    <PublicLayout>
      <section className="public-page-card pricing-page public-refresh pricing-refresh">
        <section className="pricing-hero">
          <span className="subscription-badge">Abonnementen</span>
          <h1>Kies het PerfectOddsNL abonnement dat bij je past</h1>
          <p>
            Krijg surebetmeldingen via Telegram en gebruik de Surebet Tracker om je geplaatste surebets,
            inzetten, winst, ROI en betalingen bij te houden.
          </p>
        </section>

        <SummerActionBanner countdown={summerCountdown} compact showAction={false} />

        <section className="pricing-grid">
          {plans.map((plan) => (
            <article key={plan.title} className={`pricing-card ${plan.recommended ? 'recommended' : ''}`}>
              {plan.recommended && summerCountdown.active && <span className="pricing-badge">Zomeractie</span>}
              <h2>{plan.title}</h2>
              {plan.oldPrice ? (
                <p className="pricing-price pricing-price-promo">
                  <PromoPrice oldPrice={plan.oldPrice} price={plan.price} suffix={plan.priceSuffix} />
                </p>
              ) : (
                <p className="pricing-price">{plan.price}</p>
              )}
              <p className="pricing-description">{plan.description}</p>
              <ul className="pricing-feature-list">
                {plan.features.map((feature) => (
                  <li key={feature} className="pricing-feature-item">
                    {feature}
                  </li>
                ))}
              </ul>
              {plan.planId === 'gratis' ? (
                <a href={FREE_TELEGRAM_JOIN_URL} className="card-btn pricing-card-button" target="_blank" rel="noreferrer">
                  {plan.buttonText}
                </a>
              ) : (
                <Link to={`/checkout?plan=${plan.planId}&billingCycle=month`} className="card-btn pricing-card-button">
                  {plan.buttonText}
                </Link>
              )}
            </article>
          ))}
        </section>

        <section className="pricing-community-note">
          Bij elk betaald abonnement krijg je toegang tot het bijbehorende besloten Telegramkanaal en de
          Surebet Tracker. De bot verleent en verwijdert je kanaaltoegang automatisch op basis van je abonnement.
        </section>

        <section className="pricing-referral-note" aria-label="Voorwaarden vriendenkorting">
          <strong>Vriendenkorting</strong>
          <span>Maximaal één keer €10 korting per maandbetaling. Niet combineerbaar met andere acties of kortingscodes; je korting blijft dan automatisch klaarstaan voor een volgende geschikte maand.</span>
        </section>

        <section className="pricing-comparison">
          <h2>Vergelijking</h2>
          <div className="pricing-table-wrap">
            <table className="pricing-table">
              <thead>
                <tr>
                  <th>Onderdeel</th>
                  <th>Gratis</th>
                  <th>Pro</th>
                  <th>Pre-match</th>
                  <th>Live</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row) => (
                  <tr key={row.feature}>
                    <td>{row.feature}</td>
                    <td>{row.gratis}</td>
                    <td>{row.pro}</td>
                    <td>{row.preMatch}</td>
                    <td>{row.live}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <footer className="public-footer-note pricing-disclaimer">
          <DisclaimerText />
        </footer>
      </section>
    </PublicLayout>
  );
};

// Checkout pages
const PLAN_DETAILS: Record<string, { name: string; features: string[] }> = {
  gratis: {
    name: 'Gratis kanaal',
    features: ['Toegang tot het gratis Telegramkanaal', 'Ongeveer 1 tot 3 meldingen per week', 'Geen Surebet Tracker'],
  },
  pro: {
    name: 'Pro abonnement',
    features: ['Pre-match surebetmeldingen', 'Live surebetmeldingen', 'Persoonlijke Telegramtoegang', 'Surebet Tracker', 'Resultaten en ROI', 'Kosten en correcties'],
  },
  'pre-match': {
    name: 'Pre-match abonnement',
    features: ['Pre-match surebetmeldingen', 'Persoonlijke Telegramtoegang', 'Surebet Tracker', 'Resultaten en ROI', 'Kosten en correcties'],
  },
  live: {
    name: 'Live abonnement',
    features: [
      'Live surebetmeldingen',
      'Inzet plus eventuele winst vaak binnen 1 tot 2 uur terug op je account',
      'Persoonlijke Telegramtoegang',
      'Surebet Tracker',
      'Resultaten en ROI',
      'Kosten en correcties',
    ],
  },
};

export const CheckoutPage: React.FC = () => {
  const { user } = useAuth();
  const summerCountdown = useSummerCountdown();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const plan = searchParams.get('plan') as keyof typeof PLAN_DETAILS | null;
  const checkoutStartedFromAccount = searchParams.get('source') === 'account';
  const requestedBillingCycle = normalizeBillingCycleForPlan(plan, searchParams.get('billingCycle') ?? searchParams.get('cycle'));
  const [checkoutError, setCheckoutError] = useState('');
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);
  const hasActiveRecurringSubscription = Boolean(
    user
    && user.role !== 'admin'
    && user.subscriptionStatus === 'active'
    && ['pro', 'pre-match', 'live'].includes(user.plan)
    && user.billingCycle !== 'day-pass',
  );
  const hasActiveDayPass = Boolean(
    user
    && user.role !== 'admin'
    && user.subscriptionStatus === 'active'
    && user.billingCycle === 'day-pass',
  );
  const selectedBillingCycle = hasActiveDayPass && requestedBillingCycle === 'day-pass'
    ? 'month'
    : requestedBillingCycle;
  const selectedBillingOption = isPaidPlanId(plan)
    ? getBillingOptions(plan).find((option) => option.value === selectedBillingCycle)
    : null;

  const handleOpenBillingPortal = async () => {
    setCheckoutError('');
    setIsStartingCheckout(true);
    try {
      const response = await billingAPI.createPortalSession();
      const portalUrl = response.data?.url;
      if (typeof portalUrl === 'string' && portalUrl) {
        window.location.assign(portalUrl);
        return;
      }
      setCheckoutError('Het abonnementsportaal kon niet worden geopend.');
    } catch (error) {
      setCheckoutError(getApiErrorDetail(error, 'Het abonnementsportaal kon niet worden geopend.'));
    } finally {
      setIsStartingCheckout(false);
    }
  };

  const handleCheckoutBillingCycleChange = (nextBillingCycle: PaidBillingCycle) => {
    if (!isPaidPlanId(plan)) return;
    const nextParams = new URLSearchParams(location.search);
    nextParams.set('plan', plan);
    nextParams.set('billingCycle', nextBillingCycle);
    nextParams.delete('cycle');
    navigate(`/checkout?${nextParams.toString()}`, { replace: true });
  };

  if (!plan || !PLAN_DETAILS[plan]) {
    return (
      <PublicLayout>
        <section className="public-page-card subscription-page">
          <section className="subscription-hero">
            <h1>Geen abonnement gekozen</h1>
            <p>Kies een abonnement op de abonnementenpagina om verder te gaan.</p>
            <div className="subscription-cta-row">
              <Link to="/pricing" className="card-btn card-link">Terug naar abonnementen</Link>
            </div>
          </section>
        </section>
      </PublicLayout>
    );
  }

  if (hasActiveRecurringSubscription) {
    return (
      <PublicLayout>
        <section className="public-page-card subscription-page checkout-page">
          <section className="checkout-existing-subscription card">
            <span className="checkout-success-eyebrow">Abonnement actief</span>
            <h1>Je hebt al een actief abonnement</h1>
            <p>
              Een tweede Checkout zou een dubbel abonnement veroorzaken. Open je beveiligde Stripe-portaal om van pakket of looptijd te wisselen.
            </p>
            {checkoutError && <p className="checkout-error" role="alert">{checkoutError}</p>}
            <div className="subscription-cta-row">
              <button type="button" className="card-btn" onClick={() => { void handleOpenBillingPortal(); }} disabled={isStartingCheckout}>
                {isStartingCheckout ? 'Portaal openen...' : 'Abonnement wijzigen'}
              </button>
              <Link to="/mijn-account?tab=abonnement" className="card-btn card-link account-secondary-btn">Terug naar Mijn account</Link>
            </div>
          </section>
        </section>
      </PublicLayout>
    );
  }

  const planInfo = PLAN_DETAILS[plan];
  const isPaid = plan !== 'gratis';
  const checkoutSourceSuffix = checkoutStartedFromAccount ? '&source=account' : '';
  const checkoutReturnPath = `/checkout?plan=${encodeURIComponent(plan)}&billingCycle=${encodeURIComponent(selectedBillingCycle)}${checkoutSourceSuffix}`;
  const checkoutBackPath = checkoutStartedFromAccount ? '/mijn-account' : '/pricing';
  const checkoutBackLabel = checkoutStartedFromAccount ? 'Mijn account' : 'Abonnementen';
  const loginPath = `/login?redirect=${encodeURIComponent(checkoutReturnPath)}`;
  const checkoutPrice = selectedBillingOption?.price ?? 'Gratis';
  const checkoutPriceNote = isPaidPlanId(plan)
    ? getBillingOptionTerms(plan, selectedBillingCycle)
    : 'Geen betaling nodig.';
  const summerActionApplies = summerCountdown.active && plan === 'pro' && selectedBillingCycle === 'month';
  const checkoutActionLabel = isStartingCheckout
    ? (hasActiveDayPass && selectedBillingCycle !== 'day-pass' ? 'Abonnement inplannen...' : 'Betaling starten...')
    : plan === 'gratis'
      ? 'Gratis kanaal openen'
      : BILLING_MODE === 'api'
        ? (hasActiveDayPass && selectedBillingCycle !== 'day-pass'
          ? 'Betaalmethode opslaan en abonnement inplannen'
          : 'Veilig verder naar betalen')
        : 'Abonnement bevestigen';

  const handleActivatePlan = async () => {
    setCheckoutError('');

    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(checkoutReturnPath)}`, {
        state: { from: checkoutReturnPath },
      });
      return;
    }

    if (isPaidPlanId(plan) && BILLING_MODE === 'api') {
      setIsStartingCheckout(true);
      try {
        const response = await billingAPI.createCheckoutSession(plan, selectedBillingCycle as PaidBillingCycle);
        const checkoutUrl = response.data?.url || response.data?.checkoutUrl;
        if (typeof checkoutUrl === 'string' && checkoutUrl) {
          window.location.assign(checkoutUrl);
          return;
        }
        setCheckoutError('Betaalsessie aangemaakt, maar er kwam nog geen checkout-url terug.');
      } catch (error) {
        setCheckoutError(getApiErrorDetail(error, 'Betaalsessie kon nog niet worden gestart.'));
      } finally {
        setIsStartingCheckout(false);
      }
      return;
    }

    const updatedUser = { ...user };

    if (plan === 'gratis') {
      updatedUser.subscriptionStatus = 'none';
      updatedUser.plan = 'free';
      updatedUser.billingCycle = 'none';
    } else {
      updatedUser.subscriptionStatus = 'active';
      updatedUser.plan = plan as 'pro' | 'pre-match' | 'live';
      updatedUser.billingCycle = selectedBillingCycle;
      updatedUser.planLabel = getPlanLabel(updatedUser.plan, selectedBillingCycle);
    }

    localStorage.setItem('perfectodds_current_user', JSON.stringify(updatedUser));
    navigate(isPaid ? '/checkout/success' : '/checkout/success');
  };

  return (
    <PublicLayout>
      <section className="public-page-card subscription-page checkout-page">
        <header className="checkout-topbar">
          <Link to={checkoutBackPath} className="checkout-back-link" aria-label={`Terug naar ${checkoutBackLabel.toLowerCase()}`}>
            <span aria-hidden="true">←</span>
            {checkoutBackLabel}
          </Link>
          <div className="checkout-progress" aria-label="Checkout voortgang">
            <span className="active"><b>1</b> Overzicht</span>
            <i aria-hidden="true" />
            <span><b>2</b> Betaling</span>
          </div>
        </header>

        <div className="checkout-heading">
          <span className="checkout-eyebrow">Bijna geregeld</span>
          <h1>Rond je abonnement af</h1>
          <p>
            {hasActiveDayPass && selectedBillingCycle !== 'day-pass'
              ? 'Controleer je keuze. Je slaat hierna veilig je betaalmethode op; de eerste afschrijving volgt pas nadat je dagpas afloopt.'
              : 'Controleer je keuze. Je betaalt pas in de volgende stap.'}
          </p>
        </div>

        {!user && (
          <div className="checkout-login-note">
            <span className="checkout-login-icon" aria-hidden="true">i</span>
            <p>
              Je kunt je keuze nu bekijken. Voor het afronden vragen we je eerst om{' '}
              <Link to={loginPath} state={{ from: checkoutReturnPath }}>in te loggen</Link>.
            </p>
          </div>
        )}

        {plan === 'pro' && <SummerActionBanner countdown={summerCountdown} compact showAction={false} />}

        <section className="checkout-layout">
          <div className="checkout-main-column">
            {isPaidPlanId(plan) && (
              <section className="checkout-panel checkout-cycle-panel" aria-labelledby="checkout-cycle-title">
                <div className="checkout-section-heading">
                  <div>
                    <span className="checkout-section-step">01</span>
                    <h2 id="checkout-cycle-title">Kies je looptijd</h2>
                  </div>
                  <p>Je kunt dit later vanuit je account beheren.</p>
                </div>

                <div className="checkout-cycle-grid" role="radiogroup" aria-label={`Looptijd voor ${planInfo.name}`}>
                  {getBillingOptions(plan).filter((option) => !(hasActiveDayPass && option.value === 'day-pass')).map((option) => {
                    const isSelected = selectedBillingCycle === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        className={`checkout-cycle-option${isSelected ? ' active' : ''}`}
                        aria-pressed={isSelected}
                        onClick={() => handleCheckoutBillingCycleChange(option.value)}
                      >
                        <span className="checkout-cycle-radio" aria-hidden="true" />
                        <span className="checkout-cycle-copy">
                          <strong>{option.label}</strong>
                          <small>{option.note}</small>
                        </span>
                        <span className="checkout-cycle-price">
                          {option.oldPrice && <del>{option.oldPrice}</del>}
                          <b>{option.price}</b>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            <section className="checkout-panel checkout-benefits-panel" aria-labelledby="checkout-benefits-title">
              <div className="checkout-section-heading">
                <div>
                  <span className="checkout-section-step">{isPaidPlanId(plan) ? '02' : '01'}</span>
                  <h2 id="checkout-benefits-title">Inbegrepen bij {planInfo.name}</h2>
                </div>
              </div>
              <ul className="checkout-features">
                {planInfo.features.map((feature) => (
                  <li key={feature}>
                    <span aria-hidden="true">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          <aside className="checkout-summary" aria-label="Besteloverzicht">
            <span className="checkout-summary-badge">Jouw keuze</span>
            <h2>{planInfo.name}</h2>
            <div className="checkout-summary-price">
              {selectedBillingOption?.oldPrice && <del>{selectedBillingOption.oldPrice}</del>}
              <strong>{checkoutPrice}</strong>
              {isPaidPlanId(plan) && <span>{selectedBillingOption?.label}</span>}
            </div>

            <div className="checkout-summary-divider" />

            <dl className="checkout-summary-list">
              <div>
                <dt>Abonnement</dt>
                <dd>{planInfo.name}</dd>
              </div>
              <div>
                <dt>Looptijd</dt>
                <dd>{selectedBillingOption?.label ?? 'Doorlopend gratis'}</dd>
              </div>
              {summerActionApplies && (
                <div>
                  <dt>Welkomstkorting</dt>
                  <dd>Automatisch toegepast</dd>
                </div>
              )}
              {summerActionApplies && (
                <div>
                  <dt>Daarna</dt>
                  <dd>€64,95 per maand</dd>
                </div>
              )}
              <div>
                <dt>Betaalmethode</dt>
                <dd>{isPaid ? 'Kies je in de volgende stap' : 'Niet nodig'}</dd>
              </div>
            </dl>

            <div className="checkout-summary-total">
              <span>Totaal</span>
              <strong>{checkoutPrice}</strong>
            </div>
            <p className="checkout-summary-terms">{checkoutPriceNote}</p>
            {isPaid && selectedBillingCycle === 'month' && (
              <p className="checkout-discount-rule">
                <strong>Over kortingen</strong>
                Vriendenkorting en andere acties worden niet gecombineerd. Per maandbetaling kan maximaal één vriendenkorting van €10 worden gebruikt. Een ongebruikte vriendenkorting blijft automatisch klaarstaan voor een volgende maand.
              </p>
            )}

            {checkoutError && <p className="checkout-error" role="alert">{checkoutError}</p>}

            <button
              type="button"
              className="checkout-submit-button"
              onClick={handleActivatePlan}
              disabled={isStartingCheckout}
              aria-busy={isStartingCheckout}
            >
              {checkoutActionLabel}
              {!isStartingCheckout && <span aria-hidden="true">→</span>}
            </button>

            <div className="checkout-secure-note">
              <span aria-hidden="true">✓</span>
              <p>Je keuze wordt veilig verwerkt. Je zit vóór de betaalstap nog nergens aan vast.</p>
            </div>
          </aside>
        </section>
      </section>
    </PublicLayout>
  );
};

export const CheckoutSuccessPage: React.FC = () => {
  const { user, updateCurrentUser } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sessionId = searchParams.get('session_id');
  const [isRefreshingCheckout, setIsRefreshingCheckout] = useState(BILLING_MODE === 'api' && Boolean(sessionId));
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutTelegramLink, setCheckoutTelegramLink] = useState<TelegramLinkState | null>(null);
  const [isStartingTelegramLink, setIsStartingTelegramLink] = useState(false);
  const [deferredSubscription, setDeferredSubscription] = useState<{ billingCycle: string; startAt: string | null } | null>(null);

  useEffect(() => {
    if (BILLING_MODE !== 'api' || !sessionId) {
      setIsRefreshingCheckout(false);
      return;
    }

    let isMounted = true;

    const refreshCheckoutSession = async () => {
      setCheckoutError('');
      try {
        const response = await billingAPI.getCheckoutSession(sessionId);
        if (!isMounted) {
          return;
        }
        if (response.data?.user) {
          updateCurrentUser(response.data.user);
        }
        if (response.data?.deferredSubscription) {
          setDeferredSubscription({
            billingCycle: response.data.billingCycle || 'month',
            startAt: response.data.scheduledStartAt || null,
          });
        }
        try {
          const telegramResponse = await authAPI.getTelegramLink();
          if (isMounted) {
            setCheckoutTelegramLink(telegramResponse.data.telegramLink as TelegramLinkState);
          }
        } catch {
          // De betaling blijft geslaagd als de aanvullende Telegram-controle tijdelijk niet lukt.
        }
        try {
          await billingAPI.listInvoices();
        } catch {
          // De Stripe-webhook blijft de primaire route voor facturen en automatische trackerkosten.
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }
        const detail = typeof (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail === 'string'
          ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : null;
        setCheckoutError(detail || 'Je Stripe-betaling kon nog niet worden bevestigd. Vernieuw deze pagina over een paar seconden opnieuw.');
      } finally {
        if (isMounted) {
          setIsRefreshingCheckout(false);
        }
      }
    };

    void refreshCheckoutSession();

    return () => {
      isMounted = false;
    };
  }, [sessionId, updateCurrentUser]);

  const isPaid = user?.subscriptionStatus === 'active';
  const deferredStartLabel = deferredSubscription?.startAt
    ? new Date(deferredSubscription.startAt).toLocaleString('nl-NL', { dateStyle: 'long', timeStyle: 'short' })
    : 'nadat je dagpas afloopt';
  const deferredCycleLabel = deferredSubscription?.billingCycle === 'week' ? 'weekabonnement' : 'maandabonnement';
  const billingCycleLabel = user?.billingCycle === 'month'
    ? 'Maandelijks'
    : user?.billingCycle === 'week'
      ? 'Wekelijks'
      : user?.billingCycle === 'day-pass'
        ? 'Eenmalige dagpas'
        : 'Niet van toepassing';
  const checkoutTelegramInvites = checkoutTelegramLink?.inviteLinks?.length
    ? checkoutTelegramLink.inviteLinks
    : checkoutTelegramLink?.inviteLink
      ? [{ channel: user?.plan === 'live' ? 'live' : 'prematch', inviteLink: checkoutTelegramLink.inviteLink }]
      : [];

  const handleCheckoutTelegramLink = async () => {
    setIsStartingTelegramLink(true);
    try {
      const response = await authAPI.startTelegramLink();
      const telegramLink = response.data.telegramLink as TelegramLinkState;
      setCheckoutTelegramLink(telegramLink);
      if (telegramLink?.botStartUrl) {
        window.location.assign(telegramLink.botStartUrl);
      }
    } catch (error) {
      setCheckoutError(getApiErrorDetail(error, 'Telegram koppelen kon niet worden gestart. Probeer het via Mijn account.'));
    } finally {
      setIsStartingTelegramLink(false);
    }
  };

  if (!user) {
    return (
      <PublicLayout>
        <section className="public-page-card subscription-page">
          <section className="subscription-hero">
            <h1>Niet ingelogd</h1>
            <Link to="/login" className="card-btn card-link">Inloggen</Link>
          </section>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="public-page-card checkout-success-page">
        <section className={`checkout-success-hero${checkoutError ? ' checkout-success-hero--warning' : ''}`}>
          <div className="checkout-success-icon" aria-hidden="true">
            {isRefreshingCheckout ? (
              <span className="site-loading-spinner" />
            ) : checkoutError ? (
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 8v5M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M10.3 3.8 2.1 18a2 2 0 0 0 1.7 3h16.4a2 2 0 0 0 1.7-3L13.7 3.8a2 2 0 0 0-3.4 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none">
                <path d="m6.7 12.4 3.4 3.4 7.4-8" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>

          <div className="checkout-success-hero-copy">
          {isRefreshingCheckout ? (
            <>
              <span className="checkout-success-eyebrow">Even geduld</span>
              <h1>Betaling wordt verwerkt</h1>
              <p role="status" aria-live="polite">We controleren je Stripe-betaling en activeren daarna automatisch je abonnement.</p>
            </>
          ) : checkoutError ? (
            <>
              <span className="checkout-success-eyebrow">Controle loopt nog</span>
              <h1>Betaling nog niet bevestigd</h1>
              <p>{checkoutError}</p>
            </>
          ) : deferredSubscription ? (
            <>
              <span className="checkout-success-eyebrow">Nieuw abonnement ingepland</span>
              <h1>Je {deferredCycleLabel} staat klaar</h1>
              <p>Je dagpas blijft ongewijzigd actief. Het nieuwe abonnement start op {deferredStartLabel}; dan volgt ook pas de eerste afschrijving.</p>
            </>
          ) : isPaid ? (
            <>
              <span className="checkout-success-eyebrow">Betaling geslaagd</span>
              <h1>Welkom bij PerfectOddsNL</h1>
              <p>Je betaling is verwerkt. Alle onderdelen van je abonnement staan nu voor je klaar.</p>
            </>
          ) : (
            <>
              <span className="checkout-success-eyebrow">Aanmelding voltooid</span>
              <h1>Gratis kanaal geopend</h1>
              <p>Je hebt het gratis Telegramkanaal gekozen. Betaalde functies zoals de Surebet Tracker zijn alleen beschikbaar met een betaald abonnement.</p>
            </>
          )}
          </div>

          {!isRefreshingCheckout && !checkoutError && (
            <div className="checkout-success-status">
              <span className="checkout-success-status-dot" aria-hidden="true" />
              <span>
                <small>Status</small>
                <strong>{deferredSubscription ? 'Abonnement ingepland' : isPaid ? 'Abonnement actief' : 'Gratis toegang actief'}</strong>
              </span>
            </div>
          )}
        </section>

        {isPaid && (
          <>
            <section className="checkout-success-grid">
              <article className="checkout-success-card card">
                <header className="checkout-success-card-heading">
                  <span className="checkout-success-card-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M4 7.5h16M6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11A2.5 2.5 0 0 1 6.5 4Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      <path d="M8 12h4M8 16h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                  </span>
                  <span>
                    <small>Overzicht</small>
                    <h2>Je abonnement</h2>
                  </span>
                </header>

                <dl className="checkout-success-details">
                  <div>
                    <dt>Pakket</dt>
                    <dd>{getPlanLabel(user.plan as PlanType, user.billingCycle)}</dd>
                  </div>
                  <div>
                    <dt>Facturatie</dt>
                    <dd>{billingCycleLabel}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd><span className="checkout-success-active-badge">Actief</span></dd>
                  </div>
                </dl>

                <p className="checkout-success-manage-note">
                  {deferredSubscription
                    ? `Je huidige dagpas blijft actief tot ${deferredStartLabel}. Daarna start automatisch je ${deferredCycleLabel}.`
                    : 'Je kunt je abonnement en betalingen altijd beheren via Mijn account.'}
                </p>
              </article>

              <article className="checkout-success-card checkout-success-access-card card">
                <header className="checkout-success-card-heading">
                  <span className="checkout-success-card-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" stroke="currentColor" strokeWidth="1.8" />
                      <path d="m8.2 12.3 2.4 2.4 5.2-5.4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span>
                    <small>Direct beschikbaar</small>
                    <h2>Je toegang</h2>
                  </span>
                </header>

                <div className="checkout-success-access-list">
                  <div className="checkout-success-access-item">
                    <span className="checkout-success-check" aria-hidden="true">✓</span>
                    <span><strong>Telegram meldingen</strong><small>Toegang geactiveerd</small></span>
                  </div>
                  <div className="checkout-success-access-item">
                    <span className="checkout-success-check" aria-hidden="true">✓</span>
                    <span><strong>Persoonlijke kanaaltoegang</strong><small>Via Telegrambot</small></span>
                  </div>
                  <div className="checkout-success-access-item">
                    <span className="checkout-success-check" aria-hidden="true">✓</span>
                    <span><strong>Surebet Tracker</strong><small>Klaar voor gebruik</small></span>
                  </div>
                </div>
              </article>
            </section>

            <section className="checkout-success-telegram">
              <div className="checkout-success-telegram-copy">
                <span className="checkout-success-card-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="m21 4-7.2 16-3.7-6.2L4 10.1 21 4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                    <path d="m10.1 13.8 4.1-3.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </span>
                <div>
                  <span className="checkout-success-eyebrow">Telegramtoegang</span>
                  <h2>{checkoutTelegramLink?.linked ? 'Telegram is gekoppeld' : 'Koppel eerst je Telegram-account'}</h2>
                  <p>
                    {checkoutTelegramLink?.linked
                      ? 'Je Telegram-account is gekoppeld. Je toegang blijft automatisch gelijklopen met je abonnement.'
                      : 'De bot koppelt je Telegram-ID veilig aan je account. Daarna stuurt hij persoonlijke eenmalige links waarmee jouw gekoppelde account direct lid wordt.'}
                  </p>
                </div>
              </div>

              {!checkoutTelegramLink?.linked && (
                <div className="checkout-success-telegram-links">
                  <button
                    type="button"
                    className="card-btn card-link"
                    onClick={() => { void handleCheckoutTelegramLink(); }}
                    disabled={isStartingTelegramLink}
                  >
                    {isStartingTelegramLink ? 'Telegram openen...' : 'Koppel via @PerfectOddsNLBot'} <span aria-hidden="true">↗</span>
                  </button>
                </div>
              )}

              {checkoutTelegramLink?.linked && checkoutTelegramInvites.length > 0 && (
                <div className="checkout-success-telegram-links">
                  {checkoutTelegramInvites.map((invite) => (
                    <a
                      key={`${invite.channel}-${invite.inviteLink}`}
                      href={invite.inviteLink}
                      target="_blank"
                      rel="noreferrer"
                      className="card-btn card-link"
                    >
                      Open {invite.channel === 'live' ? 'Live' : 'Pre-match'} Telegram <span aria-hidden="true">↗</span>
                    </a>
                  ))}
                </div>
              )}

              <div className="checkout-success-telegram-help">
                <span>Lukt automatisch toevoegen niet?</span>
                {TELEGRAM_SUPPORT_URL ? (
                  <a href={TELEGRAM_SUPPORT_URL} target="_blank" rel="noreferrer">Stuur mij een bericht op Telegram</a>
                ) : (
                  <Link to="/contact">Neem contact op, dan helpen we je direct</Link>
                )}
              </div>
            </section>

            <section className="checkout-success-next-step">
              <div>
                <span className="checkout-success-eyebrow">Alles staat klaar</span>
                <h2>Ga direct aan de slag</h2>
                <p>Open je tracker of bekijk je abonnementsgegevens en toegang via Mijn account.</p>
              </div>
              <div className="checkout-success-actions">
                <Link to="/tracker" className="card-btn card-link">Open mijn tracker <span aria-hidden="true">→</span></Link>
                <Link to="/mijn-account" className="card-btn card-link checkout-success-secondary-btn">Mijn account</Link>
              </div>
            </section>
          </>
        )}

        {!isPaid && !isRefreshingCheckout && (
          <section className="checkout-success-actions checkout-success-actions--standalone">
            <Link to="/mijn-account" className="card-btn card-link">Mijn account</Link>
            <Link to="/pricing" className="card-btn card-link checkout-success-secondary-btn">Bekijk abonnementen</Link>
          </section>
        )}
      </section>
    </PublicLayout>
  );
};

export const HoeHetWerktPage: React.FC = () => (
  <PublicPage
    title="Hoe het werkt"
    description="Hier leggen we uit hoe surebet alerts werken. Bij pre-match bewegen odds meestal minder snel dan live, maar ook deze kansen zijn tijdgevoelig. Controleer altijd of de odds, markt en lijn nog kloppen voordat je inzet."
  />
);

export const WatZijnSurebetsPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page public-refresh surebets-refresh">
      <section className="subscription-hero">
        <span className="subscription-badge">Surebet gids</span>
        <h1>Wat zijn surebets?</h1>
        <p>
          Een surebet is een situatie waarbij je op alle mogelijke uitkomsten van een weddenschap inzet, zodat je
          vooraf al kunt berekenen dat je winst maakt als beide bets tegen de juiste odds worden geaccepteerd.
        </p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/kennisbank/risicos-surebetten" className="card-btn card-link public-cta-secondary">Bekijk risico's</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>De simpele uitleg</h2>
        <p>
          Bookmakers geven niet altijd dezelfde odds. Soms staat dezelfde markt bij de ene bookmaker hoger geprijsd
          dan bij de andere bookmaker. Als het verschil groot genoeg is, kun je beide kanten spelen en toch winst
          overhouden.
        </p>
        <ul className="subscription-list">
          <li>Bij bookmaker A speel je de ene kant.</li>
          <li>Bij bookmaker B speel je de andere kant.</li>
          <li>Je verdeelt je inzet zo dat beide uitkomsten winst kunnen geven.</li>
          <li>Als beide bets worden geaccepteerd tegen de juiste odds, is je winst vooraf berekend.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Een eenvoudig voorbeeld</h2>
        <div className="surebet-example-card">
          <div className="surebet-example-odds">
            <article>
              <span>Bookmaker A</span>
              <strong>Over 2.5 goals</strong>
              <b>2.25</b>
            </article>
            <article>
              <span>Bookmaker B</span>
              <strong>Under 2.5 goals</strong>
              <b>2.20</b>
            </article>
          </div>
          <div className="surebet-example-result-grid">
            <div><span>Inzet A</span><strong>€50</strong></div>
            <div><span>Inzet B</span><strong>€50</strong></div>
            <div><span>Meer dan 2,5 goals</span><strong>€112,50 terug</strong><small>€12,50 winst</small></div>
            <div><span>Minder dan 2,5 goals</span><strong>€110 terug</strong><small>€10 winst</small></div>
          </div>
        </div>
        <p className="surebet-example-explanation">
          Stel je plaatst nu op beide €50. Dan krijg je met welk resultaat dan ook altijd meer geld terug dan de
          totale inzet. Bij meer dan 2,5 goals krijg je €112,50 terug (€12,50 winst) en bij minder dan 2,5 goals krijg
          je €110 terug (€10 winst).
        </p>
        <p>
          Dat is het idee van een surebet: niet gokken op wie wint, maar gebruikmaken van oddsverschillen tussen
          bookmakers.
        </p>
      </section>

      <section className="subscription-section">
        <h2>Waarom ontstaan surebets?</h2>
        <p>
          Surebets ontstaan omdat bookmakers hun odds niet altijd tegelijk aanpassen. De ene bookmaker kan sneller
          reageren dan de andere. Soms verschillen bookmakers ook van mening over de kans op een bepaalde uitkomst.
        </p>
        <ul className="subscription-list">
          <li>Odds veranderen continu.</li>
          <li>Bookmakers gebruiken verschillende systemen.</li>
          <li>De ene bookmaker past odds sneller aan dan de andere.</li>
          <li>Bij live wedstrijden bewegen odds vaak extra snel.</li>
          <li>Ook pre-match odds kunnen snel veranderen.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Wat doet PerfectOddsNL?</h2>
        <p>
          PerfectOddsNL zoekt naar oddsverschillen tussen Bet365 en Kambi-bookmakers. Wanneer er een mogelijke
          surebet wordt gevonden, sturen we een melding naar Telegram. Jij controleert daarna zelf of de odds nog
          kloppen en beslist zelf of je de bets plaatst.
        </p>
        <ul className="subscription-list">
          <li>PerfectOddsNL plaatst geen bets voor jou.</li>
          <li>Je ontvangt alleen informatieve meldingen.</li>
          <li>Je controleert zelf de odds, markt en lijn.</li>
          <li>Je vult je geplaatste bets later in de Surebet Tracker in.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Waarom snelheid belangrijk is</h2>
        <p>
          Surebets kunnen snel verdwijnen. Bij live betting kan dat binnen enkele seconden gebeuren. Bij pre-match
          betting bewegen odds meestal minder snel dan live, maar ook daar kunnen odds veranderen voordat je inzet is
          geplaatst.
        </p>
      </section>

      <section className="subscription-section subscription-cta-block">
        <h2>Wil je surebets ontvangen via Telegram?</h2>
        <p>Bekijk de abonnementen en kies welke vorm bij jou past: gratis kanaal, pre-match, live of Pro.</p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/kennisbank/risicos-surebetten" className="card-btn card-link public-cta-secondary">Lees de risico's</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Belangrijk om te weten</h2>
        <p>
          Een surebet betekent niet dat er nooit iets mis kan gaan. Odds kunnen veranderen, bookmakers kunnen inzetten
          weigeren en gebruikers kunnen fouten maken bij het plaatsen van bets. Daarom blijft controle altijd
          belangrijk.
        </p>
        <p>
          <DisclaimerText />
        </p>
      </section>
    </section>
  </PublicLayout>
);

export const PersonalTrackerPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page tracker-page public-refresh tracker-info-refresh">
      <section className="subscription-hero tracker-hero">
        <span className="subscription-badge">Persoonlijke tracker</span>
        <h1>Surebet Tracker</h1>
        <p>
          Met de Surebet Tracker houd je als abonnee bij welke surebets je hebt geplaatst, welke odds je echt kreeg,
          hoeveel je hebt ingezet en wat je resultaat is.
        </p>
      </section>

      <section className="subscription-section">
        <h2>Waarom een tracker?</h2>
        <p>
          Surebetting draait niet alleen om meldingen ontvangen. Je moet ook weten wat je echt hebt geplaatst en wat
          het oplevert.
        </p>
      </section>

      <section className="subscription-section">
        <h2>Wat kun je bijhouden?</h2>
        <ul className="subscription-list">
          <li>Geplaatste surebets</li>
          <li>Eigen odds</li>
          <li>Eigen inzetten</li>
          <li>Winst en ROI</li>
          <li>Openstaande bets</li>
          <li>Betalingen en kosten</li>
          <li>Netto resultaat</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Hoe werkt het?</h2>
        <ul className="subscription-list">
          <li>Kies de datum van de Telegram melding</li>
          <li>Klik de surebet aan</li>
          <li>Vul je echte inzet in</li>
          <li>Pas odds aan als ze veranderd waren</li>
          <li>Sla op en bekijk je resultaat</li>
        </ul>
      </section>

      <section className="subscription-section subscription-cta-block">
        <h2>Alle betaalde abonnementen</h2>
        <p>De Surebet Tracker zit in elk betaald PerfectOddsNL abonnement.</p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/dashboard" className="card-btn card-link public-cta-secondary">Log in</Link>
        </div>
      </section>
    </section>
  </PublicLayout>
);

export const VoorbeeldmeldingPage: React.FC = () => (
  <PublicPage
    title="Voorbeeldmelding"
    description="Hier tonen we een voorbeeld van een Telegram surebetmelding voor pre-match en live kansen. Controleer altijd zelf of de odds, markt en lijn nog kloppen voordat je inzet."
  />
);

interface BookmakersInfoCard {
  title: string;
  text: string;
}

interface SportsbookLogoCard {
  name: string;
  logoSrc?: string;
}

const bookmakersComparePoints = [
  'Odds veranderen continu.',
  'Bookmakers passen odds niet altijd tegelijk aan.',
  'Bet365 en Kambi kunnen dezelfde uitkomst anders prijzen.',
];

const bet365Cards: BookmakersInfoCard[] = [
  {
    title: 'Eigen oddsstructuur',
    text: 'Bet365 gebruikt een eigen manier om markten, selecties en odds aan te bieden.',
  },
  {
    title: 'Live en pre-match',
    text: 'Bet365 is interessant voor zowel pre-match als live surebetkansen.',
  },
  {
    title: 'Belangrijk voor props',
    text: 'Voor sommige sporten kunnen team- en spelergerelateerde markten interessant zijn.',
  },
  {
    title: 'Vergelijking met Kambi',
    text: 'Juist doordat Bet365 anders kan prijzen dan Kambi, kunnen er interessante oddsverschillen ontstaan.',
  },
];

const kambiCards: BookmakersInfoCard[] = [
  {
    title: 'Een platform, meerdere bookmakers',
    text: 'Meerdere bookmakers gebruiken Kambi als basis voor hun sportsbook.',
  },
  {
    title: 'Goede vergelijking met Bet365',
    text: 'Omdat Bet365 en Kambi anders kunnen prijzen, zijn verschillen tussen deze bronnen interessant.',
  },
  {
    title: 'Live en pre-match mogelijkheden',
    text: 'Kambi-markten kunnen zowel voor de wedstrijd als tijdens wedstrijden gebruikt worden.',
  },
  {
    title: 'Herkenbare marktstructuur',
    text: 'Kambi-markten zijn vaak goed te verwerken, waardoor vergelijking met andere bronnen mogelijk wordt.',
  },
];

const kambiBookmakers = [
  { name: 'Unibet', logoSrc: '/assets/bookmakers/unibet.png' },
  { name: 'BetCity', logoSrc: '/assets/bookmakers/betcity.png' },
  { name: 'BetMGM', logoSrc: '/assets/bookmakers/betmgm.png' },
  { name: 'Jacks', logoSrc: '/assets/bookmakers/Jacks.png' },
  { name: 'LeoVegas', logoSrc: '/assets/bookmakers/Leovegas.png' },
  { name: '711', logoSrc: '/assets/bookmakers/711.png' },
  { name: 'Bingoal', logoSrc: '/assets/bookmakers/bingoal.png' },
  { name: 'Oranje Palace', logoSrc: '/assets/bookmakers/Oranje palace.png' },
  { name: 'Hommerson', logoSrc: '/assets/bookmakers/hommersonNL.png' },
  { name: 'Betnation', logoSrc: '/assets/bookmakers/betnation.png' },
] satisfies SportsbookLogoCard[];

const footballMarketCards: BookmakersInfoCard[] = [
  { title: '1X2', text: 'Winst thuisteam, gelijkspel of winst uitteam.' },
  { title: 'Over/Under goals', text: 'Bijvoorbeeld Over 2.5 goals of Under 2.5 goals.' },
  { title: 'Both Teams To Score', text: 'Of beide teams wel of niet scoren.' },
  { title: 'Asian handicap', text: 'Handicapmarkten waarbij een team een virtuele voorsprong of achterstand krijgt.' },
  { title: 'Player shots', text: 'Aantal schoten van een speler.' },
  { title: 'Player shots on target', text: 'Aantal schoten op doel van een speler.' },
  { title: 'Team shots', text: 'Aantal schoten van een team.' },
  { title: 'Team shots on target', text: 'Aantal schoten op doel van een team.' },
];

const liveModeCards = [
  {
    title: 'Pre-match',
    text: 'Pre-match odds bewegen meestal minder snel dan live odds, maar ook hier blijft snel handelen belangrijk. Odds kunnen veranderen voordat je beide kanten hebt geplaatst.',
    bullets: [
      'Voor de wedstrijd',
      'Vaak iets beter te controleren dan live',
      'Nog steeds tijdgevoelig',
      'Geschikt voor gebruikers die voor wedstrijden willen handelen',
    ],
  },
  {
    title: 'Live',
    text: 'Live odds bewegen sneller. Daardoor kan de ROI soms hoger zijn, maar kansen kunnen ook sneller verdwijnen.',
    bullets: [
      'Tijdens de wedstrijd',
      'Sneller handelen nodig',
      'Odds kunnen binnen seconden veranderen',
      'Geschikt voor gebruikers die beide bookmakers direct open hebben staan',
    ],
  },
];

const BookmakersCardGrid: React.FC<{ items: BookmakersInfoCard[]; compact?: boolean }> = ({ items, compact = false }) => (
  <div className={`subscription-grid ${compact ? 'subscription-grid-2' : 'subscription-grid-4'} bookmakers-card-grid`}>
    {items.map((item) => (
      <article key={item.title} className="card subscription-feature-card bookmakers-info-card">
        <h3>{item.title}</h3>
        <p>{item.text}</p>
      </article>
    ))}
  </div>
);

export const BookmakersPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page bookmakers-page public-refresh bookmakers-refresh">
      <section className="subscription-hero bookmakers-hero">
        <span className="subscription-badge">Bet365 + Kambi</span>
        <h1>Bookmakers</h1>
        <p>
          PerfectOddsNL richt zich op oddsverschillen tussen Bet365 en Kambi-bookmakers. Door deze bronnen met elkaar
          te vergelijken kunnen mogelijke surebetkansen sneller worden herkend.
        </p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/markten" className="card-btn card-link public-cta-secondary">Bekijk markten</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Bet365</h2>
        <p>
          Bet365 is een belangrijke odds-bron binnen PerfectOddsNL. De oddsstructuur van Bet365 is anders dan die van
          Kambi, waardoor markten, selecties en lijnen goed herkend en gekoppeld moeten worden.
        </p>
        <BookmakersCardGrid items={bet365Cards} />
      </section>

      <section className="subscription-section">
        <h2>Kambi</h2>
        <p>
          Kambi is een sportsbook-platform dat door meerdere Nederlandse bookmakers wordt gebruikt. PerfectOddsNL
          gebruikt Kambi als belangrijke bron om odds te vergelijken met Bet365.
        </p>
        <BookmakersCardGrid items={kambiCards} />
      </section>

      <section className="subscription-section">
        <h2>Kambi-bookmakers</h2>
        <div className="subscription-grid subscription-grid-3 bookmakers-sportsbook-grid">
          {kambiBookmakers.map((bookmaker) => (
            <article key={bookmaker.name} className="card subscription-feature-card bookmakers-info-card bookmakers-logo-card">
              {bookmaker.logoSrc ? (
                <img className="bookmakers-logo-image" src={bookmaker.logoSrc} alt={`${bookmaker.name} logo`} loading="lazy" />
              ) : (
                <div className="bookmakers-logo-fallback" aria-hidden="true">
                  <span>{bookmaker.name}</span>
                </div>
              )}
              <h3>{bookmaker.name}</h3>
            </article>
          ))}
        </div>
      </section>

      <section className="subscription-section">
        <h2>Waarom bookmakers vergelijken?</h2>
        <p>
          Bookmakers geven niet altijd dezelfde odds. Soms staat dezelfde uitkomst bij de ene bookmaker hoger dan bij
          de andere bookmaker. Als het verschil groot genoeg is, kan er een surebet ontstaan.
        </p>
        <ul className="subscription-list bookmakers-list-spaced">
          {bookmakersComparePoints.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="subscription-section subscription-cta-block bookmakers-cta-block">
        <h2>Wil je meldingen ontvangen?</h2>
        <p>Kies een abonnement dat past bij hoe jij wilt werken: gratis kanaal, pre-match, live of Pro.</p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/markten" className="card-btn card-link public-cta-secondary">Bekijk markten</Link>
        </div>
      </section>

      <section className="subscription-disclaimer-card card">
        <h3>Belangrijk om te weten</h3>
        <p>
          <DisclaimerText />
        </p>
      </section>
    </section>
  </PublicLayout>
);

export const MarktenPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page bookmakers-page markten-page public-refresh markten-refresh">
      <section className="subscription-hero bookmakers-hero">
        <span className="subscription-badge">Voetbal</span>
        <h1>Markten</h1>
        <p>
          PerfectOddsNL vergelijkt markten tussen Bet365 en Kambi-bookmakers. Belangrijk is dat markt, lijn en
          selectie echt overeenkomen voordat een surebet betrouwbaar kan worden berekend.
        </p>
        <div className="subscription-cta-row">
          <Link to="/bookmakers" className="card-btn card-link">Bekijk bookmakers</Link>
          <Link to="/calculator" className="card-btn card-link public-cta-secondary">Open calculator</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Voetbalmarkten</h2>
        <p>
          Voetbal is een belangrijke sport voor PerfectOddsNL. Vooral markten waar odds snel bewegen of waar
          bookmakers verschillend prijzen, kunnen interessant zijn.
        </p>
        <BookmakersCardGrid items={footballMarketCards} compact />
      </section>

      <section className="subscription-section">
        <h2>Waarom markten goed moeten kloppen</h2>
        <p>
          Een surebet werkt alleen als beide kanten over dezelfde markt gaan. De wedstrijd, selectie, lijn en richting
          moeten overeenkomen. Als een onderdeel niet klopt, klopt de berekening ook niet.
        </p>
        <ul className="subscription-list bookmakers-list-spaced">
          <li>De wedstrijd moet hetzelfde zijn.</li>
          <li>De markt moet hetzelfde zijn.</li>
          <li>De lijn moet hetzelfde zijn.</li>
          <li>Over en Under moeten tegenover elkaar staan.</li>
          <li>De selectie moet goed gekoppeld zijn.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Live en pre-match markten</h2>
        <p>
          PerfectOddsNL maakt onderscheid tussen pre-match en live. Pre-match kansen ontstaan voor de wedstrijd. Live
          kansen ontstaan tijdens de wedstrijd.
        </p>
        <div className="subscription-grid subscription-grid-2 bookmakers-mode-grid">
          {liveModeCards.map((item) => (
            <article key={item.title} className="card subscription-feature-card bookmakers-mode-card">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
              <ul className="subscription-list">
                {item.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="subscription-section subscription-cta-block bookmakers-cta-block">
        <h2>Wil je markten zelf testen?</h2>
        <p>Gebruik de calculator om te controleren of twee of drie odds samen een surebet kunnen vormen.</p>
        <div className="subscription-cta-row">
          <Link to="/calculator" className="card-btn card-link">Open calculator</Link>
          <Link to="/pricing" className="card-btn card-link public-cta-secondary">Bekijk abonnementen</Link>
        </div>
      </section>

      <section className="subscription-disclaimer-card card">
        <h3>Belangrijk om te weten</h3>
        <p>
          <DisclaimerText />
        </p>
      </section>
    </section>
  </PublicLayout>
);

export const KennisbankPage: React.FC = () => (
  <PublicPage
    title="Kennisbank"
    description="Hier bouwen we een kennisbank met uitleg, tips en achtergrondinformatie over surebets en het gebruik van het platform, inclusief waarom pre-match kansen ook tijdgevoelig blijven."
  />
);

interface FAQAction {
  label: string;
  to: string;
  external?: boolean;
}

interface FAQItem {
  question: string;
  answer: string;
  actions?: FAQAction[];
}

interface FAQSection {
  title: string;
  intro: string;
  items: FAQItem[];
}

const faqSections: FAQSection[] = [
  {
    title: 'Abonnementen en toegang',
    intro: 'Alles over gratis toegang, betaalde pakketten en wat er gebeurt nadat je een abonnement kiest.',
    items: [
      {
        question: 'Wat krijg ik in het gratis kanaal?',
        answer: 'Het gratis kanaal is bedoeld om kennis te maken met PerfectOddsNL. Je krijgt beperkt toegang tot surebetmeldingen, zodat je kunt zien hoe de signalen eruitzien voordat je een betaald pakket kiest.',
        actions: [{ label: 'Gratis meedoen', to: FREE_TELEGRAM_JOIN_URL, external: true }],
      },
      {
        question: 'Wat is het verschil tussen Pro, pre-match en live?',
        answer: 'Pro combineert pre-match- en livemeldingen in één pakket. Pre-match richt zich op kansen vóór wedstrijden beginnen. Live is sneller en actiever, omdat odds tijdens wedstrijden veel sneller bewegen.',
        actions: [{ label: 'Bekijk prijzen', to: '/pricing' }],
      },
      {
        question: 'Krijg ik toegang tot de Surebet Tracker?',
        answer: 'De Surebet Tracker is onderdeel van de betaalde ervaring. Daarmee houd je geplaatste bets, resultaat, kosten en rendement overzichtelijk bij in je eigen account.',
        actions: [{ label: 'Bekijk tracker uitleg', to: '/tracker-uitleg' }],
      },
      {
        question: 'Waar beheer ik mijn account?',
        answer: 'Na inloggen kun je via Mijn account je profiel, abonnement en beschikbare functies bekijken. Klopt je toegang niet, neem dan contact op met onze klantenservice.',
        actions: [{ label: 'Mijn account', to: '/mijn-account' }],
      },
    ],
  },
  {
    title: 'Telegrammeldingen',
    intro: 'Hoe meldingen werken, waarom snelheid belangrijk is en waar je zelf op moet letten.',
    items: [
      {
        question: 'Plaatsen jullie weddenschappen voor mij?',
        answer: 'Nee. PerfectOddsNL geeft informatieve odds-signalen. Je controleert zelf de wedstrijd, markt, odds en inzet voordat je eventueel een bet plaatst.',
      },
      {
        question: 'Waarom verdwijnen sommige kansen snel?',
        answer: 'Bookmakers passen odds continu aan. Vooral live kunnen odds binnen seconden veranderen of markten tijdelijk sluiten. Controleer daarom altijd opnieuw voordat je inzet.',
      },
      {
        question: 'Welke bookmakers worden gebruikt?',
        answer: 'PerfectOddsNL focust op verschillen tussen Bet365 en Kambi-bookmakers. Denk bij Kambi aan aanbieders zoals Unibet, BetCity, Betnation, Bingoal, Jacks en vergelijkbare partijen.',
        actions: [{ label: 'Bekijk bookmakers', to: '/bookmakers' }],
      },
      {
        question: 'Wat betekent ROI in een melding?',
        answer: 'ROI laat zien hoeveel theoretische marge er in de odds zit op het moment van signaleren. Het is geen winstgarantie, omdat odds kunnen wijzigen en inzetten kunnen worden geweigerd.',
      },
    ],
  },
  {
    title: 'Live en pre-match surebets',
    intro: 'De belangrijkste verschillen tussen rustig voorbereiden en snel reageren tijdens wedstrijden.',
    items: [
      {
        question: 'Wat is een pre-match surebet?',
        answer: 'Een pre-match surebet is een kans die ontstaat voordat een wedstrijd begint. Je hebt meestal iets meer tijd dan bij live, maar ook pre-match odds kunnen snel wijzigen.',
        actions: [{ label: 'Surebet gids', to: '/surebets' }],
      },
      {
        question: 'Wat is een live surebet?',
        answer: 'Een live surebet ontstaat tijdens een wedstrijd. De snelheid ligt hoger, markten bewegen sneller en je moet extra goed controleren of beide kanten nog openstaan.',
      },
      {
        question: 'Kan ik winst garanderen met surebets?',
        answer: 'Nee. Een berekening kan theoretisch positief zijn, maar in de praktijk blijven er risico’s zoals veranderende odds, gesloten markten, limieten of verkeerd gekozen marktregels.',
        actions: [{ label: 'Lees de risico’s', to: '/kennisbank/risicos-surebetten' }],
      },
      {
        question: 'Is surebetten legaal?',
        answer: "Ja, surebetten is legaal. Je gebruikt oddsverschillen tussen bookmakers en plaatst zelf je weddenschappen. Bookmakers en casino's hebben liever niet dat je dit doet en kunnen daarom accounts limiteren of inzetten weigeren.",
      },
      {
        question: 'Hoe controleer ik zelf een kans?',
        answer: 'Controleer wedstrijd, markt, lijn, selectie en odds bij beide bookmakers. Gebruik daarna eventueel de calculator om te zien of de odds samen nog steeds positief zijn.',
        actions: [{ label: 'Open calculator', to: '/calculator' }],
      },
    ],
  },
  {
    title: 'Tracker en resultaat',
    intro: 'Vragen over het bijhouden van bets, kosten en je echte rendement.',
    items: [
      {
        question: 'Waarom zou ik mijn bets bijhouden?',
        answer: 'Je echte resultaat hangt niet alleen af van gemelde ROI, maar ook van inzet, geaccepteerde bets, kosten, fouten en oddsveranderingen. De tracker maakt dat zichtbaar.',
        actions: [{ label: 'Naar tracker', to: '/tracker' }],
      },
      {
        question: 'Kan ik kosten zoals abonnementen toevoegen?',
        answer: 'Ja. In de tracker kun je kosten bijhouden, zodat je netto resultaat realistischer wordt. Dat helpt om niet alleen naar losse wins te kijken, maar naar je volledige maandresultaat.',
      },
      {
        question: 'Wat als mijn tracker toegang niet werkt?',
        answer: 'Controleer eerst of je met het juiste account bent ingelogd. Werkt het daarna nog niet, stuur ons dan het e-mailadres van je account en vermeld welk pakket je hebt.',
        actions: [{ label: 'Neem contact op', to: '/contact' }],
      },
    ],
  },
];

export const FAQPage: React.FC = () => {
  const [openItem, setOpenItem] = useState<string | null>('0-0');

  return (
    <PublicLayout>
      <section className="public-page-card faq-page public-refresh faq-refresh">
        <section className="subscription-hero faq-hero">
          <span className="subscription-badge">FAQ</span>
          <h1>Veelgestelde vragen</h1>
          <p>
            Antwoorden over abonnementen, Telegrammeldingen, live en pre-match surebets, de calculator en de Surebet Tracker.
          </p>
          <div className="subscription-cta-row faq-hero-actions">
            <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
            <Link to="/contact" className="card-btn card-link public-cta-secondary">Contact opnemen</Link>
          </div>
        </section>

        {faqSections.map((section, sectionIndex) => (
          <section className="subscription-section faq-section" key={section.title}>
            <div className="faq-section-heading">
              <h2>{section.title}</h2>
              <p>{section.intro}</p>
            </div>
            <div className="faq-accordion">
              {section.items.map((item, itemIndex) => {
                const itemId = `${sectionIndex}-${itemIndex}`;
                const isOpen = openItem === itemId;

                return (
                  <article className={`faq-item${isOpen ? ' is-open' : ''}`} key={item.question}>
                    <button
                      type="button"
                      className="faq-item-toggle"
                      aria-expanded={isOpen}
                      aria-controls={`faq-answer-${itemId}`}
                      onClick={() => setOpenItem(isOpen ? null : itemId)}
                    >
                      <span>{item.question}</span>
                      <span className="faq-item-icon" aria-hidden="true">{isOpen ? '-' : '+'}</span>
                    </button>
                    {isOpen && (
                      <div className="faq-item-answer" id={`faq-answer-${itemId}`}>
                        <p>{item.answer}</p>
                        {item.actions && (
                          <div className="faq-item-answer-actions">
                            {item.actions.map((action) => action.external ? (
                              <a key={action.to} href={action.to} className="card-btn card-link public-cta-secondary" target="_blank" rel="noreferrer">
                                {action.label}
                              </a>
                            ) : (
                              <Link key={action.to} to={action.to} className="card-btn card-link public-cta-secondary">
                                {action.label}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          </section>
        ))}

        <section className="subscription-section subscription-cta-block faq-cta-card card">
          <h2>Staat je vraag er niet tussen?</h2>
          <p>Stuur ons je vraag. Vermeld bij accountvragen het e-mailadres waarmee je inlogt.</p>
          <div className="subscription-cta-row">
            <Link to="/contact" className="card-btn card-link">Contact opnemen</Link>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="card-btn card-link public-cta-secondary">Mail ons</a>
          </div>
        </section>
      </section>
    </PublicLayout>
  );
};

export const ContactPage: React.FC = () => {
  const [form, setForm] = useState(CONTACT_FORM_INITIAL_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedRequest, setSubmittedRequest] = useState<{
    id: number;
    mailSent: boolean;
    mailDeliveryStatus: string;
  } | null>(null);

  const updateField = (field: keyof typeof form, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = 'Vul je naam in.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) nextErrors.email = 'Vul een geldig e-mailadres in.';
    if (form.message.trim().length < 20) nextErrors.message = 'Beschrijf je vraag in minimaal 20 tekens.';

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      const firstInvalidField = Object.keys(nextErrors)[0];
      document.getElementById(`contact-${firstInvalidField}`)?.focus();
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    try {
      const response = await supportApi.submitContactRequest(form);
      setSubmittedRequest({
        id: response.data.request.id,
        mailSent: response.data.mailSent,
        mailDeliveryStatus: response.data.request.mailDeliveryStatus,
      });
    } catch (error) {
      setSubmitError(getApiErrorDetail(error, `Je vraag kon niet worden verstuurd. Probeer het opnieuw of mail ons via ${SUPPORT_EMAIL}.`));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <section className="public-page-card contact-page public-refresh contact-refresh">
        <section className="subscription-hero contact-hero">
          <span className="subscription-badge">Contact</span>
          <h1>Waar kunnen we je mee helpen?</h1>
          <p>
            Stel je vraag over abonnementen, Telegramtoegang, de Surebet Tracker of een melding die je hebt ontvangen.
            We reageren zo concreet mogelijk, zodat je snel verder kunt.
          </p>
          <div className="subscription-cta-row contact-hero-actions">
            <a href={`mailto:${SUPPORT_EMAIL}`} className="card-btn card-link">Mail ons</a>
            <Link to="/veelgestelde-vragen" className="card-btn card-link public-cta-secondary">Bekijk FAQ</Link>
          </div>
        </section>

        <section className="subscription-section contact-form-section">
          <div className="contact-section-copy">
            <h2>Stuur een vraag</h2>
            <p>Gebruik het formulier en we nemen zo snel mogelijk contact met je op. Je ontvangt een referentienummer waarmee we je vraag gemakkelijk kunnen terugvinden.</p>
          </div>

          {submittedRequest ? (
            <div className="contact-success-card" role="status">
              <h3>Vraag ontvangen</h3>
              <p>
                {submittedRequest.mailSent
                  ? `Bedankt. Je vraag is ontvangen en doorgestuurd naar ${SUPPORT_EMAIL}.`
                  : 'Bedankt. Je vraag is ontvangen. We nemen zo snel mogelijk contact met je op.'}
              </p>
              <p>Referentienummer: #{submittedRequest.id}</p>
              <div className="subscription-cta-row contact-telegram-actions">
                <button
                  type="button"
                  className="card-btn card-link"
                  onClick={() => {
                    setForm(CONTACT_FORM_INITIAL_STATE);
                    setErrors({});
                    setSubmitError('');
                    setSubmittedRequest(null);
                  }}
                >
                  Nog een vraag sturen
                </button>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="card-btn card-link public-cta-secondary">Mail ons</a>
              </div>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              {submitError && <div className="contact-form-alert" role="alert">{submitError}</div>}
              <div className="contact-form-grid">
                <label className="contact-form-field" htmlFor="contact-name">
                  <span>Naam</span>
                  <input
                    id="contact-name"
                    value={form.name}
                    onChange={(event) => updateField('name', event.target.value)}
                    placeholder="Je naam"
                    autoComplete="name"
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? 'contact-name-error' : undefined}
                  />
                  {errors.name && <small id="contact-name-error" className="contact-form-error">{errors.name}</small>}
                </label>
                <label className="contact-form-field" htmlFor="contact-email">
                  <span>E-mailadres</span>
                  <input
                    id="contact-email"
                    type="email"
                    value={form.email}
                    onChange={(event) => updateField('email', event.target.value)}
                    placeholder="jij@example.nl"
                    inputMode="email"
                    autoComplete="email"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'contact-email-error' : undefined}
                  />
                  {errors.email && <small id="contact-email-error" className="contact-form-error">{errors.email}</small>}
                </label>
                <label className="contact-form-field" htmlFor="contact-topic">
                  <span>Onderwerp</span>
                  <select id="contact-topic" value={form.topic} onChange={(event) => updateField('topic', event.target.value)}>
                    <option value="support">Algemene vraag</option>
                    <option value="abonnement">Abonnement of betaling</option>
                    <option value="telegram">Telegramtoegang</option>
                    <option value="tracker">Surebet Tracker</option>
                    <option value="melding">Vraag over een melding</option>
                  </select>
                </label>
                <label className="contact-form-field" htmlFor="contact-plan">
                  <span>Pakket</span>
                  <select id="contact-plan" value={form.plan} onChange={(event) => updateField('plan', event.target.value)}>
                    <option value="nog-geen-abonnement">Nog geen abonnement</option>
                    <option value="gratis">Gratis kanaal</option>
                    <option value="pro">Pro</option>
                    <option value="pre-match">Pre-match</option>
                    <option value="live">Live</option>
                  </select>
                </label>
                <label className="contact-form-field contact-form-field-full" htmlFor="contact-message">
                  <span>Bericht</span>
                  <textarea
                    id="contact-message"
                    value={form.message}
                    onChange={(event) => updateField('message', event.target.value)}
                    rows={7}
                    placeholder="Beschrijf je vraag"
                    aria-invalid={Boolean(errors.message)}
                    aria-describedby={errors.message ? 'contact-message-error' : undefined}
                  />
                  {errors.message && <small id="contact-message-error" className="contact-form-error">{errors.message}</small>}
                </label>
              </div>
              <button type="submit" className="card-btn card-link contact-submit-button" disabled={isSubmitting} aria-busy={isSubmitting}>
                {isSubmitting ? 'Versturen...' : 'Vraag sturen'}
              </button>
            </form>
          )}
        </section>

        <section className="subscription-section contact-telegram-section">
          <div className="contact-section-copy">
            <h2>Snel zelf regelen</h2>
            <p>Veel vragen kun je direct oplossen via je account, de abonnementenpagina of de uitlegpagina's.</p>
          </div>
          <div className="subscription-grid subscription-grid-3 contact-quick-help-grid">
            <article className="contact-quick-card">
              <h3>Abonnement kiezen</h3>
              <p>Bekijk alle pakketten, prijzen en verschillen tussen Pro, pre-match en live.</p>
              <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
            </article>
            <article className="contact-quick-card">
              <h3>Account beheren</h3>
              <p>Bekijk je status, toegang en tracker vanuit je eigen accountomgeving.</p>
              <Link to="/mijn-account" className="card-btn card-link">Mijn account</Link>
            </article>
            <article className="contact-quick-card">
              <h3>Veelgestelde vragen</h3>
              <p>Lees antwoorden over meldingen, tracker, abonnementen en verantwoordelijk spelen.</p>
              <Link to="/veelgestelde-vragen" className="card-btn card-link">Open FAQ</Link>
            </article>
          </div>
        </section>

      </section>
    </PublicLayout>
  );
};

// Abonnementen category pages
export const AbonnementenPage: React.FC = () => (
  <PublicPage
    title="Wat krijg je?"
    description="Overzicht van wat inbegrepen is in uw PerfectOddsNL abonnement."
  />
);

const ImportantDisclaimerCard: React.FC = () => (
  <section className="subscription-disclaimer-card card">
    <h3>Belangrijk om te weten</h3>
    <p>
      <DisclaimerText />
    </p>
  </section>
);

export const GratisGroepPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page">
      <section className="subscription-hero">
        <span className="subscription-badge">Gratis toegang</span>
        <h1>Gratis kanaal</h1>
        <p>
          Volg PerfectOddsNL gratis mee en ontvang af en toe een beperkte selectie surebet meldingen in Telegram.
          Ideaal om kennis te maken met de service zonder direct een betaald abonnement te nemen.
        </p>
        <div className="subscription-cta-row gratis-hero-actions">
          <a href={FREE_TELEGRAM_JOIN_URL} className="card-btn card-link" target="_blank" rel="noreferrer">Gratis meedoen</a>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Wat krijg je?</h2>
        <div className="gratis-features-grid">
          <div className="gratis-features-col gratis-features-col--yes">
            <p className="gratis-features-col-label">Inbegrepen</p>
            <ul className="gratis-features-list">
              <li><span className="gratis-check">✓</span> Toegang tot het gratis Telegramkanaal</li>
              <li><span className="gratis-check">✓</span> Ongeveer 1 tot 3 surebet meldingen per week</li>
              <li><span className="gratis-check">✓</span> Beperkte selectie van kansen</li>
            </ul>
          </div>
          <div className="gratis-features-col gratis-features-col--no">
            <p className="gratis-features-col-label">Niet inbegrepen</p>
            <ul className="gratis-features-list">
              <li><span className="gratis-cross">✕</span> Geen garantie op dagelijkse meldingen</li>
              <li><span className="gratis-cross">✕</span> Geen Surebet Tracker</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Voor wie is het gratis kanaal?</h2>
        <p>
          Het gratis kanaal is bedoeld voor gebruikers die eerst willen zien hoe PerfectOddsNL werkt. Je krijgt een
          indruk van de meldingen, de opbouw van de signalen en het type surebets dat gedeeld kan worden. Voor actief
          gebruik is een betaald abonnement beter geschikt.
        </p>
        <ul className="subscription-list">
          <li>Je wilt PerfectOddsNL vrijblijvend proberen</li>
          <li>Je bent nog niet klaar om actief surebets te plaatsen</li>
          <li>Je wilt af en toe een voorbeeldmelding ontvangen</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Wat moet je niet verwachten?</h2>
        <p>
          Het gratis kanaal is geen volledige surebetservice. Meldingen zijn beperkt, kunnen onregelmatig komen en
          bevatten niet altijd de beste of snelste kansen. Voor structureel gebruik en toegang tot de Surebet Tracker
          is een betaald abonnement nodig.
        </p>
      </section>

      <section className="subscription-section subscription-cta-block">
        <h2>Wil je meer meldingen en de tracker gebruiken?</h2>
        <p>Bekijk de betaalde abonnementen als je actief met surebets aan de slag wilt.</p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk Pricing</Link>
          <a href={FREE_TELEGRAM_JOIN_URL} className="card-btn card-link public-cta-secondary" target="_blank" rel="noreferrer">Gratis meedoen</a>
        </div>
      </section>

      <ImportantDisclaimerCard />
    </section>
  </PublicLayout>
);

export const ProAbonnementPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page pro-page">
      <section className="subscription-hero pro-hero">
        <span className="subscription-badge">Zomeractie: automatisch €20 korting op je eerste maand</span>
        <span className="subscription-kicker">Meest compleet</span>
        <h1>Pro abonnement</h1>
        <p>
          Het Pro Abonnement is de complete PerfectOddsNL ervaring. Je ontvangt zowel pre-match als live surebet
          meldingen in twee besloten kanalen en gebruikt de Surebet Tracker om je resultaten
          professioneel bij te houden.
        </p>
        <PlanBillingSelector plan="pro" />
        <div className="subscription-cta-row">
          <Link to="/calculator" className="card-btn card-link public-cta-secondary">Open calculator</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Waarom Pro?</h2>
        <p>
          Met Pro hoef je niet te kiezen tussen pre-match en live. Je krijgt toegang tot beide soorten meldingen,
          waardoor je meer kansen ziet en flexibeler kunt werken. Combineer de snelle actie van live betting met pre
          match kansen die vaak iets beter te controleren zijn, maar nog steeds snel handelen vragen.
        </p>
        <div className="subscription-grid subscription-grid-4 premium-grid">
          <article className="card subscription-feature-card"><h3>Pre-match + live</h3><p>Ontvang meldingen voor wedstrijden en tijdens wedstrijden.</p></article>
          <article className="card subscription-feature-card"><h3>Hoogste waarde</h3><p>Pro combineert alle belangrijke onderdelen van PerfectOddsNL in een abonnement.</p></article>
          <article className="card subscription-feature-card"><h3>Surebet Tracker inbegrepen</h3><p>Houd je geplaatste surebets, odds, inzetten, winst, ROI en betalingen bij.</p></article>
          <article className="card subscription-feature-card"><h3>Automatische kanaaltoegang</h3><p>De bot houdt je toegang tot beide betaalde Telegramkanalen gelijk met je abonnement.</p></article>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Hoe ziet Pro er in de praktijk uit?</h2>
        <p>
          Met Pro kun je zowel pre-match als live meldingen ontvangen. Pre-match kansen ontstaan voor de wedstrijd en
          zijn vaak iets beter te controleren dan live kansen, terwijl live kansen sneller bewegen en sneller kunnen
          verdwijnen.
        </p>
        <ol className="subscription-flow">
          <li>Je ontvangt een pre-match of live melding in Telegram</li>
          <li>Je controleert de odds bij de bookmakers</li>
          <li>Je plaatst de inzetten zelf</li>
          <li>Je voegt de surebet toe aan je tracker</li>
          <li>Je ziet je resultaat, ROI en netto overzicht</li>
        </ol>
      </section>

      <section className="subscription-section">
        <h2>Waarom Pro de beste keuze is</h2>
        <p>
          Als je PerfectOddsNL actief wilt gebruiken, is Pro de meest complete keuze. Je krijgt het breedste aanbod
          aan meldingen, de meeste flexibiliteit en alle tools om je resultaten overzichtelijk bij te houden.
        </p>
      </section>

      <section className="subscription-section subscription-cta-block pro-cta">
        <h2>Klaar voor de complete PerfectOddsNL ervaring?</h2>
        <p>Kies Pro als je toegang wilt tot pre-match, live, community en de Surebet Tracker.</p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk Pricing</Link>
          <Link to="/voorbeeldmelding" className="card-btn card-link public-cta-secondary">Bekijk voorbeeldmelding</Link>
        </div>
      </section>

      <ImportantDisclaimerCard />
    </section>
  </PublicLayout>
);

export const PreMatchAbonnementPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page">
      <section className="subscription-hero">
        <span className="subscription-badge">Voorbereid en snel handelen</span>
        <h1>Pre-match abonnement</h1>
        <p>
          Het Pre-match Abonnement is gericht op surebet meldingen voor de wedstrijd. Je krijgt vaak iets meer
          controle dan bij live betting, omdat odds meestal minder snel bewegen, maar ook bij pre-match surebets
          blijft snel handelen belangrijk.
        </p>
        <PlanBillingSelector plan="pre-match" />
        <div className="subscription-cta-row">
          <Link to="/abonnementen/pro" className="card-btn card-link public-cta-secondary">Bekijk Pro</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Wat is pre-match betting?</h2>
        <p>
          Pre-match betting betekent dat je kansen bekijkt voordat een wedstrijd begint. In vergelijking met live
          betting bewegen odds meestal minder snel, waardoor je vaak iets meer tijd hebt om markt, lijn en odds te
          controleren. Toch kunnen ook pre-match odds snel veranderen, zeker wanneer meerdere gebruikers dezelfde kans
          zien of bookmakers hun prijzen aanpassen.
        </p>
      </section>

      <section className="subscription-section">
        <h2>Pre-match in de praktijk</h2>
        <ol className="subscription-flow">
          <li>Je ontvangt een melding voor de wedstrijd</li>
          <li>Je opent direct Bet365 en de Kambi-bookmaker</li>
          <li>Je controleert markt, lijn en odds</li>
          <li>Je plaatst beide inzetten als alles nog klopt</li>
          <li>Je voegt de surebet toe aan je Surebet Tracker</li>
        </ol>
      </section>

      <section className="subscription-section">
        <h2>Wanneer is Pro beter?</h2>
        <p>
          Wil je naast pre-match meldingen ook live surebet kansen ontvangen? Dan is het Pro Abonnement waarschijnlijk
          de beste keuze. Met Pro combineer je pre-match kansen met live kansen, waardoor je meer mogelijkheden krijgt
          en flexibeler kunt handelen.
        </p>
      </section>

      <section className="subscription-section subscription-cta-block">
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk Pricing</Link>
          <Link to="/abonnementen/pro" className="card-btn card-link public-cta-secondary">Bekijk Pro abonnement</Link>
        </div>
      </section>

      <ImportantDisclaimerCard />
    </section>
  </PublicLayout>
);

export const LiveAbonnementPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page">
      <section className="subscription-hero">
        <span className="subscription-badge">Voor snelle actie</span>
        <h1>Live abonnement</h1>
        <p>
          Het Live Abonnement is gericht op surebet meldingen tijdens wedstrijden. Dit abonnement past bij gebruikers
          die snel kunnen reageren en actief live odds willen volgen.
        </p>
        <PlanBillingSelector plan="live" />
        <div className="subscription-cta-row">
          <Link to="/abonnementen/pro" className="card-btn card-link public-cta-secondary">Bekijk Pro</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Wat is live betting?</h2>
        <p>
          Live betting betekent dat odds veranderen terwijl een wedstrijd bezig is. Daardoor kunnen kansen ontstaan,
          maar ze kunnen ook snel weer verdwijnen. Live surebets vragen meer snelheid, focus en controle dan pre
          match surebets.
        </p>
        <p>
          Live surebets kunnen vaak een hogere ROI hebben dan pre-match surebets, omdat odds tijdens wedstrijden
          sneller bewegen. Daar staat tegenover dat live kansen sneller kunnen verdwijnen en odds binnen seconden
          kunnen veranderen.
        </p>
      </section>

      <section className="subscription-section">
        <h2>Voordelen van het Live abonnement</h2>
        <ul className="subscription-list">
          <li>
            <strong>Je geld staat sneller weer op je bookmakersaccount.</strong> Omdat de wedstrijd al bezig is, is de
            uitslag vaak binnen 1 tot 2 uur bekend. Na de afwikkeling door de bookmaker heb je je inzet plus eventuele
            winst doorgaans snel weer terug.
          </li>
          <li>
            <strong>Minder lang wachten dan bij pre-match.</strong> Bij een pre-match surebet plaats je soms al dagen
            vóór de wedstrijd je inzet, waardoor dat geld langer vaststaat.
          </li>
          <li>
            <strong>Meer beweging in de odds.</strong> Tijdens wedstrijden veranderen odds sneller, waardoor er
            regelmatig nieuwe surebetkansen kunnen ontstaan.
          </li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Live in de praktijk</h2>
        <ol className="subscription-flow">
          <li>Je ontvangt een live melding tijdens een wedstrijd</li>
          <li>Je opent direct beide bookmakers</li>
          <li>Je controleert of beide odds nog staan</li>
          <li>Je plaatst de inzetten als alles klopt</li>
          <li>Je voegt de surebet toe aan je tracker</li>
        </ol>
      </section>

      <section className="subscription-section">
        <h2>Wanneer is Pro beter?</h2>
        <p>
          Wil je naast live meldingen ook pre-match surebet kansen ontvangen? Dan is het Pro Abonnement waarschijnlijk
          de beste keuze. Met Pro combineer je beide vormen, waardoor je meer mogelijkheden krijgt en flexibeler kunt
          handelen.
        </p>
      </section>

      <section className="subscription-section subscription-cta-block">
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk Pricing</Link>
          <Link to="/abonnementen/pro" className="card-btn card-link public-cta-secondary">Bekijk Pro abonnement</Link>
        </div>
      </section>

      <ImportantDisclaimerCard />
    </section>
  </PublicLayout>
);

export const TelegramPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card telegram-page telegram-refresh">
      <section className="telegram-refresh-hero">
        <div className="telegram-refresh-hero-copy">
          <span className="telegram-refresh-eyebrow">Veilige accountkoppeling</span>
          <h1>Telegram koppelen.<br />In een paar stappen geregeld.</h1>
          <p>
            Koppel één Telegram-account aan je PerfectOddsNL-account. De bot stuurt daarna automatisch de persoonlijke
            links voor de kanalen die bij jouw abonnement horen.
          </p>
          <Link to="/mijn-account?tab=telegram" className="card-btn card-link telegram-refresh-hero-button">
            Naar Mijn account <span aria-hidden="true">→</span>
          </Link>
        </div>
        <div className="telegram-refresh-hero-visual" aria-hidden="true">
          <div className="telegram-refresh-phone">
            <span className="telegram-refresh-phone-bar" />
            <div className="telegram-refresh-bot-avatar">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="m21 4-7.2 16-3.7-6.2L4 10.1 21 4Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                <path d="m10.1 13.8 4.1-3.7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </div>
            <strong>@PerfectOddsNLBot</strong>
            <small>Account veilig gekoppeld</small>
            <span className="telegram-refresh-phone-status">✓ Toegang gesynchroniseerd</span>
          </div>
        </div>
      </section>

      <section className="telegram-refresh-section" aria-labelledby="telegram-link-title">
        <div className="telegram-refresh-section-heading">
          <span className="telegram-refresh-eyebrow">Zo werkt het</span>
          <h2 id="telegram-link-title">Koppel je account aan Telegram</h2>
          <p>Je hoeft geen Telegramnaam of chat-ID in te vullen. De bot herkent je account automatisch via een tijdelijke, persoonlijke koppelcode.</p>
        </div>

        <div className="telegram-refresh-steps">
          <article className="telegram-refresh-step card">
            <span className="telegram-refresh-step-number">01</span>
            <div className="telegram-refresh-step-icon" aria-hidden="true">↗</div>
            <h3>Open de koppeling</h3>
            <p>Ga in Mijn account naar Telegram en klik op <strong>Koppel via Telegram bot</strong>.</p>
          </article>
          <article className="telegram-refresh-step card">
            <span className="telegram-refresh-step-number">02</span>
            <div className="telegram-refresh-step-icon" aria-hidden="true">▶</div>
            <h3>Druk op Start</h3>
            <p>Telegram opent <strong>@PerfectOddsNLBot</strong>. Druk daar op Start om jouw Telegram-ID veilig te koppelen.</p>
          </article>
          <article className="telegram-refresh-step card">
            <span className="telegram-refresh-step-number">03</span>
            <div className="telegram-refresh-step-icon" aria-hidden="true">✓</div>
            <h3>Open je kanaallinks</h3>
            <p>De bot stuurt persoonlijke eenmalige links. Open ze om direct lid te worden van jouw betaalde kanalen.</p>
          </article>
        </div>

        <div className="telegram-refresh-sync-note">
          <span className="telegram-refresh-sync-icon" aria-hidden="true">✓</span>
          <div>
            <strong>Toegang blijft automatisch gelijklopen</strong>
            <p>Bij wijzigen, verlopen of ontkoppelen controleert de bot opnieuw welke kanalen bij je abonnement horen. Via Mijn account kun je de toegang altijd opnieuw synchroniseren.</p>
          </div>
        </div>
      </section>

      <section className="telegram-refresh-section telegram-refresh-example" aria-labelledby="telegram-example-title">
        <div className="telegram-refresh-section-heading">
          <span className="telegram-refresh-eyebrow">Voorbeeldmelding</span>
          <h2 id="telegram-example-title">Zo komt een surebet binnen</h2>
        </div>
        <div className="telegram-refresh-message-shell">
          <div className="telegram-refresh-message-topbar">
            <span className="telegram-refresh-message-avatar">PO</span>
            <span><strong>PerfectOddsNL</strong><small>bot</small></span>
            <i aria-hidden="true" />
          </div>
          <div className="telegram-refresh-message-bubble">
            <pre>{`(6.70%👥🟡) Real Madrid v Barcelona (Zo 17 Mei 20:00)

⚽️ Barcelona
🥅 5.5 schoten op doel
↗️ kambi (Over) 1.94 (€55*) → €106,70
↙️ bet365 (Under) 2.41 (€45*) → €108,45`}</pre>
            <span className="telegram-refresh-message-time">20:00 ✓✓</span>
          </div>
          <p>*Inzetten zijn voorbeeldbedragen. Odds kunnen wijzigen voordat je weddenschap is geplaatst.</p>
        </div>
      </section>
    </section>
  </PublicLayout>
);

// ============================================================
// MOCK DATA FOR ADMIN & ACCOUNT PAGES
// ============================================================

const adminStatCards = [
  { label: 'Gebruikers', value: '128', subtext: 'Totaal geregistreerd' },
  { label: 'Actieve abonnementen', value: '42', subtext: 'Betalende klanten' },
  { label: 'Telegram toegang', value: '39', subtext: 'Actieve koppelingen' },
  { label: 'Surebets vandaag', value: '18', subtext: 'Verzonden meldingen' },
  { label: 'Systeemstatus', value: 'Online', subtext: 'Alle services actief' },
  { label: 'Errors vandaag', value: '2', subtext: 'Controle aanbevolen' },
];

const adminUsers = [
  { name: 'Mauro Marrale', email: 'mauromarrale2@gmail.com', role: 'Gebruiker', subscription: 'Pro abonnement', status: 'Actief', telegram: 'Actief', joined: '15 mei 2026' },
  { name: 'Test gebruiker', email: 'test@perfectodds.nl', role: 'Gebruiker', subscription: 'Geen', status: 'Geen', telegram: 'Niet actief', joined: '15 mei 2026' },
  { name: 'Betalende klant', email: 'klant@perfectodds.nl', role: 'Gebruiker', subscription: 'Pro abonnement', status: 'Actief', telegram: 'Actief', joined: '15 mei 2026' },
  { name: 'Admin', email: 'admin@perfectodds.nl', role: 'Admin', subscription: 'Admin', status: 'Actief', telegram: 'Admin', joined: '15 mei 2026' },
];

const adminSubscriptions = [
  { user: 'klant@perfectodds.nl', email: 'klant@perfectodds.nl', package: 'Pro abonnement', status: 'Actief', startDate: '15 mei 2026', nextPayment: 'Nog niet gekoppeld', tracker: 'Ja', community: 'Ja' },
  { user: 'test@perfectodds.nl', email: 'test@perfectodds.nl', package: 'Geen', status: 'Geen', startDate: '-', nextPayment: '-', tracker: 'Nee', community: 'Nee' },
  { user: 'mauromarrale2@gmail.com', email: 'mauromarrale2@gmail.com', package: 'Pro abonnement', status: 'Actief', startDate: '15 mei 2026', nextPayment: 'Nog niet gekoppeld', tracker: 'Ja', community: 'Ja' },
];

const adminActivityFeed = [
  { type: 'info', message: 'Nieuwe gebruiker geregistreerd: test@perfectodds.nl', time: 'Nu' },
  { type: 'info', message: 'Pro abonnement actief: klant@perfectodds.nl', time: '5 min geleden' },
  { type: 'info', message: 'Surebet verzonden: Real Madrid v Barcelona', time: '8 min geleden' },
  { type: 'info', message: 'Telegram koppeling actief: klant@perfectodds.nl', time: '12 min geleden' },
  { type: 'warning', message: 'Waarschuwing: Bet365 feed tijdelijk vertraagd', time: '2 uur geleden' },
];

const adminSurebets = [
  { date: '17 mei 2026', match: 'Real Madrid v Barcelona', sport: 'Voetbal', market: '5.5 schoten op doel', type: 'Pre-match', bookmakers: 'Bet365 / Kambi', odds: '1.94 / 2.41', roi: '6,70%', status: 'Verzonden' },
  { date: '17 mei 2026', match: 'Ajax v PSV', sport: 'Voetbal', market: 'Over/Under 2.5 goals', type: 'Live', bookmakers: 'Bet365 / Kambi', odds: '2.10 / 1.89', roi: '2,90%', status: 'Verlopen' },
];

const adminBookmakers = [
  { source: 'Bet365', platform: 'Bet365', status: 'Online', lastUpdate: '3 sec geleden', live: 'Ja', preMatch: 'Ja', remark: 'Eigen oddsstructuur' },
  { source: 'Kambi', platform: 'Kambi', status: 'Online', lastUpdate: '4 sec geleden', live: 'Ja', preMatch: 'Ja', remark: 'Platformbron' },
  { source: 'Unibet', platform: 'Kambi', status: 'Mock', lastUpdate: '-', live: 'Later', preMatch: 'Later', remark: 'Kambi-bookmaker' },
  { source: 'BetCity', platform: 'Kambi', status: 'Mock', lastUpdate: '-', live: 'Later', preMatch: 'Later', remark: 'Kambi-bookmaker' },
];

const adminLogs = [
  { time: '12:45', type: 'Info', source: 'Auth', message: 'Admin ingelogd', details: 'admin@perfectodds.nl' },
  { time: '12:42', type: 'Info', source: 'Tracker', message: 'Surebet opgeslagen in localStorage', details: 'Real Madrid v Barcelona' },
  { time: '12:38', type: 'Warning', source: 'Bookmaker', message: 'Bet365 update vertraagd', details: 'Feed update duurt langer dan verwacht' },
  { time: '12:30', type: 'Error', source: 'Telegram', message: 'Testmelding niet verzonden', details: 'Telegram nog niet gekoppeld' },
  { time: '12:15', type: 'Info', source: 'Payment', message: 'Payment provider nog niet gekoppeld', details: 'Betaalsysteem moet nog worden geïntegreerd' },
];

// ============================================================
// ADMIN PAGE - Full implementation with 8 tabs
// ============================================================

export const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overzicht');
  const [notice, setNotice] = useState<string>('');
  const tabs = ['Overzicht', 'Gebruikers', 'Abonnementen', 'Telegram', 'Surebet history', 'Bookmakers', 'Logs', 'Status'];

  const showNotice = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(''), 3000);
  };

  return (
    <PublicLayout>
      <section className="public-page-card admin-page">
        <section className="admin-hero">
          <span className="subscription-badge">Alleen admin</span>
          <h1>Admin dashboard</h1>
          <p>Beheer gebruikers, abonnementen, Telegram toegang, surebet history en systeemstatus.</p>
        </section>

        {notice && <div className="admin-notice" role="status">{notice}</div>}

        {/* Stat Cards */}
        <div className="admin-stats-grid">
          {adminStatCards.map((card) => (
            <div key={card.label} className="admin-stat-card card">
              <div className="stat-value">{card.value}</div>
              <div className="stat-label">{card.label}</div>
              <div className="stat-subtext">{card.subtext}</div>
            </div>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="admin-tabs-container">
          <div className="admin-tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`admin-tab ${activeTab === tab.toLowerCase().replace(' ', '-') ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.toLowerCase().replace(' ', '-'))}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="admin-tab-content">
          {activeTab === 'overzicht' && (
            <div>
              <h2>Snelle acties</h2>
              <div className="admin-quick-actions">
                <div className="card">
                  <h3>Gebruiker zoeken</h3>
                  <p>Zoek een gebruiker op e-mail of status.</p>
                  <button className="card-btn" onClick={() => setActiveTab('gebruikers')}>Naar gebruikers</button>
                </div>
                <div className="card">
                  <h3>Abonnement controleren</h3>
                  <p>Bekijk actieve, verlopen en gratis accounts.</p>
                  <button className="card-btn" onClick={() => setActiveTab('abonnementen')}>Naar abonnementen</button>
                </div>
                <div className="card">
                  <h3>Telegram testmelding</h3>
                  <p>Controleer later of Telegram meldingen goed werken.</p>
                  <button className="card-btn" onClick={() => setActiveTab('telegram')}>Naar Telegram</button>
                </div>
                <div className="card">
                  <h3>Systeemstatus bekijken</h3>
                  <p>Bekijk bookmaker feeds, Telegram bot en trackerstatus.</p>
                  <button className="card-btn" onClick={() => setActiveTab('status')}>Naar status</button>
                </div>
              </div>

              <h2 style={{ marginTop: '2rem' }}>Laatste activiteit</h2>
              <div className="admin-activity-feed">
                {adminActivityFeed.map((item, i) => (
                  <div key={i} className={`activity-item activity-${item.type}`}>
                    <div className="activity-content">
                      <p>{item.message}</p>
                      <span className="activity-time">{item.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'gebruikers' && (
            <div>
              <h2>Gebruikers</h2>
              <p>Bekijk en filter gebruikers binnen PerfectOddsNL.</p>
              <input type="text" placeholder="Zoek op naam of e-mail..." className="admin-search-input" />
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Naam</th>
                      <th>E-mail</th>
                      <th>Rol</th>
                      <th>Abonnement</th>
                      <th>Status</th>
                      <th>Telegram</th>
                      <th>Lid sinds</th>
                      <th>Acties</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map((user) => (
                      <tr key={user.email}>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>{user.role}</td>
                        <td>{user.subscription}</td>
                        <td><span className={`badge badge-${user.status === 'Actief' ? 'success' : 'soft'}`}>{user.status}</span></td>
                        <td>{user.telegram}</td>
                        <td>{user.joined}</td>
                        <td className="admin-actions">
                          <button className="card-btn card-btn-sm" onClick={() => showNotice(`Gebruiker details: ${user.email}`)}>Bekijken</button>
                          <button className="card-btn card-btn-sm" onClick={() => showNotice('Gebruiker bewerken wordt later gekoppeld.')}>Bewerken</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'abonnementen' && (
            <div>
              <h2>Abonnementen</h2>
              <p>Bekijk abonnementen, pakketten en toegang per gebruiker.</p>
              <div className="admin-stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="admin-stat-card card">
                  <div className="stat-value">86</div>
                  <div className="stat-label">Gratis kanaal</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">12</div>
                  <div className="stat-label">Pre-match</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">8</div>
                  <div className="stat-label">Live</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">22</div>
                  <div className="stat-label">Pro</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">5</div>
                  <div className="stat-label">Verlopen</div>
                </div>
              </div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Gebruiker</th>
                      <th>Pakket</th>
                      <th>Status</th>
                      <th>Startdatum</th>
                      <th>Volgende betaling</th>
                      <th>Tracker</th>
                      <th>Community</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminSubscriptions.map((sub) => (
                      <tr key={sub.email}>
                        <td>{sub.user}</td>
                        <td>{sub.package}</td>
                        <td><span className={`badge badge-${sub.status === 'Actief' ? 'success' : 'soft'}`}>{sub.status}</span></td>
                        <td>{sub.startDate}</td>
                        <td>{sub.nextPayment}</td>
                        <td>{sub.tracker}</td>
                        <td>{sub.community}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="card" style={{ marginTop: '2rem' }}>
                <h3>Later koppelen aan betalingen</h3>
                <p>Deze omgeving gebruikt nu voorbeelddata. Later worden abonnementen gekoppeld aan het betaalsysteem, zodat status, betaling en toegang automatisch bijgewerkt worden.</p>
              </div>
            </div>
          )}

          {activeTab === 'telegram' && (
            <div>
              <h2>Telegram toegang</h2>
              <p>Beheer Telegrammeldingen en kanaaltoegang per abonnement.</p>
              <div className="admin-stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="admin-stat-card card">
                  <div className="stat-value">39</div>
                  <div className="stat-label">Actieve koppelingen</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">4</div>
                  <div className="stat-label">Niet gekoppeld</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">39</div>
                  <div className="stat-label">Community toegang</div>
                </div>
              </div>
              <div className="card">
                <h3>Telegram bot status</h3>
                <div className="status-grid">
                  <div><strong>Bot status:</strong> Online</div>
                  <div><strong>Laatste check:</strong> Zojuist</div>
                  <div><strong>Laatste fout:</strong> Geen</div>
                </div>
              </div>
              <div className="card">
                <h3>Telegram koppeling</h3>
                <p>Telegramtoegang wordt automatisch gekoppeld aan het actieve abonnement. Betaalde klanten krijgen hun besloten meldingenkanaal; gratis gebruikers openen rechtstreeks het gratis kanaal.</p>
              </div>
            </div>
          )}

          {activeTab === 'surebet-history' && (
            <div>
              <h2>Surebet history</h2>
              <p>Bekijk mock Telegram surebets die door het systeem zijn gevonden of verzonden.</p>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Wedstrijd</th>
                      <th>Sport</th>
                      <th>Markt</th>
                      <th>Type</th>
                      <th>Bookmakers</th>
                      <th>Odds</th>
                      <th>ROI</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminSurebets.map((sb) => (
                      <tr key={`${sb.date}-${sb.match}`}>
                        <td>{sb.date}</td>
                        <td>{sb.match}</td>
                        <td>{sb.sport}</td>
                        <td>{sb.market}</td>
                        <td>{sb.type}</td>
                        <td>{sb.bookmakers}</td>
                        <td>{sb.odds}</td>
                        <td>{sb.roi}</td>
                        <td><span className={`badge badge-${sb.status === 'Verzonden' ? 'success' : 'soft'}`}>{sb.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'bookmakers' && (
            <div>
              <h2>Bookmaker status</h2>
              <p>Bekijk later de status van Bet365, Kambi en de Kambi-bookmakers.</p>
              <div className="admin-stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="admin-stat-card card">
                  <div className="stat-value">Online</div>
                  <div className="stat-label">Bet365</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">Online</div>
                  <div className="stat-label">Kambi</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">3 sec</div>
                  <div className="stat-label">Laatste update</div>
                </div>
              </div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Bron</th>
                      <th>Platform</th>
                      <th>Status</th>
                      <th>Laatste update</th>
                      <th>Live</th>
                      <th>Pre-match</th>
                      <th>Opmerking</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminBookmakers.map((bm) => (
                      <tr key={bm.source}>
                        <td>{bm.source}</td>
                        <td>{bm.platform}</td>
                        <td><span className={`badge badge-${bm.status === 'Online' ? 'success' : 'soft'}`}>{bm.status}</span></td>
                        <td>{bm.lastUpdate}</td>
                        <td>{bm.live}</td>
                        <td>{bm.preMatch}</td>
                        <td>{bm.remark}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div>
              <h2>Logs</h2>
              <p>Bekijk systeemmeldingen, errors en acties. Voor nu gebruikt deze pagina mock logs.</p>
              <div className="admin-logs-list">
                {adminLogs.map((log, i) => (
                  <div key={i} className={`log-entry log-${log.type.toLowerCase()}`}>
                    <div className="log-time">{log.time}</div>
                    <div className="log-type"><span className={`badge badge-${log.type === 'Error' ? 'danger' : log.type === 'Warning' ? 'warning' : 'soft'}`}>{log.type}</span></div>
                    <div className="log-source">{log.source}</div>
                    <div className="log-message">{log.message}</div>
                    <div className="log-details">{log.details}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'status' && (
            <div>
              <h2>Technische status</h2>
              <p>Bekijk later of alle onderdelen van PerfectOddsNL goed werken.</p>
              <div className="admin-stats-grid" style={{ marginBottom: '2rem' }}>
                <div className="admin-stat-card card">
                  <div className="stat-value">Online</div>
                  <div className="stat-label">Website</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">Mock</div>
                  <div className="stat-label">Auth</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">Actief</div>
                  <div className="stat-label">Tracker (LocalStorage)</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">Niet gekoppeld</div>
                  <div className="stat-label">Telegram bot</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">Niet gekoppeld</div>
                  <div className="stat-label">Payment provider</div>
                </div>
                <div className="admin-stat-card card">
                  <div className="stat-value">Mock</div>
                  <div className="stat-label">Bookmaker feeds</div>
                </div>
              </div>
              <div className="card">
                <h3>Wat moet later gekoppeld worden?</h3>
                <ul>
                  <li>Echte database</li>
                  <li>Echte gebruikersauthenticatie</li>
                  <li>Betaalprovider</li>
                  <li>Telegram bot</li>
                  <li>Telegram kanaaltoegang</li>
                  <li>Bookmaker feeds</li>
                  <li>Surebet engine</li>
                  <li>Logging en monitoring</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

// ============================================================
// MIJN ACCOUNT PAGE - Full implementation with tabs
// ============================================================

export const MijnAccountPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overzicht');
  const [notice, setNotice] = useState<string>('');
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [telegramLink, setTelegramLink] = useState<TelegramLinkState | null>(null);
  const [isLoadingTelegram, setIsLoadingTelegram] = useState(false);
  const [billingInvoices, setBillingInvoices] = useState<BillingInvoiceApiItem[]>([]);
  const [invoiceLoadState, setInvoiceLoadState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [billingSummary, setBillingSummary] = useState<BillingSubscriptionSummaryApi | null>(null);
  const [billingSummaryState, setBillingSummaryState] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [referralOverview, setReferralOverview] = useState<ReferralOverviewApi | null>(null);
  const [isLoadingReferrals, setIsLoadingReferrals] = useState(false);
  const { user, logout, updateCurrentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (requestedTab && ['overzicht', 'abonnement', 'telegram', 'vrienden', 'facturen', 'beveiliging'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);

  useEffect(() => {
    if (!showBillingModal) return undefined;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isOpeningPortal) setShowBillingModal(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpeningPortal, showBillingModal]);

  useEffect(() => {
    const flashMessage = (location.state as { message?: string } | null | undefined)?.message;
    if (flashMessage) {
      setNotice(flashMessage);
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (activeTab !== 'telegram' || !user) {
      return;
    }

    let cancelled = false;
    setIsLoadingTelegram(true);
    authAPI.getTelegramLink()
      .then((response) => {
        if (cancelled) {
          return;
        }
        const nextLink = response.data.telegramLink as TelegramLinkState;
        setTelegramLink(nextLink);
      })
      .catch(() => {
        if (!cancelled) {
          showNotice('Telegram status ophalen is niet gelukt.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingTelegram(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, user]);

  useEffect(() => {
    if (!['vrienden', 'abonnement'].includes(activeTab) || !user) return;
    let cancelled = false;
    setIsLoadingReferrals(true);
    referralAPI.getMine()
      .then((response) => {
        if (!cancelled) setReferralOverview(response.data?.referral as ReferralOverviewApi);
      })
      .catch(() => {
        if (!cancelled) showNotice('Je vriendenoverzicht kon niet worden geladen.');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingReferrals(false);
      });
    return () => { cancelled = true; };
  }, [activeTab, user]);

  useEffect(() => {
    if (activeTab !== 'facturen' || !user) {
      return;
    }

    let cancelled = false;
    setInvoiceLoadState('loading');
    billingAPI.listInvoices()
      .then((response) => {
        if (cancelled) return;
        setBillingInvoices((response.data?.invoices || []) as BillingInvoiceApiItem[]);
        setInvoiceLoadState('loaded');
      })
      .catch(() => {
        if (!cancelled) setInvoiceLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, user]);

  useEffect(() => {
    if (activeTab !== 'abonnement' || !user) return;
    let cancelled = false;
    setBillingSummaryState('loading');
    billingAPI.getSubscriptionSummary()
      .then((response) => {
        if (cancelled) return;
        setBillingSummary(response.data?.subscription || null);
        setBillingSummaryState('loaded');
      })
      .catch(() => {
        if (!cancelled) setBillingSummaryState('error');
      });
    return () => { cancelled = true; };
  }, [activeTab, user]);

  const accountStatus = user?.subscriptionStatus ?? 'none';
  const accountRole = user?.role ?? 'user';
  const accountLabel = user?.name ?? 'Gebruiker';
  const accountPlan = user?.plan ?? 'none';
  const accountBillingCycle = user?.billingCycle ?? 'none';
  const billingManagedByStripe = user?.billingManagedByStripe === true;
  const hasRecurringBilling = accountRole !== 'admin' && billingManagedByStripe && accountBillingCycle !== 'day-pass' && accountStatus === 'active';
  const hasManualSubscription = accountRole !== 'admin'
    && !billingManagedByStripe
    && accountStatus === 'active'
    && ['pro', 'pre-match', 'live'].includes(accountPlan);
  const accountPackage = accountRole === 'admin'
    ? 'Admin toegang'
    : accountStatus === 'active'
      ? getPlanLabel(accountPlan as PlanType, accountBillingCycle)
      : 'Geen actief abonnement';
  const hasAccountAccess = accountStatus === 'active' || accountRole === 'admin';
  const accountInitial = (accountLabel.replace(/^@/, '').trim().charAt(0) || 'P').toUpperCase();
  const accountMemberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString('nl-NL', { month: 'short', year: 'numeric' })
    : 'Onbekend';
  const accountBillingCycleLabel: Record<BillingCycle, string> = {
    none: 'Niet beschikbaar',
    month: 'Maandelijks',
    week: 'Wekelijks',
    'day-pass': 'Dagpas',
  };
  const accountAccessUntil = accountRole === 'admin'
    ? 'Onbeperkt'
    : user?.subscriptionEndsAt
      ? new Date(user.subscriptionEndsAt).toLocaleDateString('nl-NL', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
      : 'Niet beschikbaar';
  const paymentMethodDisplay = getPaymentMethodDisplay(billingSummary?.paymentMethod);
  const readyReferralRewards = (referralOverview?.availableRewards || 0) + (referralOverview?.reservedRewards || 0);
  const nextChargeHasReferral = billingSummary?.nextCharge?.discounts.some((discount) => discount.isReferral) === true;
  const nextChargeHasOtherDiscount = billingSummary?.nextCharge?.discounts.some((discount) => !discount.isReferral) === true;

  const showNotice = (message: string) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 3000);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCopyReferralLink = async () => {
    if (!referralOverview?.shareUrl) return;
    try {
      await navigator.clipboard.writeText(referralOverview.shareUrl);
      showNotice('Vriendenlink gekopieerd.');
    } catch {
      showNotice('Kopiëren lukte niet. Selecteer de link handmatig.');
    }
  };

  const handleShareReferral = async () => {
    if (!referralOverview?.shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: '€10 korting bij PerfectOddsNL',
          text: 'Gebruik mijn vriendenlink. We krijgen allebei €10 korting op een volgende maandbetaling.',
          url: referralOverview.shareUrl,
        });
        return;
      } catch {
        // De gebruiker kan het native deelvenster zonder gevolgen sluiten.
      }
    }
    await handleCopyReferralLink();
  };

  const handleManageSubscription = async (flow: 'manage' | 'subscription_update' | 'subscription_cancel') => {
    setIsOpeningPortal(true);
    try {
      const response = await billingAPI.createPortalSession(flow);
      const portalUrl = response.data?.url;
      if (typeof portalUrl === 'string' && portalUrl) {
        window.location.assign(portalUrl);
        return;
      }
      showNotice('Het abonnementsportaal kon niet worden geopend.');
    } catch (error) {
      showNotice(getApiErrorDetail(error, 'Het abonnementsportaal kon niet worden geopend.'));
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleConfirmPasswordChange = async () => {
    if (!currentPassword.trim()) {
      showNotice('Vul je huidige wachtwoord in.');
      return;
    }
    if (newPassword.trim().length < 6) {
      showNotice('Nieuw wachtwoord moet minimaal 6 tekens bevatten.');
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await authAPI.changePassword({
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      showNotice(response.data?.message || 'Je wachtwoord is gewijzigd.');
    } catch (error) {
      showNotice(getApiErrorDetail(error, 'Wachtwoord wijzigen is niet gelukt.'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user?.email) {
      showNotice('Geen e-mailadres gevonden voor dit account.');
      return;
    }
    if (!deletePassword.trim()) {
      showNotice('Vul je wachtwoord in om je account te verwijderen.');
      return;
    }
    const confirmed = window.confirm('Weet je zeker dat je je account definitief wilt verwijderen? Je profiel, trackerdata en Telegram-koppeling worden verwijderd.');
    if (!confirmed) {
      return;
    }

    setIsDeletingAccount(true);
    try {
      await authAPI.deleteAccount({ password: deletePassword });
      logout();
      navigate('/', { replace: true, state: { message: 'Je account is verwijderd.' } });
    } catch (error) {
      showNotice(getApiErrorDetail(error, 'Account verwijderen is niet gelukt.'));
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleStartTelegramLink = async () => {
    setIsLoadingTelegram(true);
    try {
      const response = await authAPI.startTelegramLink();
      setTelegramLink(response.data.telegramLink);
      const botStartUrl = response.data.telegramLink?.botStartUrl;
      if (botStartUrl) {
        window.location.assign(botStartUrl);
        return;
      }
      showNotice('De Telegram botlink kon niet worden geopend.');
    } catch (error) {
      const message = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : undefined;
      showNotice(message || 'Telegram koppelcode maken is niet gelukt.');
    } finally {
      setIsLoadingTelegram(false);
    }
  };

  const handleUnlinkTelegram = async () => {
    setIsLoadingTelegram(true);
    try {
      const response = await authAPI.unlinkTelegram();
      setTelegramLink(response.data.telegramLink);
      if (response.data.user) {
        updateCurrentUser(response.data.user);
      }
      showNotice('Telegram account ontkoppeld.');
    } catch (error) {
      const message = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : undefined;
      showNotice(message || 'Telegram ontkoppelen is niet gelukt.');
    } finally {
      setIsLoadingTelegram(false);
    }
  };

  const handleSyncTelegramAccess = async () => {
    setIsLoadingTelegram(true);
    try {
      const response = await authAPI.syncTelegramAccess();
      setTelegramLink(response.data.telegramLink);
      if (response.data.user) {
        updateCurrentUser(response.data.user);
      }
      showNotice(response.data.telegramLink?.inviteLink ? 'Uitnodigingen zijn naar je Telegram botchat gestuurd.' : 'Telegram toegang is bijgewerkt.');
    } catch (error) {
      const message = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : undefined;
      showNotice(message || 'Telegram toegang bijwerken is niet gelukt.');
    } finally {
      setIsLoadingTelegram(false);
    }
  };

  const getPlanFeatures = (plan: string): string[] => {
    const features: Record<string, string[]> = {
      pro: ['Pre-match surebetmeldingen', 'Live surebetmeldingen', 'Persoonlijke Telegramtoegang', 'Surebet Tracker', 'Resultaten en ROI bijhouden', 'Kosten en correcties bijhouden'],
      'pre-match': ['Pre-match surebetmeldingen', 'Persoonlijke Telegramtoegang', 'Surebet Tracker', 'Resultaten en ROI bijhouden', 'Kosten en correcties bijhouden'],
      live: ['Live surebetmeldingen', 'Persoonlijke Telegramtoegang', 'Surebet Tracker', 'Resultaten en ROI bijhouden', 'Kosten en correcties bijhouden'],
      gratis: ['Toegang tot het gratis Telegramkanaal', 'Ongeveer 1 tot 3 meldingen per week'],
    };
    return features[plan] || features.pro;
  };

  const tabs = [
    { id: 'overzicht', label: 'Overzicht' },
    { id: 'abonnement', label: 'Abonnement' },
    { id: 'telegram', label: 'Telegram' },
    { id: 'vrienden', label: 'Vrienden' },
    { id: 'facturen', label: 'Facturen' },
    { id: 'beveiliging', label: 'Beveiliging' },
  ];

  return (
    <PublicLayout>
      <section className="public-page-card account-page">
        <section className="account-hero">
          <div className="account-identity">
            <div className="account-avatar" aria-hidden="true">{accountInitial}</div>
            <div className="account-hero-copy">
              <span className="account-eyebrow">Mijn PerfectOddsNL</span>
              <h1>Mijn account</h1>
              <p>Beheer je profiel, abonnement en toegang vanuit één overzicht.</p>
            </div>
          </div>
          <div className="account-hero-status">
            <span className={`account-state account-state--${hasAccountAccess ? 'active' : 'inactive'}`}>
              <i aria-hidden="true" />
              {hasAccountAccess ? 'Toegang actief' : 'Geen actief abonnement'}
            </span>
            <strong>{accountPackage}</strong>
            <small>{user?.email}</small>
          </div>
        </section>

        {notice && (
          <div className="account-notice" role="status" aria-live="polite">
            {notice}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="account-tabs-container">
          <div className="account-tabs" role="tablist" aria-label="Accountonderdelen">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                className={`account-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="account-tab-content">
          {activeTab === 'overzicht' && (
            <>
              <section className="account-overview-card card">
                <div className="account-overview-copy">
                  <div className="account-card-heading">
                    <span className="account-card-icon" aria-hidden="true">P</span>
                    <div>
                      <h2>Profielgegevens</h2>
                      <p>De gegevens die aan je account zijn gekoppeld.</p>
                    </div>
                  </div>
                  <dl className="account-details-list">
                    <div>
                      <dt>E-mail</dt>
                      <dd>{user?.email}</dd>
                    </div>
                    <div>
                      <dt>Account sinds</dt>
                      <dd>{accountMemberSince}</dd>
                    </div>
                  </dl>
                </div>

                <div className="account-badge-stack">
                  <span className="account-label">Huidige toegang</span>
                  <div className="account-access-summary-row">
                    <span>Abonnement</span>
                    <strong>{accountPackage}</strong>
                  </div>
                  <div className="account-access-summary-row">
                    <span>Surebet Tracker</span>
                    <strong className={hasAccountAccess ? 'is-active' : ''}>{hasAccountAccess ? 'Actief' : 'Geen toegang'}</strong>
                  </div>
                  <div className="account-access-summary-row">
                    <span>Telegram</span>
                    <strong className={hasAccountAccess ? 'is-active' : ''}>{hasAccountAccess ? 'Actief' : 'Niet actief'}</strong>
                  </div>
                </div>
              </section>

              {accountStatus !== 'active' && accountRole !== 'admin' && (
                <div className="card account-upsell-card">
                  <div>
                    <span className="account-card-kicker">Volgende stap</span>
                    <h3>Ontgrendel je volledige account</h3>
                    <p>Kies een abonnement voor toegang tot de Surebet Tracker en Telegrammeldingen.</p>
                  </div>
                  <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
                </div>
              )}

              {(accountStatus === 'active' || accountRole === 'admin') && (
                <div className="account-quick-actions">
                  <Link to="/tracker" className="card-btn card-link">Open Mijn tracker</Link>
                  {accountRole === 'admin' && <Link to="/admin" className="card-btn card-link">Open admin dashboard</Link>}
                </div>
              )}
            </>
          )}

          {activeTab === 'abonnement' && (
            <>
              <div className="account-section-header account-subscription-header">
                <h2>Mijn abonnement</h2>
                {accountStatus !== 'active' && <p>Je hebt nog geen actief abonnement.</p>}
              </div>
              {accountStatus === 'active' ? (
                <>
                  <div className="account-subscription-card card">
                    <div className="subscription-info-grid">
                      <div>
                        <span className="account-label">Huidig pakket</span>
                        <strong>{accountPackage}</strong>
                      </div>
                      <div>
                        <span className="account-label">Status</span>
                        <strong>Actief</strong>
                      </div>
                      <div>
                        <span className="account-label">Looptijd</span>
                        <strong>{accountBillingCycleLabel[accountBillingCycle]}</strong>
                      </div>
                      <div>
                        <span className="account-label">Toegang tot</span>
                        <strong>{accountAccessUntil}</strong>
                      </div>
                    </div>

                    <div className="account-feature-list-block">
                      <h3>Toegang binnen je abonnement</h3>
                      <ul className="subscription-list account-feature-list">
                        {getPlanFeatures(accountPlan).map((feature) => (
                          <li key={feature}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {billingManagedByStripe && accountBillingCycle !== 'day-pass' && (
                    <section className="account-next-charge-card card" aria-labelledby="next-charge-title">
                      <div className="account-next-charge-heading">
                        <div>
                          <span className="account-card-kicker">Komende betaling</span>
                          <h3 id="next-charge-title">Volgende afschrijving</h3>
                        </div>
                        {billingSummary?.nextCharge && <span className="account-estimate-badge">Stripe-berekening</span>}
                      </div>

                      {billingSummaryState === 'loading' ? (
                        <div className="account-billing-loading" role="status" aria-live="polite">
                          <span className="site-loading-spinner" aria-hidden="true" />
                          <span>Volgende afschrijving ophalen...</span>
                        </div>
                      ) : billingSummaryState === 'error' ? (
                        <div className="account-billing-message account-billing-message--warning">
                          <strong>Betalingsinformatie tijdelijk niet beschikbaar</strong>
                          <span>Je abonnement blijft gewoon actief. Via Abonnement beheren kun je de gegevens ook bij Stripe bekijken.</span>
                        </div>
                      ) : billingSummary?.cancelAtPeriodEnd ? (
                        <div className="account-billing-message">
                          <strong>Geen volgende afschrijving gepland</strong>
                          <span>
                            Je abonnement is opgezegd en loopt door tot{' '}
                            {billingSummary.cancellationDate
                              ? new Date(billingSummary.cancellationDate).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
                              : accountAccessUntil}.
                          </span>
                        </div>
                      ) : billingSummary?.nextCharge ? (
                        <>
                          <div className="account-next-charge-hero">
                            <div>
                              <span>Verwachte datum</span>
                              <strong>
                                {billingSummary.nextCharge.date
                                  ? new Date(billingSummary.nextCharge.date).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                                  : 'Wordt door Stripe bepaald'}
                              </strong>
                            </div>
                            <div className="account-next-charge-total">
                              <span>Verwacht totaal</span>
                              <strong>{formatBillingAmount(billingSummary.nextCharge.amount, billingSummary.nextCharge.currency)}</strong>
                            </div>
                          </div>

                          <dl className="account-next-charge-details">
                            <div>
                              <dt>Pakket bij afschrijving</dt>
                              <dd>{getPlanLabel(billingSummary.nextCharge.plan, billingSummary.nextCharge.billingCycle)}</dd>
                            </div>
                            <div>
                              <dt>Betaalmethode</dt>
                              <dd>{paymentMethodDisplay.title}<small>{paymentMethodDisplay.detail}</small></dd>
                            </div>
                            {billingSummary.paymentMethod?.billingName && (
                              <div>
                                <dt>Naam betaalmethode</dt>
                                <dd>{billingSummary.paymentMethod.billingName}</dd>
                              </div>
                            )}
                            <div>
                              <dt>Afschrijving</dt>
                              <dd>Automatisch via Stripe<small>De factuur verschijnt na verwerking bij Facturen.</small></dd>
                            </div>
                          </dl>

                          <div className="account-next-charge-breakdown">
                            <div><span>Bedrag vóór korting</span><strong>{formatBillingAmount(billingSummary.nextCharge.subtotal, billingSummary.nextCharge.currency)}</strong></div>
                            {billingSummary.nextCharge.discounts.map((discount, index) => (
                              <div className="is-discount" key={`${discount.name}-${index}`}>
                                <span>{discount.isReferral ? 'Vriendenkorting' : discount.name}</span>
                                <strong>−{formatBillingAmount(discount.amount, billingSummary.nextCharge?.currency)}</strong>
                              </div>
                            ))}
                            {billingSummary.nextCharge.taxAmount > 0 && (
                              <div><span>Belasting</span><strong>{formatBillingAmount(billingSummary.nextCharge.taxAmount, billingSummary.nextCharge.currency)}</strong></div>
                            )}
                            <div className="is-total"><span>Verwachte afschrijving</span><strong>{formatBillingAmount(billingSummary.nextCharge.amount, billingSummary.nextCharge.currency)}</strong></div>
                          </div>

                          {(nextChargeHasReferral || readyReferralRewards > 0) && (
                            <div className="account-referral-queue-note">
                              <strong>
                                {nextChargeHasReferral
                                  ? 'Op deze maandbetaling wordt één vriendenkorting van €10 gebruikt.'
                                  : nextChargeHasOtherDiscount
                                    ? 'Je vriendenkorting blijft voor de volgende maand klaarstaan.'
                                    : `${readyReferralRewards} vriendenkorting${readyReferralRewards === 1 ? '' : 'en'} klaar voor een volgende maandbetaling.`}
                              </strong>
                              <span>
                                Vriendenkorting is niet combineerbaar met andere acties of kortingscodes. Per maandbetaling wordt maximaal één vriendenkorting van €10 gebruikt; eventuele overige kortingen schuiven automatisch door.
                              </span>
                            </div>
                          )}

                          {billingSummary.scheduledChange && (
                            <div className="account-scheduled-change">
                              <span>Ingeplande wijziging</span>
                              <strong>{getPlanLabel(billingSummary.scheduledChange.plan, billingSummary.scheduledChange.billingCycle)}</strong>
                              {billingSummary.scheduledChange.effectiveAt && (
                                <small>Vanaf {new Date(billingSummary.scheduledChange.effectiveAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</small>
                              )}
                            </div>
                          )}

                          <p className="account-next-charge-note">
                            Dit is een actuele schatting van Stripe. Een latere abonnementswijziging, korting of openstaand bedrag kan het uiteindelijke totaal veranderen.
                          </p>
                        </>
                      ) : (
                        <div className="account-billing-message">
                          <strong>Nog geen volgende afschrijving beschikbaar</strong>
                          <span>Stripe heeft op dit moment geen nieuwe automatische betaling ingepland.</span>
                        </div>
                      )}
                    </section>
                  )}

                  <div className="subscription-cta-row account-cta-row">
                    {hasRecurringBilling ? (
                      <button type="button" className="card-btn" onClick={() => setShowBillingModal(true)} disabled={isOpeningPortal} aria-busy={isOpeningPortal}>
                        Abonnement beheren
                      </button>
                    ) : accountBillingCycle === 'day-pass' ? (
                      <Link to="/pricing" className="card-btn card-link">Nieuw week- of maandabonnement afsluiten</Link>
                    ) : null}
                  </div>
                  {hasRecurringBilling && (
                    <p className="account-note account-subscription-note">
                      Kies je een goedkoper pakket of een kortere looptijd? Dan blijft je huidige abonnement actief tot het einde van de al betaalde periode. De wijziging gaat daarna automatisch in.
                    </p>
                  )}
                  {accountBillingCycle === 'day-pass' && !hasRecurringBilling && (
                    <p className="account-note">
                      Sluit nu alvast een nieuw week- of maandabonnement af. Je dagpas blijft actief en de eerste abonnementsbetaling volgt pas nadat de dagpas afloopt.
                    </p>
                  )}
                  {hasManualSubscription && (
                    <p className="account-note account-manual-subscription-note">
                      Dit abonnement is handmatig door PerfectOddsNL toegekend. Er is geen automatische afschrijving of Stripe-abonnement om te beheren.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="card">
                    <h3>Beschikbare opties</h3>
                    <div className="account-option-grid">
                      <a href={FREE_TELEGRAM_JOIN_URL} className="account-option-card" target="_blank" rel="noreferrer">
                        <span className="account-option-kicker">Gratis</span>
                        <span className="account-option-title">Gratis kanaal</span>
                        <span className="account-option-meta">Gratis surebetmeldingen</span>
                      </a>
                      <Link to="/checkout?plan=pre-match&billingCycle=month&source=account" className="account-option-card">
                        <span className="account-option-kicker">Pre-match</span>
                        <span className="account-option-title">Pre-match abonnement</span>
                        <span className="account-option-meta">Surebetmeldingen vóór de wedstrijd</span>
                      </Link>
                      <Link to="/checkout?plan=live&billingCycle=month&source=account" className="account-option-card">
                        <span className="account-option-kicker">Live</span>
                        <span className="account-option-title">Live abonnement</span>
                        <span className="account-option-meta">Live surebetmeldingen tijdens wedstrijden</span>
                      </Link>
                      <Link to="/checkout?plan=pro&billingCycle=month&source=account" className="account-option-card">
                        <span className="account-option-kicker">Pro</span>
                        <span className="account-option-title">Pro abonnement</span>
                        <span className="account-option-meta">Pre-match + live gecombineerd</span>
                      </Link>
                    </div>
                    <Link to="/pricing" className="card-btn card-link" style={{ marginTop: '1rem', display: 'block', textAlign: 'center' }}>Neem een abonnement</Link>
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'telegram' && (
            <>
              <div className="account-section-heading">
                <div>
                  <span className="account-card-kicker">Meldingen</span>
                  <h2>Telegramtoegang</h2>
                </div>
                <p>Beheer je koppeling en uitnodigingen.</p>
              </div>
              {accountStatus === 'active' || accountRole === 'admin' ? (
                <>
                  <div className="account-access-list">
                    <article className="account-access-card card">
                      <div className="account-access-topline">
                        <h3>Telegrammeldingen</h3>
                        <span className="account-status-badge account-status-badge--success">Actief</span>
                      </div>
                      <p>Je ontvangt meldingen op basis van je abonnement.</p>
                    </article>
                    <article className="account-access-card card">
                      <div className="account-access-topline">
                        <h3>Gratis Telegramkanaal</h3>
                        <span className="account-status-badge account-status-badge--soft">Gratis</span>
                      </div>
                      <p>Dit openbare instapkanaal staat los van je betaalde abonnement.</p>
                      <a href={FREE_TELEGRAM_JOIN_URL} className="card-btn card-link account-secondary-btn" target="_blank" rel="noreferrer">Open gratis kanaal</a>
                    </article>
                    <article className="account-access-card card">
                      <div className="account-access-topline">
                        <h3>Telegram koppeling</h3>
                        <span className={`account-status-badge account-status-badge--${telegramLink?.linked ? 'success' : 'soft'}`}>
                          {telegramLink?.linked ? 'Gekoppeld' : 'Nog niet gekoppeld'}
                        </span>
                      </div>
                      <p>
                        {telegramLink?.linked
                          ? `Gekoppeld aan ${telegramLink.username ? `@${telegramLink.username}` : `chat-id ${telegramLink.chatId}`}.`
                          : 'Koppel veilig via de PerfectOddsNL bot. Je hoeft geen chat-id op te zoeken of in te vullen.'}
                      </p>
                    </article>
                  </div>

                  <div className="account-telegram-panel card">
                    <div className="account-telegram-code-row">
                      <div>
                        <span className="account-label">Telegramtoegang</span>
                        <strong>{telegramLink?.accessStatus || 'Nog niet gesynchroniseerd'}</strong>
                      </div>
                      {telegramLink?.accessSyncedAt && (
                        <div>
                          <span className="account-label">Laatst bijgewerkt</span>
                          <strong>{new Date(telegramLink.accessSyncedAt).toLocaleString('nl-NL')}</strong>
                        </div>
                      )}
                    </div>
                    <div className="account-telegram-security-note">
                      <span aria-hidden="true">✓</span>
                      <p>
                        Uitnodigingen worden alleen in je gekoppelde Telegramchat afgeleverd. De bot controleert je Telegram-ID en weigert doorgestuurde links automatisch.
                      </p>
                    </div>
                    {telegramLink?.accessError && <p className="account-note">Laatste fout: {telegramLink.accessError}</p>}
                    <div className="subscription-cta-row account-cta-row">
                      {telegramLink?.linked && (
                        <button type="button" className="card-btn" onClick={handleSyncTelegramAccess} disabled={isLoadingTelegram} aria-busy={isLoadingTelegram}>
                          Stuur kanaaluitnodigingen naar mijn botchat
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="account-telegram-panel card">
                    <div className="account-telegram-code-row">
                      <div>
                        <span className="account-label">Automatisch koppelen</span>
                        <strong>{telegramLink?.linked ? 'Telegram is gekoppeld' : 'Via @PerfectOddsNLBot'}</strong>
                      </div>
                      {telegramLink?.linkedAt && (
                        <div>
                          <span className="account-label">Gekoppeld op</span>
                          <strong>{new Date(telegramLink.linkedAt).toLocaleString('nl-NL')}</strong>
                        </div>
                      )}
                    </div>
                    <p className="account-note">
                      Klik op de knop en druk in Telegram op Start. De bot koppelt je Telegram-ID aan dit account en stuurt daarna je persoonlijke kanaallinks.
                    </p>
                    <div className="subscription-cta-row account-cta-row">
                      {!telegramLink?.linked && (
                        <button type="button" className="card-btn" onClick={handleStartTelegramLink} disabled={isLoadingTelegram} aria-busy={isLoadingTelegram}>
                          Koppel via Telegram bot
                        </button>
                      )}
                      {telegramLink?.linked && (
                        <button type="button" className="card-btn account-danger-btn" onClick={handleUnlinkTelegram} disabled={isLoadingTelegram} aria-busy={isLoadingTelegram}>
                          Ontkoppelen
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="subscription-cta-row account-cta-row">
                    <Link to="/telegram" className="card-btn card-link">Telegram uitleg bekijken</Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="account-empty-state card">
                    <h3>Begin met het gratis Telegramkanaal</h3>
                    <p>Zonder betaald abonnement heb je geen toegang tot de besloten pre-match- en livekanalen. Het gratis kanaal kun je wel direct openen.</p>
                    <div className="subscription-cta-row account-cta-row">
                      <a href={FREE_TELEGRAM_JOIN_URL} className="card-btn card-link" target="_blank" rel="noreferrer">Open gratis kanaal</a>
                      <Link to="/pricing" className="card-btn card-link account-secondary-btn">Bekijk abonnementen</Link>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {activeTab === 'facturen' && (
            <>
              <div className="account-section-heading">
                <div>
                  <span className="account-card-kicker">Betalingen</span>
                  <h2>Facturen</h2>
                </div>
                <p>Bekijk je betaalhistorie en documenten.</p>
              </div>
              {invoiceLoadState === 'loading' ? (
                  <div className="account-empty-state card">
                    <div className="site-loading-block" role="status" aria-live="polite">
                      <span className="site-loading-spinner" aria-hidden="true" />
                      <p>Stripe-facturen laden...</p>
                    </div>
                  </div>
                ) : invoiceLoadState === 'error' ? (
                  <div className="account-empty-state card">
                    <span className="account-card-kicker">Tijdelijk niet beschikbaar</span>
                    <h3>Facturen konden niet worden geladen</h3>
                    <p>Probeer het tabblad opnieuw te openen of neem contact met ons op als dit blijft gebeuren.</p>
                    <div className="account-cta-row">
                      <Link to="/contact" className="card-btn card-link account-secondary-btn">Vraag over een betaling</Link>
                    </div>
                  </div>
                ) : billingInvoices.length > 0 ? (
                  <div className="account-invoice-list">
                    {billingInvoices.map((invoice) => (
                      <article key={invoice.id} className="account-invoice-card card">
                        <div className="account-invoice-main">
                          <span className="account-card-kicker">{invoice.number ? `Factuur ${invoice.number}` : 'Stripe factuur'}</span>
                          <h3>
                            {new Intl.NumberFormat('nl-NL', {
                              style: 'currency',
                              currency: invoice.currency || 'EUR',
                            }).format((invoice.amountPaid || invoice.amountDue || 0) / 100)}
                          </h3>
                          {invoice.discountAmount > 0 && (
                            <div className="account-invoice-discount">
                              <span>
                                Subtotaal {new Intl.NumberFormat('nl-NL', {
                                  style: 'currency',
                                  currency: invoice.currency || 'EUR',
                                }).format((invoice.subtotal || 0) / 100)}
                              </span>
                              <strong>
                                Korting −{new Intl.NumberFormat('nl-NL', {
                                  style: 'currency',
                                  currency: invoice.currency || 'EUR',
                                }).format(invoice.discountAmount / 100)}
                              </strong>
                            </div>
                          )}
                          <p>{new Date(invoice.paidAt || invoice.createdAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                        </div>
                        <span className={`account-status-badge account-status-badge--${invoice.status === 'paid' ? 'success' : 'soft'}`}>
                          {invoice.status === 'paid' ? 'Betaald' : invoice.status}
                        </span>
                        <div className="account-invoice-actions">
                          {invoice.hostedInvoiceUrl && (
                            <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer" className="card-btn card-link account-secondary-btn">Bekijk factuur</a>
                          )}
                          {invoice.invoicePdf && (
                            <a href={invoice.invoicePdf} target="_blank" rel="noreferrer" className="card-btn card-link">Download PDF</a>
                          )}
                        </div>
                      </article>
                    ))}
                    <p className="account-invoice-note">Betaalde Stripe-facturen worden automatisch als negatieve abonnementskosten in je tracker gezet.</p>
                  </div>
                ) : (
                  <div className="account-empty-state card">
                    <span className="account-card-kicker">Nog geen documenten</span>
                    <h3>Geen downloadbare facturen</h3>
                    <p>Zodra Stripe een factuur voor een betaling heeft gemaakt, verschijnt die hier automatisch.</p>
                    <div className="account-cta-row">
                      <Link to="/contact" className="card-btn card-link account-secondary-btn">Vraag over een betaling</Link>
                    </div>
                  </div>
                )}
            </>
          )}

          {activeTab === 'vrienden' && (
            <>
              <div className="account-section-heading">
                <div><span className="account-card-kicker">Vriendenvoordeel</span><h2>Nodig een vriend uit</h2></div>
                <p>Jullie krijgen allebei €10 korting op een geschikte maandbetaling.</p>
              </div>
              {isLoadingReferrals && !referralOverview ? (
                <div className="account-empty-state card"><div className="site-loading-block" role="status"><span className="site-loading-spinner" aria-hidden="true" /><p>Vriendenlink laden...</p></div></div>
              ) : referralOverview ? (
                <div className="referral-account-grid">
                  <section className="referral-share-card card">
                    <div className="referral-gift-mark" aria-hidden="true">€10</div>
                    <span className="account-card-kicker">Deel je persoonlijke link</span>
                    <h3>€10 voor jou, €10 voor je vriend</h3>
                    <p>Je vriend krijgt de korting op de eerste geschikte maandbetaling. Jouw korting komt vrij zodra die maandbetaling geslaagd is.</p>
                    <div className="referral-link-box">
                      <input aria-label="Persoonlijke vriendenlink" readOnly value={referralOverview.shareUrl} />
                      <button type="button" onClick={() => { void handleCopyReferralLink(); }}>Kopieer</button>
                    </div>
                    <div className="account-cta-row referral-actions">
                      <button type="button" className="card-btn" onClick={() => { void handleShareReferral(); }}>Deel vriendenlink</button>
                    </div>
                    <small className="referral-terms">Alleen geldig op Pro-, Pre-match- en Live-maandabonnementen. Niet op weekabonnementen of dagpassen.</small>
                  </section>

                  <section className="referral-status-card card">
                    <span className="account-card-kicker">Jouw voortgang</span>
                    <div className="referral-metrics">
                      <div><strong>{referralOverview.availableRewards + referralOverview.reservedRewards}</strong><span>kortingen klaar</span></div>
                      <div><strong>{referralOverview.qualifiedFriends}</strong><span>vrienden betaald</span></div>
                      <div><strong>{referralOverview.redeemedRewards}</strong><span>kortingen gebruikt</span></div>
                    </div>
                    <div className="referral-explanation">
                      <div><span>1</span><p>Deel je persoonlijke link.</p></div>
                      <div><span>2</span><p>Je vriend maakt via de link een nieuw account.</p></div>
                      <div><span>3</span><p>Na een geslaagde maandbetaling staat jouw €10 automatisch klaar.</p></div>
                    </div>
                    <div className="referral-rules-box">
                      <strong>Zo wordt je korting gebruikt</strong>
                      <ul>
                        <li>Maximaal één vriendenkorting van €10 per maandbetaling.</li>
                        <li>Niet combineerbaar met een andere actie of kortingscode.</li>
                        <li>Geldt er al een andere korting? Dan blijft je €10 klaarstaan voor de eerstvolgende geschikte maandbetaling.</li>
                        <li>Heb je meerdere vriendenkortingen? Dan worden ze automatisch over volgende maanden verdeeld.</li>
                      </ul>
                    </div>
                  </section>

                  <section className="referral-friends-card card">
                    <div className="account-card-heading"><div><h3>Uitgenodigde vrienden</h3><p>Privacyvriendelijk weergegeven.</p></div></div>
                    {referralOverview.friends.length ? (
                      <div className="referral-friend-list">
                        {referralOverview.friends.map((friend) => (
                          <div key={friend.id}>
                            <span>{friend.label}</span>
                            <strong className={`account-status-badge account-status-badge--${friend.status === 'qualified' ? 'success' : 'soft'}`}>
                              {friend.status === 'qualified' ? 'Beloning verdiend' : 'Wacht op maandbetaling'}
                            </strong>
                          </div>
                        ))}
                      </div>
                    ) : <div className="referral-empty"><strong>Nog niemand uitgenodigd</strong><p>Deel je link om je eerste vriendenvoordeel te verdienen.</p></div>}
                  </section>
                </div>
              ) : (
                <div className="account-empty-state card"><h3>Vriendenvoordeel tijdelijk niet beschikbaar</h3><p>Probeer dit tabblad later opnieuw.</p></div>
              )}
            </>
          )}

          {activeTab === 'beveiliging' && (
            <>
              <div className="account-section-heading">
                <div>
                  <span className="account-card-kicker">Accountinstellingen</span>
                  <h2>Beveiliging</h2>
                </div>
                <p>Beheer je wachtwoord en sessie.</p>
              </div>
              <div className="account-actions-list">
                <article className="account-security-card">
                  <div className="account-security-card-header">
                    <h3>Wachtwoord wijzigen</h3>
                    <p>Wijzig je wachtwoord direct met je huidige wachtwoord.</p>
                  </div>
                  <div className="account-security-card-body">
                    <div className="account-form-grid account-password-grid">
                      <label className="account-field">
                        Huidig wachtwoord
                        <input
                          type="password"
                          value={currentPassword}
                          onChange={(event) => setCurrentPassword(event.target.value)}
                          placeholder="Je huidige wachtwoord"
                          autoComplete="current-password"
                        />
                      </label>
                      <label className="account-field">
                        Nieuw wachtwoord
                        <input
                          type="password"
                          value={newPassword}
                          onChange={(event) => setNewPassword(event.target.value)}
                          placeholder="Minimaal 6 tekens"
                          autoComplete="new-password"
                        />
                      </label>
                    </div>
                    <div className="account-cta-row">
                      <button
                        type="button"
                        className="card-btn"
                        onClick={() => { void handleConfirmPasswordChange(); }}
                        disabled={isChangingPassword}
                        aria-busy={isChangingPassword}
                      >
                        {isChangingPassword ? 'Wijzigen...' : 'Wachtwoord opslaan'}
                      </button>
                    </div>
                  </div>
                </article>

                <article className="account-action-row">
                  <div>
                    <h3>Uitloggen</h3>
                    <p>Log uit op dit apparaat.</p>
                  </div>
                  <button type="button" className="card-btn account-secondary-btn" onClick={handleLogout}>
                    Uitloggen
                  </button>
                </article>

                <article className="account-security-card account-security-card--danger">
                  <div className="account-security-card-header">
                    <h3>Account verwijderen</h3>
                    <p>Verwijder je account definitief. Je wordt daarna uitgelogd.</p>
                  </div>
                  <div className="account-security-card-body">
                    <div className="account-form-grid account-password-grid">
                      <label className="account-field">
                        Wachtwoord ter bevestiging
                        <input
                          type="password"
                          value={deletePassword}
                          onChange={(event) => setDeletePassword(event.target.value)}
                          placeholder="Je wachtwoord"
                          autoComplete="current-password"
                        />
                      </label>
                    </div>
                    <div className="account-cta-row">
                      <button
                        type="button"
                        className="card-btn account-danger-btn"
                        onClick={() => { void handleDeleteAccount(); }}
                        disabled={isDeletingAccount}
                        aria-busy={isDeletingAccount}
                      >
                        {isDeletingAccount ? 'Verwijderen...' : 'Account verwijderen'}
                      </button>
                    </div>
                  </div>
                </article>
              </div>
            </>
          )}
        </div>

        {showBillingModal && (
          <div
            className="modal-overlay billing-manager-overlay"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget && !isOpeningPortal) setShowBillingModal(false);
            }}
          >
            <div className="modal-content billing-manager-modal" role="dialog" aria-modal="true" aria-labelledby="billing-manager-title">
              <button
                type="button"
                className="modal-close-button"
                onClick={() => setShowBillingModal(false)}
                disabled={isOpeningPortal}
                aria-label="Venster sluiten"
              >
                ×
              </button>
              <div className="billing-manager-header">
                <span className="billing-manager-brand" aria-hidden="true">PO</span>
                <div>
                  <span className="account-card-kicker">PerfectOddsNL Billing</span>
                  <h2 id="billing-manager-title">Abonnement beheren</h2>
                  <p>Kies wat je wilt aanpassen. Je gaat daarna rechtstreeks naar de juiste beveiligde Stripe-stap.</p>
                </div>
              </div>

              <div className="billing-manager-current">
                <div>
                  <span>Huidig abonnement</span>
                  <strong>{accountPackage}</strong>
                </div>
                <span className="account-status-badge account-status-badge--success">Actief</span>
              </div>

              <div className="billing-manager-options">
                <button type="button" onClick={() => { void handleManageSubscription('subscription_update'); }} disabled={isOpeningPortal}>
                  <span className="billing-manager-option-icon" aria-hidden="true">↗</span>
                  <span><strong>Pakket of looptijd wijzigen</strong><small>Kies een ander abonnement of een andere betaalperiode.</small></span>
                  <span aria-hidden="true">›</span>
                </button>
                <button type="button" onClick={() => { void handleManageSubscription('manage'); }} disabled={isOpeningPortal}>
                  <span className="billing-manager-option-icon" aria-hidden="true">€</span>
                  <span><strong>Betaalgegevens en facturen</strong><small>Beheer je betaalmethode en bekijk Stripe-documenten.</small></span>
                  <span aria-hidden="true">›</span>
                </button>
                <button className="billing-manager-option-danger" type="button" onClick={() => { void handleManageSubscription('subscription_cancel'); }} disabled={isOpeningPortal}>
                  <span className="billing-manager-option-icon" aria-hidden="true">×</span>
                  <span><strong>Abonnement opzeggen</strong><small>Beëindig je abonnement volgens de ingestelde opzegtermijn.</small></span>
                  <span aria-hidden="true">›</span>
                </button>
              </div>

              <div className="billing-manager-security">
                <span aria-hidden="true">✓</span>
                <p>{isOpeningPortal ? 'Beveiligde Stripe-pagina openen...' : 'Betalingsgegevens blijven beveiligd bij Stripe. Na afronding kom je automatisch terug bij Mijn account.'}</p>
              </div>
            </div>
          </div>
        )}
      </section>
    </PublicLayout>
  );
};

export const GeenToegangPage: React.FC = () => {
  const location = useLocation();
  const reason = (location.state as { reason?: string } | null | undefined)?.reason;

  return (
    <PublicLayout>
      <section className="public-page-card subscription-page access-page">
        <section className="subscription-hero">
          <h1>Geen toegang</h1>
          <p>Deze pagina is alleen beschikbaar voor gebruikers met de juiste toegang.</p>
          {reason === 'tracker' && <p>De Surebet Tracker is beschikbaar voor betalende abonnees.</p>}
          {reason === 'admin' && <p>Adminpagina’s zijn alleen beschikbaar voor beheerders.</p>}
          <div className="subscription-cta-row">
            <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
            <Link to="/" className="card-btn card-link public-cta-secondary">Terug naar home</Link>
          </div>
        </section>
      </section>
    </PublicLayout>
  );
};

// Surebets category pages
export const CalculatorPage: React.FC = () => {
  const [form, setForm] = useState<SurebetCalcInput>({
    oddA: '',
    oddB: '',
    oddC: '',
    totalStake: '100',
  });
  const [includeOddC, setIncludeOddC] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const calculation = useMemo(
    () => calculateSurebet(form, { includeOddC }),
    [form, includeOddC],
  );

  const handleInputChange = (field: keyof SurebetCalcInput, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setShowResult(true);
  };

  const handleReset = () => {
    setForm({ oddA: '', oddB: '', oddC: '', totalStake: '100' });
    setIncludeOddC(false);
    setShowResult(false);
  };

  const handleToggleOddC = () => {
    setIncludeOddC((prev) => {
      const next = !prev;
      if (!next) {
        setForm((current) => ({ ...current, oddC: '' }));
      }
      return next;
    });
    setShowResult(true);
  };

  const result = calculation.result;
  const hasErrors = calculation.errors.length > 0;
  const showStakeHints = showResult && !hasErrors && !!result;

  return (
    <PublicLayout>
      <section className="public-page-card subscription-page calculator-page public-refresh calculator-refresh">
        <section className="subscription-hero calculator-hero">
          <span className="subscription-badge">Rekentool</span>
          <h1>Surebet calculator</h1>
          <p>
            Bereken eenvoudig of twee of drie odds samen een surebet vormen. Vul de odds en je totale inzet in, en
            PerfectOddsNL berekent automatisch de inzetverdeling, uitbetaling, winst en ROI.
          </p>
          <p className="public-note">
            Gebruik deze calculator altijd als hulpmiddel. Controleer odds, markt en lijn zelf voordat je inzet.
          </p>
        </section>

        <section className="subscription-section calculator-main-card">
          <h2>Bereken je surebet</h2>
          <div className="calculator-controls-row">
            <div className="calculator-field calculator-total-field">
              <label htmlFor="total-stake">Totale inzet</label>
              <input
                id="total-stake"
                type="text"
                inputMode="decimal"
                placeholder="Bijv. 100"
                value={form.totalStake}
                onChange={(e) => handleInputChange('totalStake', e.target.value)}
              />
            </div>
            <div className="calculator-field calculator-odd-c-control">
              <button
                type="button"
                className={`calculator-toggle ${includeOddC ? 'is-on' : ''}`}
                onClick={handleToggleOddC}
                aria-pressed={includeOddC}
              >
                <span>Odd C</span>
                <span className="calculator-toggle-pill">{includeOddC ? 'Aan' : 'Uit'}</span>
              </button>
            </div>
          </div>

          <div className={`calculator-outcome-grid ${includeOddC ? 'three-way' : ''}`}>
            <div className="calculator-field calculator-outcome-field">
              <label htmlFor="odd-a">Odd A</label>
              <div className="calculator-inline-row">
                <input
                  id="odd-a"
                  type="text"
                  inputMode="decimal"
                  placeholder="Bijv. 1.94"
                  value={form.oddA}
                  onChange={(e) => handleInputChange('oddA', e.target.value)}
                />
                <div className="calculator-stake-chip">
                  Inzet: {showStakeHints && result ? formatEuro(result.stakeA) : '--'}
                </div>
              </div>
            </div>

            <div className="calculator-field calculator-outcome-field">
              <label htmlFor="odd-b">Odd B</label>
              <div className="calculator-inline-row">
                <input
                  id="odd-b"
                  type="text"
                  inputMode="decimal"
                  placeholder="Bijv. 2.41"
                  value={form.oddB}
                  onChange={(e) => handleInputChange('oddB', e.target.value)}
                />
                <div className="calculator-stake-chip">
                  Inzet: {showStakeHints && result ? formatEuro(result.stakeB) : '--'}
                </div>
              </div>
            </div>

            {includeOddC && (
              <div className="calculator-field calculator-outcome-field calculator-c-field">
                <label htmlFor="odd-c">Odd C</label>
                <div className="calculator-inline-row">
                  <input
                    id="odd-c"
                    type="text"
                    inputMode="decimal"
                    placeholder="Bijv. 3.20"
                    value={form.oddC}
                    onChange={(e) => handleInputChange('oddC', e.target.value)}
                  />
                  <div className="calculator-stake-chip">
                    Inzet:{' '}
                    {showStakeHints && result && result.usesOddC && result.stakeC !== undefined
                      ? formatEuro(result.stakeC)
                      : '--'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {showResult && hasErrors && (
            <div className="calculator-message calculator-error" role="alert">
              <h3>Controleer je invoer</h3>
              <ul className="subscription-list">
                {calculation.errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {showResult && !hasErrors && result && (
            <div className={`calculator-roi-banner ${result.isSurebet ? 'is-positive' : 'is-warning'}`}>
              <div className="calculator-roi-main">
                <span className="calculator-roi-label">ROI</span>
                <strong className="calculator-roi-value">{formatPercent(result.roiPercent)}</strong>
              </div>
              <div className="calculator-roi-side">
                <span className="calculator-roi-state">{result.isSurebet ? 'Surebet' : 'Geen surebet'}</span>
                <p className="calculator-roi-caption">
                  {result.isSurebet ? 'Positieve marge' : 'Negatieve marge'}
                </p>
              </div>
            </div>
          )}

          {showResult && !hasErrors && result && (
            <div className={`calculator-message ${result.isSurebet ? 'calculator-success' : 'calculator-warning'}`}>
              <h3>{result.isSurebet ? 'Surebet gevonden' : 'Geen surebet'}</h3>
              {!result.isSurebet && (
                <p>
                  Deze odds vormen samen geen surebet. De totale implied probability is te hoog.
                </p>
              )}

              <div className="calculator-result-grid">
                <div><strong>Totale inzet:</strong> {formatEuro(result.totalStake)}</div>
                <div>
                  <strong>{result.isSurebet ? 'Winst:' : 'Verwacht tekort/verlies:'}</strong>{' '}
                  {formatEuro(result.isSurebet ? result.minimumProfit : result.expectedDeficit)}
                </div>
                {!result.isSurebet && (
                  <div><strong>Tip:</strong> Verhoog je odds of verlaag je inzet voor minder risico.</div>
                )}
              </div>
            </div>
          )}

          <div className="calculator-reset-bottom">
            <button type="button" className="card-btn calculator-reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </section>

        <section className="subscription-disclaimer-card card">
          <h3>Belangrijk om te weten</h3>
          <p>
            Rond je inzetten bij voorkeur af op 5-tallen om minder voorspelbare patronen te tonen richting
            bookmakers.
          </p>
          <p>
            Deze calculator is een hulpmiddel. <DisclaimerText />
          </p>
        </section>
      </section>
    </PublicLayout>
  );
};

// Bookmakers category pages
export const Bet365Page: React.FC = () => (
  <PublicPage
    title="Bet365"
    description="Informatie over hoe Bet365 wordt geïntegreerd in het PerfectOddsNL platform."
  />
);

export const KambiPage: React.FC = () => (
  <PublicPage
    title="Kambi"
    description="Overzicht van Kambi-bookmakers en hoe ze door PerfectOddsNL worden monitord."
  />
);

// Kennisbank subcategory pages
export const WatIsEenSurebetPage: React.FC = () => (
  <PublicPage
    title="Wat is een surebet?"
    description="Leer wat een surebet is en hoe het werkt."
  />
);

export const ArbitrageBettingPage: React.FC = () => (
  <PublicPage
    title="Arbitrage betting"
    description="Dieper inzicht in arbitrage betting en hoe PerfectOddsNL dit gebruikt."
  />
);

export const OverUnderPage: React.FC = () => (
  <PublicPage
    title="Over/under uitleg"
    description="Alles wat u moet weten over over/under weddenschappen."
  />
);

export const PlayerPropsPage: React.FC = () => (
  <PublicPage
    title="Player props"
    description="Uitleg over player props en how-to gids voor spelerstatistieken."
  />
);

export const TeamPropsPage: React.FC = () => (
  <PublicPage
    title="Team props"
    description="Alles over team props en teamgerelateerde statistieken."
  />
);

export const WatIsKambiPage: React.FC = () => (
  <PublicPage
    title="Wat is Kambi?"
    description="Inleiding tot Kambi en waarom het belangrijk is voor surebet arbitrage."
  />
);

export const WaaromOddsVeranderenPage: React.FC = () => (
  <PublicPage
    title="Waarom odds veranderen"
    description="Begrijp waarom odds fluctueren en wat dat betekent voor surebets."
  />
);

export const RisicosVanSurebettenPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page public-refresh risks-refresh">
      <section className="subscription-hero">
        <span className="subscription-badge">Veilig starten</span>
        <h1>Risico's van surebetting</h1>
        <p>
          Surebets kunnen interessant zijn, maar je moet snel en zorgvuldig werken. De grootste risico's ontstaan
          door veranderende odds, verkeerde markten of te laat reageren.
        </p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/surebets/wat-zijn-surebets" className="card-btn card-link public-cta-secondary">Wat zijn surebets?</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Odds kunnen veranderen</h2>
        <p>
          Odds kunnen veranderen voordat je beide bets hebt geplaatst. Daardoor kan een goede surebet minder goed
          worden of helemaal verdwijnen.
        </p>
        <ul className="subscription-list">
          <li>Controleer beide odds voordat je inzet.</li>
          <li>Plaats alleen als beide odds nog kloppen.</li>
          <li>Is een odd veranderd? Sla de kans over of bereken opnieuw.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Je kunt de verkeerde markt kiezen</h2>
        <p>
          Soms lijken markten op elkaar, maar zijn ze niet hetzelfde. Bijvoorbeeld schoten, schoten op doel, team
          shots of player shots. Als je de verkeerde markt kiest, klopt de berekening niet meer.
        </p>
        <ul className="subscription-list">
          <li>Controleer sport, wedstrijd, markt en lijn.</li>
          <li>Let goed op Over of Under.</li>
          <li>Plaats geen bet als je twijfelt.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Live betting is sneller en gevoeliger</h2>
        <p>
          Bij live surebets kan de ROI vaak hoger zijn, maar de kansen verdwijnen ook sneller. Odds kunnen binnen
          seconden veranderen.
        </p>
        <ul className="subscription-list">
          <li>Zorg dat je voor de wedstrijd al bent ingelogd bij beide bookmakers.</li>
          <li>Houd Bet365 en de Kambi-bookmaker tegelijk open.</li>
          <li>Sla live kansen over als je te laat bent.</li>
        </ul>
        <p>
          Een gemiste live surebet is geen probleem. Te laat of verkeerd inzetten is veel erger.
        </p>
      </section>

      <section className="subscription-section">
        <h2>Pre-match is ook tijdgevoelig</h2>
        <p>
          Pre-match betekent niet dat je rustig achterover kunt leunen. Het betekent alleen dat de wedstrijd nog niet
          begonnen is. Odds bewegen meestal minder snel dan live, maar ook pre-match kansen kunnen veranderen of
          verdwijnen.
        </p>
        <ul className="subscription-list">
          <li>Open de bookmakers direct na een melding.</li>
          <li>Controleer of markt, lijn en odds nog kloppen.</li>
          <li>Plaats alleen als beide kanten nog goed staan.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Bookmakers kunnen accounts beperken</h2>
        <p>
          Bookmakers kunnen gebruikers beperken wanneer zij structureel afwijkend speelgedrag laten zien. Dit kan per
          bookmaker verschillen en hoeft niet direct te gebeuren, maar het is wel iets om rekening mee te houden.
        </p>
        <ul className="subscription-list">
          <li>Bouw rustig op met je inzetten.</li>
          <li>Speel alleen met bedragen die passen bij je bankroll.</li>
          <li>Houd je resultaten goed bij.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Je eigen administratie is belangrijk</h2>
        <p>
          Zonder overzicht weet je niet of je echt winst maakt. Daarom zit de Surebet Tracker in elk betaald
          abonnement.
        </p>
        <ul className="subscription-list">
          <li>Vul je echte odds en inzetten in.</li>
          <li>Houd je winst, ROI en betalingen bij.</li>
          <li>Bekijk je netto resultaat per periode.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Hoe beperk je de risico's?</h2>
        <ul className="subscription-list">
          <li>Begin met kleine bedragen.</li>
          <li>Controleer elke melding zelf.</li>
          <li>Plaats nooit blind een bet.</li>
          <li>Sla kansen over als je twijfelt.</li>
          <li>Gebruik de Surebet Tracker.</li>
          <li>Speel alleen met geld dat je kunt missen.</li>
        </ul>
        <p>
          Het doel is niet om elke melding koste wat kost te spelen. Het doel is om alleen de kansen te gebruiken die
          je goed en veilig kunt plaatsen.
        </p>
      </section>

      <section className="subscription-section subscription-cta-block">
        <h2>Gebruik surebets als hulpmiddel, niet als garantie</h2>
        <p>
          PerfectOddsNL helpt je kansen sneller te vinden, maar jij blijft zelf verantwoordelijk voor je keuzes,
          controles en inzetten.
        </p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/surebets/wat-zijn-surebets" className="card-btn card-link public-cta-secondary">Wat zijn surebets?</Link>
        </div>
      </section>

      <section className="subscription-disclaimer-card card">
        <h3>Belangrijk om te weten</h3>
        <p>
          <DisclaimerText />
        </p>
      </section>
    </section>
  </PublicLayout>
);

// Tracker category pages (Mijn Tracker)
export const TrackerPage: React.FC = () => (
  <PublicPage
    title="Mijn tracker"
    description="Persoonlijke surebet tracker dashboard voor abonnees."
  />
);

// Support category pages
export const VerantwoordSpelenPage: React.FC = () => (
  <PublicPage
    title="Verantwoord spelen"
    description="Informatie over verantwoord gokken en ondersteuning beschikbaar."
  />
);

export const DisclaimerPage: React.FC = () => (
  <PublicPage
    title="Disclaimer"
    description="Juridische disclaimer en waarschuwingen voor PerfectOddsNL."
  />
);

export const RiscosVanSurebettingPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card subscription-page public-refresh risks-refresh">
      <section className="subscription-hero">
        <span className="subscription-badge">Veilig starten</span>
        <h1>Risico's van surebetting</h1>
        <p>
          Surebets kunnen interessant zijn, maar je moet snel en zorgvuldig werken. De grootste risico's ontstaan door
          veranderende odds, verkeerde markten of te laat reageren.
        </p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/surebets" className="card-btn card-link public-cta-secondary">Wat zijn surebets?</Link>
        </div>
      </section>

      <section className="subscription-section">
        <h2>Odds kunnen veranderen</h2>
        <p>
          Odds kunnen veranderen voordat je beide bets hebt geplaatst. Daardoor kan een goede surebet minder goed
          worden of helemaal verdwijnen.
        </p>
        <ul className="subscription-list">
          <li>Controleer beide odds voordat je inzet.</li>
          <li>Plaats alleen als beide odds nog kloppen.</li>
          <li>Is een odd veranderd? Sla de kans over of bereken opnieuw.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Je kunt de verkeerde markt kiezen</h2>
        <p>
          Sommige markten lijken op elkaar, zoals schoten, schoten op doel, team shots en player shots. Kies je de
          verkeerde markt, dan klopt de berekening niet meer en kan je inzet verloren gaan.
        </p>
        <ul className="subscription-list">
          <li>Controleer wedstrijd, markt, lijn en selectie.</li>
          <li>Let goed op Over of Under.</li>
          <li>Plaats geen bet als je twijfelt.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Live betting gaat snel</h2>
        <p>
          Bij live surebets kan de ROI vaak hoger zijn, maar de kansen verdwijnen ook sneller. Odds kunnen binnen
          seconden veranderen of geheel wegvallen.
        </p>
        <ul className="subscription-list">
          <li>Zorg dat je al bent ingelogd bij beide bookmakers.</li>
          <li>Houd Bet365 en de Kambi-bookmaker tegelijk open.</li>
          <li>Sla live kansen over als je te laat bent.</li>
        </ul>
        <p>
          <strong>Een gemiste live surebet is geen probleem. Te laat of verkeerd inzetten is veel erger.</strong>
        </p>
      </section>

      <section className="subscription-section">
        <h2>Pre-match blijft tijdgevoelig</h2>
        <p>
          Pre-match odds bewegen meestal minder snel dan live odds, maar ook pre-match surebets kunnen snel veranderen
          of verdwijnen, vooral wanneer meerdere gebruikers dezelfde kans zien of bookmakers hun prijzen aanpassen.
        </p>
        <ul className="subscription-list">
          <li>Open de bookmakers direct na een melding.</li>
          <li>Controleer of markt, lijn en odds nog kloppen.</li>
          <li>Plaats alleen als beide kanten nog goed staan.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Bookmakers kunnen accounts beperken</h2>
        <p>
          Bookmakers kunnen spelers beperken wanneer speelgedrag structureel afwijkt van normale gokkers. Dit hoeft
          niet direct te gebeuren, maar het is wel iets om voorzichtig mee om te gaan.
        </p>
        <ul className="subscription-list">
          <li>Bouw rustig op met je inzetten.</li>
          <li>Speel met bedragen die passen bij je bankroll.</li>
          <li>Houd je resultaten goed bij.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Je administratie moet kloppen</h2>
        <p>
          Zonder overzicht weet je niet of je echt winst maakt. Daarom zit de Surebet Tracker in elk betaald
          abonnement. Houd altijd je echte inzetten, odds en resultaten bij.
        </p>
        <ul className="subscription-list">
          <li>Vul je echte odds en inzetten in.</li>
          <li>Houd winst, ROI en kosten bij.</li>
          <li>Bekijk je netto resultaat per periode.</li>
        </ul>
      </section>

      <section className="subscription-section">
        <h2>Zo beperk je de risico's</h2>
        <ul className="subscription-list">
          <li>Begin met kleine bedragen.</li>
          <li>Controleer elke melding zelf.</li>
          <li>Plaats nooit blind een bet.</li>
          <li>Sla kansen over als je twijfelt.</li>
          <li>Gebruik de Surebet Tracker.</li>
          <li>Speel alleen met geld dat je kunt missen.</li>
        </ul>
        <p>
          Het doel is niet om elke melding te spelen. Het doel is om alleen kansen te gebruiken die je goed en veilig
          kunt plaatsen.
        </p>
      </section>

      <section className="subscription-section subscription-cta-block">
        <h2>Wil je surebets ontvangen?</h2>
        <p>Zorg dat je goed voorbereid bent en kies het abonnement dat het best bij jou past.</p>
        <div className="subscription-cta-row">
          <Link to="/pricing" className="card-btn card-link">Bekijk abonnementen</Link>
          <Link to="/surebets" className="card-btn card-link public-cta-secondary">Wat zijn surebets?</Link>
        </div>
      </section>

      <ImportantDisclaimerCard />
    </section>
  </PublicLayout>
);

const LegalPageHeader: React.FC<{
  label?: string;
  title: string;
  description: string;
}> = ({ label, title, description }) => (
  <header className={`legal-hero${label ? '' : ' legal-hero--plain'}`}>
    {label && <span>{label}</span>}
    <h1>{title}</h1>
    <p>{description}</p>
    <div className="legal-meta">
      <span>Versie 1.0</span>
      <span>Laatst bijgewerkt: 26 juli 2026</span>
    </div>
  </header>
);

const LegalRegistrationBackLink: React.FC = () => {
  const location = useLocation();
  const source = new URLSearchParams(location.search).get('from');

  if (source === 'google-register') {
    return (
      <Link to="/registreren/afronden" className="legal-registration-back">
        <span aria-hidden="true">←</span>
        Terug naar registratie afronden
      </Link>
    );
  }

  if (source === 'register') {
    return (
      <Link to="/register" className="legal-registration-back">
        <span aria-hidden="true">←</span>
        Terug naar registreren
      </Link>
    );
  }

  return null;
};

export const AlgemeneVoorwaardenPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card legal-page">
      <LegalRegistrationBackLink />
      <LegalPageHeader
        title="Algemene voorwaarden"
        description="Duidelijke afspraken over je account, abonnement en het gebruik van PerfectOddsNL."
      />

      <div className="legal-layout">
        <nav className="legal-toc" aria-label="Inhoud algemene voorwaarden">
          <strong>Op deze pagina</strong>
          <a href="#voorwaarden-toepassing">1. Toepassing</a>
          <a href="#voorwaarden-dienst">2. Onze dienst</a>
          <a href="#voorwaarden-account">3. Account</a>
          <a href="#voorwaarden-abonnement">4. Abonnement en betaling</a>
          <a href="#voorwaarden-opzeggen">5. Opzeggen en bedenktijd</a>
          <a href="#voorwaarden-gebruik">6. Verantwoord gebruik</a>
          <a href="#voorwaarden-beschikbaarheid">7. Beschikbaarheid</a>
          <a href="#voorwaarden-aansprakelijkheid">8. Aansprakelijkheid</a>
          <a href="#voorwaarden-klachten">9. Klachten en wijzigingen</a>
        </nav>

        <article className="legal-content">
          <section id="voorwaarden-toepassing">
            <span className="legal-section-number">01</span>
            <h2>Toepassing en contact</h2>
            <p>
              Deze voorwaarden gelden voor ieder account, ieder abonnement en ieder gebruik van de website,
              Telegramdiensten en Surebet Tracker van PerfectOddsNL. Door een account aan te maken bevestig je dat je
              deze voorwaarden vóór registratie hebt kunnen lezen.
            </p>
            <p>
              Vragen over deze voorwaarden kun je sturen naar{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          </section>

          <section id="voorwaarden-dienst">
            <span className="legal-section-number">02</span>
            <h2>Onze dienst</h2>
            <p>
              PerfectOddsNL levert informatieve signalen over mogelijke surebets, toegang tot Telegramkanalen en
              hulpmiddelen om geplaatste weddenschappen en resultaten bij te houden. Wij plaatsen geen weddenschappen,
              beheren geen spelerssaldo en zijn geen bookmaker of kansspelaanbieder.
            </p>
            <div className="legal-callout">
              <strong>Geen winstgarantie</strong>
              <p>
                Odds, markten en beschikbaarheid kunnen snel wijzigen. Een melding is geen persoonlijk financieel
                advies en geen garantie dat een weddenschap kan worden geplaatst of winst oplevert.
              </p>
            </div>
          </section>

          <section id="voorwaarden-account">
            <span className="legal-section-number">03</span>
            <h2>Je account</h2>
            <ul>
              <li>Je bent minimaal 18 jaar en wettelijk bevoegd om de dienst te gebruiken.</li>
              <li>Je verstrekt een geldig e-mailadres dat bij jou hoort.</li>
              <li>Je houdt je wachtwoord en toegangscodes geheim en deelt je account niet met anderen.</li>
              <li>Je meldt misbruik of onbevoegde toegang zo snel mogelijk bij support.</li>
              <li>Eén persoon mag de dienst niet via meerdere accounts misbruiken.</li>
            </ul>
            <p>
              We mogen toegang tijdelijk beperken wanneer dit nodig is voor beveiliging, onderhoud, misbruikpreventie
              of naleving van de wet. Bij een definitieve beperking leggen we, voor zover mogelijk, de reden uit.
            </p>
          </section>

          <section id="voorwaarden-abonnement">
            <span className="legal-section-number">04</span>
            <h2>Abonnement, prijzen en betaling</h2>
            <p>
              Het gekozen pakket, de looptijd, de prijs en eventuele terugkerende betaling worden vóór het afronden
              van de checkout getoond. De informatie in de checkout maakt onderdeel uit van de overeenkomst.
            </p>
            <ul>
              <li>Maand- en weekabonnementen worden verlengd volgens de informatie die in de checkout staat.</li>
              <li>Een dagpas en acties die uitdrukkelijk als eenmalig zijn aangeduid, worden niet automatisch verlengd.</li>
              <li>Toegang start nadat de betaling en abonnementsstatus succesvol zijn bevestigd.</li>
              <li>Bij een mislukte of teruggedraaide betaling mogen we betaalde toegang opschorten.</li>
              <li>Een vriendenbeloning is €10, geldt alleen voor een maandbetaling en niet voor een weekabonnement of dagpas.</li>
            </ul>
            <p>
              De nieuwe gebruiker krijgt één vriendenbeloning na geldige registratie via de persoonlijke link. De
              verwijzer ontvangt zijn beloning pas nadat die vriend daadwerkelijk een maandbetaling heeft afgerond.
              Per betaling kan maximaal één vriendenbeloning worden gebruikt. Beloningen zijn niet uitbetaalbaar,
              niet overdraagbaar en kunnen bij fraude, zelfverwijzing of misbruik worden ingetrokken.
            </p>
            <p>
              We wijzigen een lopende prijs of essentiële abonnementsvoorwaarde niet zomaar. Bij een nadelige wijziging
              informeren we je vooraf en geven we je, wanneer de wet dat vereist, de mogelijkheid om op te zeggen.
            </p>
          </section>

          <section id="voorwaarden-opzeggen">
            <span className="legal-section-number">05</span>
            <h2>Opzeggen en bedenktijd</h2>
            <p>
              Een terugkerend abonnement kun je online vanuit Mijn account beëindigen of per e-mail opzeggen via{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. De opzegging voorkomt de volgende verlenging;
              toegang blijft in beginsel beschikbaar tot het einde van de reeds betaalde periode.
            </p>
            <p>
              Als consument heb je bij een online overeenkomst in beginsel veertien dagen bedenktijd. Vraag je om de
              digitale dienst al tijdens die periode te laten beginnen, dan kan bij herroeping een evenredig bedrag
              verschuldigd zijn voor wat al is geleverd. Het herroepingsrecht vervalt alleen wanneer de wet dat toestaat
              en je daar vooraf uitdrukkelijk mee hebt ingestemd.
            </p>
            <p>
              Neem voor herroeping contact op met support en vermeld het e-mailadres van je account, het gekozen pakket
              en de aankoopdatum.
            </p>
          </section>

          <section id="voorwaarden-gebruik">
            <span className="legal-section-number">06</span>
            <h2>Verantwoord gebruik</h2>
            <p>
              Je blijft zelf verantwoordelijk voor het controleren van bookmaker, wedstrijd, markt, selectie, odds en
              inzet voordat je een weddenschap plaatst. Gebruik uitsluitend legale en voor jou toegankelijke
              kansspelaanbieders. Speel alleen met geld dat je kunt missen.
            </p>
            <p>
              Het is niet toegestaan onze signalen, software, inhoud of toegangscodes geautomatiseerd te kopiëren,
              door te verkopen, openbaar te maken of te gebruiken om de dienst of andere gebruikers te schaden.
            </p>
          </section>

          <section id="voorwaarden-beschikbaarheid">
            <span className="legal-section-number">07</span>
            <h2>Beschikbaarheid en intellectuele eigendom</h2>
            <p>
              We streven naar een betrouwbare dienst, maar kunnen geen ononderbroken beschikbaarheid garanderen.
              Onderhoud, storingen bij bookmakers, Telegram, hosting- of betaalpartners en veranderingen in
              oddsdatabronnen kunnen de dienst tijdelijk beïnvloeden.
            </p>
            <p>
              De website, software, vormgeving, teksten en door ons samengestelde signalen zijn beschermd. Je ontvangt
              uitsluitend een persoonlijk, niet-overdraagbaar gebruiksrecht zolang je toegang geldig is.
            </p>
          </section>

          <section id="voorwaarden-aansprakelijkheid">
            <span className="legal-section-number">08</span>
            <h2>Aansprakelijkheid</h2>
            <p>
              PerfectOddsNL is niet verantwoordelijk voor beslissingen die je zelfstandig bij bookmakers neemt, voor
              veranderde odds, geweigerde inzetten, accountlimieten of fouten die ontstaan doordat markt of selectie
              niet is gecontroleerd. Niets in deze voorwaarden beperkt aansprakelijkheid die volgens dwingend recht
              niet mag worden beperkt.
            </p>
            <p>
              Ontstaat een probleem dat aan ons kan worden toegerekend, neem dan zo snel mogelijk contact op zodat we
              het kunnen onderzoeken en waar passend herstellen.
            </p>
          </section>

          <section id="voorwaarden-klachten">
            <span className="legal-section-number">09</span>
            <h2>Klachten, wijzigingen en recht</h2>
            <p>
              Stuur een klacht met een duidelijke omschrijving naar{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We bevestigen de ontvangst en proberen binnen
              een redelijke termijn inhoudelijk te reageren.
            </p>
            <p>
              We kunnen deze voorwaarden aanpassen bij wijzigingen in de dienst of wetgeving. Bij wezenlijke
              wijzigingen informeren we gebruikers vooraf. Op de overeenkomst is Nederlands recht van toepassing,
              met behoud van dwingende consumentenrechten die in jouw woonland gelden.
            </p>
          </section>
        </article>
      </div>
    </section>
  </PublicLayout>
);

export const PrivacybeleidPage: React.FC = () => (
  <PublicLayout>
    <section className="public-page-card legal-page">
      <LegalRegistrationBackLink />
      <LegalPageHeader
        label="Privacy"
        title="Privacybeleid"
        description="Hier lees je welke persoonsgegevens PerfectOddsNL gebruikt, waarom dat nodig is en welke keuzes je hebt."
      />

      <div className="legal-layout">
        <nav className="legal-toc" aria-label="Inhoud privacybeleid">
          <strong>Op deze pagina</strong>
          <a href="#privacy-verantwoordelijke">1. Verantwoordelijke</a>
          <a href="#privacy-gegevens">2. Welke gegevens</a>
          <a href="#privacy-doelen">3. Doelen en grondslagen</a>
          <a href="#privacy-delen">4. Ontvangers</a>
          <a href="#privacy-bewaren">5. Bewaartermijnen</a>
          <a href="#privacy-rechten">6. Jouw rechten</a>
          <a href="#privacy-beveiliging">7. Beveiliging</a>
          <a href="#privacy-opslag">8. Cookies en opslag</a>
          <a href="#privacy-contact">9. Contact</a>
        </nav>

        <article className="legal-content">
          <section id="privacy-verantwoordelijke">
            <span className="legal-section-number">01</span>
            <h2>Wie is verantwoordelijk?</h2>
            <p>
              PerfectOddsNL is verantwoordelijk voor de verwerking van persoonsgegevens binnen de website,
              accountomgeving, Telegramkoppeling en Surebet Tracker. Voor privacyvragen kun je contact opnemen via{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
          </section>

          <section id="privacy-gegevens">
            <span className="legal-section-number">02</span>
            <h2>Welke gegevens verwerken we?</h2>
            <div className="legal-data-grid">
              <div>
                <strong>Accountgegevens</strong>
                <p>E-mailadres, beveiligde wachtwoordhash, accountstatus en inlogmomenten.</p>
              </div>
              <div>
                <strong>Abonnement en betaling</strong>
                <p>Gekozen pakket, looptijd, betaalstatus en identificatoren van de betaalprovider.</p>
              </div>
              <div>
                <strong>Telegram</strong>
                <p>Na vrijwillig koppelen: Telegramgebruikersnaam, chat-id, koppelstatus, uitnodigingen en synchronisatiestatus.</p>
              </div>
              <div>
                <strong>Trackergegevens</strong>
                <p>Door jou opgeslagen surebets, inzetten, odds, resultaten, kosten, notities en ROI.</p>
              </div>
              <div>
                <strong>Support</strong>
                <p>Contactgegevens, onderwerp, berichtinhoud en informatie die je zelf met support deelt.</p>
              </div>
              <div>
                <strong>Vriendenprogramma</strong>
                <p>Persoonlijke vriendencode, verwijzingsstatus en toegekende of gebruikte kortingsbeloningen.</p>
              </div>
              <div>
                <strong>Technische gegevens</strong>
                <p>IP-adres, apparaat- en browserinformatie, beveiligingslogs en foutmeldingen.</p>
              </div>
            </div>
          </section>

          <section id="privacy-doelen">
            <span className="legal-section-number">03</span>
            <h2>Doelen en juridische grondslagen</h2>
            <div className="legal-purpose-list">
              <div>
                <strong>Uitvoering van de overeenkomst</strong>
                <p>Accountbeheer, inloggen, leveren van abonnementstoegang, Telegrammeldingen en de Tracker.</p>
              </div>
              <div>
                <strong>Gerechtvaardigd belang</strong>
                <p>Beveiliging, misbruikpreventie, foutopsporing, verbetering van de dienst en klantenservice.</p>
              </div>
              <div>
                <strong>Wettelijke verplichting</strong>
                <p>Administratie, fiscale bewaarplichten en reageren op geldige verzoeken van bevoegde instanties.</p>
              </div>
              <div>
                <strong>Toestemming</strong>
                <p>Alleen wanneer toestemming wettelijk nodig is, bijvoorbeeld voor niet-noodzakelijke tracking.</p>
              </div>
            </div>
          </section>

          <section id="privacy-delen">
            <span className="legal-section-number">04</span>
            <h2>Met wie worden gegevens gedeeld?</h2>
            <p>We verkopen je persoonsgegevens niet. Gegevens kunnen, voor zover noodzakelijk, worden verwerkt door:</p>
            <ul>
              <li>hosting- en infrastructuurleveranciers die de website en database beschikbaar houden;</li>
              <li>Google Identity Services wanneer je Google-login kiest;</li>
              <li>Google Analytics wanneer je toestemming geeft voor analytische cookies;</li>
              <li>Telegram wanneer je je account koppelt of een uitnodiging opent;</li>
              <li>de betaalprovider wanneer je een betaald abonnement afsluit;</li>
              <li>e-mail- en supportleveranciers voor serviceberichten en klantenservice;</li>
              <li>overheidsinstanties wanneer een geldige wettelijke verplichting dit vereist.</li>
            </ul>
            <p>
              Sommige leveranciers kunnen gegevens buiten de Europese Economische Ruimte verwerken. In dat geval
              gebruiken we de waarborgen die de AVG voorschrijft, zoals een adequaatheidsbesluit of passende
              contractuele afspraken.
            </p>
          </section>

          <section id="privacy-bewaren">
            <span className="legal-section-number">05</span>
            <h2>Hoe lang bewaren we gegevens?</h2>
            <p>
              We bewaren persoonsgegevens niet langer dan nodig voor het doel waarvoor ze zijn verzameld. Je
              accountgegevens en Trackergegevens bewaren we zolang je account bestaat, tenzij een langere bewaartermijn
              wettelijk nodig is. Na verwijdering kunnen gegevens nog korte tijd in beveiligde back-ups staan.
            </p>
            <p>
              Financiële administratie bewaren we gedurende de wettelijke fiscale bewaartermijn. Supportberichten en
              technische logs worden verwijderd of geanonimiseerd zodra ze niet meer nodig zijn voor ondersteuning,
              beveiliging of geschilafhandeling.
            </p>
          </section>

          <section id="privacy-rechten">
            <span className="legal-section-number">06</span>
            <h2>Jouw privacyrechten</h2>
            <p>Je kunt, afhankelijk van de situatie, vragen om:</p>
            <ul>
              <li>inzage in de persoonsgegevens die we van je verwerken;</li>
              <li>correctie van onjuiste of onvolledige gegevens;</li>
              <li>verwijdering of beperking van de verwerking;</li>
              <li>overdracht van gegevens die je zelf hebt aangeleverd;</li>
              <li>bezwaar tegen verwerking op basis van gerechtvaardigd belang;</li>
              <li>intrekking van toestemming, zonder gevolgen voor eerdere rechtmatige verwerking.</li>
            </ul>
            <p>
              Je kunt je account ook vanuit Mijn account verwijderen. Voor andere verzoeken mail je support vanaf het
              e-mailadres dat bij je account hoort. We kunnen om aanvullende verificatie vragen om je gegevens te
              beschermen.
            </p>
          </section>

          <section id="privacy-beveiliging">
            <span className="legal-section-number">07</span>
            <h2>Beveiliging en minderjarigen</h2>
            <p>
              We gebruiken passende technische en organisatorische maatregelen, waaronder versleutelde verbindingen,
              toegangsbeperking en beveiligde wachtwoordopslag. Geen enkele internetdienst is volledig zonder risico;
              meld vermoedelijke onbevoegde toegang daarom direct.
            </p>
            <p>
              PerfectOddsNL is uitsluitend bedoeld voor personen van 18 jaar en ouder. Ontdekken we dat een minderjarige
              een account heeft aangemaakt, dan kunnen we het account en de bijbehorende gegevens verwijderen.
            </p>
          </section>

          <section id="privacy-opslag">
            <span className="legal-section-number">08</span>
            <h2>Cookies en browseropslag</h2>
            <p>
              We gebruiken noodzakelijke browseropslag om je ingelogd te houden, je thema te onthouden en de werking
              van de site te beveiligen. De opslag van je cookievoorkeur is eveneens noodzakelijk om je keuze te
              onthouden.
            </p>
            <p>
              Alleen na jouw toestemming laden we Google Analytics 4. Daarmee meten we onder meer bezochte pagina&apos;s,
              paginawisselingen, globale interacties en technische informatie over browser en apparaat. Google
              Analytics kan daarbij cookies plaatsen die beginnen met <code>_ga</code>. Advertentieopslag en
              advertentiepersonalisatie staan binnen onze Analytics-installatie uit.
            </p>
            <p>
              Je kunt Analytics weigeren zonder dat dit de werking van PerfectOddsNL beperkt. Via de knop{' '}
              <button type="button" className="legal-inline-button" onClick={openCookiePreferences}>
                Cookievoorkeuren
              </button>{' '}
              in de footer kun je je toestemming later geven of intrekken. Bij intrekking blokkeren we verdere
              Analyticsmetingen en verwijderen we beschikbare Analyticscookies van PerfectOddsNL.
            </p>
          </section>

          <section id="privacy-contact">
            <span className="legal-section-number">09</span>
            <h2>Contact, klachten en wijzigingen</h2>
            <p>
              Voor vragen of verzoeken kun je mailen naar{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. Ben je niet tevreden over de afhandeling, dan
              kun je een klacht indienen bij de Autoriteit Persoonsgegevens.
            </p>
            <p>
              We kunnen dit privacybeleid aanpassen wanneer onze dienst of de wet verandert. De actuele versie en datum
              blijven op deze pagina zichtbaar. Bij belangrijke wijzigingen informeren we gebruikers waar passend
              rechtstreeks.
            </p>
          </section>
        </article>
      </div>
    </section>
  </PublicLayout>
);

export const CookiebeleidPage: React.FC = () => (
  <PublicPage
    title="Cookiebeleid"
    description="Informatie over hoe PerfectOddsNL cookies gebruikt."
  />
);
